import { validateSemanticFactBinding } from '../audit/semanticFactBinding';
import { checkModeledProjectBudgetNumber } from './modeledProjectBudget';
import { checkModeledAnnualFinancialMetric } from './modeledAnnualFinancialMetrics';
import { checkCustomBriefAnnualFinancialMetric } from './customBriefAnnualFinancialMetrics';
import { checkCampusResourceAdvisoryNumber } from '../findings/campusResourceAdvisory';
import { isOpenQuestionNumber, isDeclaredScheduleTarget, isOtherProjectCapacityMention, isRegionalGeneratorQueueBenchmark, isAssertedGenerationCapacity, isGeneratorQueueBenchmarkFraming } from './numericAssertionRole';
import { floodClaimIsHedged, isFloodSentenceEnd, preChangeWouldSuppress } from '../floodDisclosurePhrases';
import { isFrozenBomNameplateMention, isFrozenBomTransformerRating, isSourceCitedTransformerRating, isOwnerRecordedPhaseBatteryRating, isSupportedSourceRoleNumber, contextOnlyQueueHeuristicAt, isSourceQuotedDocumentDuration, isDeveloperStatedGridCapacity, isSourceCitedSolarLandEstimate, phaseClaimSequenceAt, phaseTargetAt, sourceCitedTribalHeadquartersDistance, type NumericSourceEvidence } from './numericSourceEvidence';
import { checkSourceStatedDesignLoadMargin } from './sourceStatedDesignLoadMargin';
import { checkModeledGridImportMargin } from './modeledGridImportMargin';
import { checkSourceFinancialPlanningEnergyPrice } from './sourceFinancialPlanningEnergyPrice';
import { NUMBERED_REF_TOKEN_RE } from '../citationTokens';
import { phaseDeltaRoleAt } from './phaseDeltaBinding';
import { checkSourceBoundBomSubtotal, type FrozenBomSubtotalSource } from './bomSubtotalSource';
/**
 * groundingValidator — PURE post-generation anti-hallucination gate for the
 * grounded-content layer (Report-gen quality remediation P1, tidy-sniffing-
 * teacup rev9).
 *
 * ─── Why ──────────────────────────────────────────────────────────────
 *
 * `sedToCanonicalText` hands the LLM ONE canonical value per logical fact +
 * an explicit "use ONLY these values" instruction. This validator is the
 * BLOCKING twin that catches the cases where the model ignored it: a number
 * or named entity in the generated narrative that does NOT match the
 * authoritative `CanonicalFactStore`. It is the anti-hallucination core of
 * the layer — the prompt asks, the gate enforces.
 *
 * It runs AFTER generation and BEFORE persistence (the Wire agent slots it
 * into `applyPostGenAuditGates`, after the R-67 internal-numbers gate). It
 * composes the already-built-but-dormant structural citation gate
 * (`checkCitationTargets`, src/lib/audit/citationTargetGate.ts) so dead
 * citation pointers + orphan references are surfaced in the SAME verdict.
 *
 * ─── Violation kinds ───────────────────────────────────────────────────
 *
 *   • `value-mismatch`     (BLOCK) — a narrative number is tagged to a
 *     governed fact (by co-located unit) whose canonical value differs
 *     beyond the tolerance band. The model substituted a different figure.
 *     FIX 1: on a unit backing SEVERAL distinct governed values, only a
 *     NEAR-MISS of some candidate (≤ NEAR_MISS_FACTOR × tolerance) is a
 *     mismatch — a value far from every candidate is a derived/contextual
 *     figure (a 250 MW deficit, a 50 km radius) and falls through to the
 *     unlabeled-estimate handling instead of false-blocking.
 *   • `invented-number`    (BLOCK) — an `absent` fact (no value at any tier)
 *     nonetheless appears asserted with that fact's unit. The model invented
 *     a value the evidence does not support. FIX 1: also fires when a
 *     present-unit mention matches NO governed value but its nearby text
 *     names an ABSENT same-unit fact ("a firm 49 MW" while
 *     firmCapacity.targetMw is ABSENT).
 *   • `invented-entity`    (BLOCK) — the narrative names a DIFFERENT utility /
 *     ISO / operator than the canonical one for a governed entity fact, OR
 *     asserts an entity for an `absent` entity fact.
 *   • `unlabeled-estimate` (WARN in warn-mode, BLOCK for labeled-unverifiable
 *     in on-mode) — an `estimated` / `labeled-unverifiable` value appears in
 *     prose with no nearby uncertainty qualifier ("estimated", "approx",
 *     "unverified", "~", a range). It reads as hard fact when it is not.
 *
 * ─── Discipline ───────────────────────────────────────────────────────
 *
 *  • PURE. No I/O, no store, no DOM, never mutates inputs, never throws on
 *    bad data — malformed inputs degrade to "no violation", not a crash.
 *  • Takes FLAT inputs (the assembled narrative text + the fact store +
 *    the citation-gate inputs), mirroring `checkCitationTargets`. The
 *    `SiteRagReport` → flat-input adapter is the caller's job; a thin
 *    convenience adapter (`buildGroundingInputsFromReport`) is provided but
 *    optional, so this module never imports the report type for matching.
 *  • Conservative scope: only the GOVERNED facts in the store are policed.
 *    A number with no governed unit, or one that simply does not collide
 *    with any fact, flows through untouched — the gate is a guard against
 *    contradicting / inventing the authoritative facts, not a blanket
 *    every-number-must-be-cited rule (that is R-66 / R-67's job).
 *  • Fail-open for empty inputs (no facts ⇒ `clean`); fail-closed for a
 *    genuine contradiction.
 *
 * ─── Wiring (the Wire agent) ───────────────────────────────────────────
 *
 *  `runGroundingGate(...)` composes `checkGrounding` + `checkCitationTargets`
 *  into one result + applies the mode policy (shadow/warn/on). `on` mode
 *  with blocking violations is intended to drive a one-shot regenerate via
 *  `GroundingGateError` + the `onGroundingRepair` context hook (mirroring
 *  `CitationDensityGateError` / `onCitationRetry`). This module does NOT
 *  throw on its own — the gate runner decides when to escalate to the error.
 */

import {
  checkCitationTargets,
  type CitationTarget,
  type CitationTargetGateResult,
  type CitationTargetViolation,
  type ReferenceEntry,
  type RenderedSection,
} from '../audit/citationTargetGate';
import {
  parseCapacityMw,
  parseDistanceKm,
  parseAreaAcres,
  parseDurationYears,
} from '../audit/unitNormalize';
import type { GroundingMode } from '../runtimeFlags';
import type {
  CanonicalFactStore,
  EntityFactIndexEntry,
  NumericFactIndexEntry,
} from './canonicalFacts';
import { checkPublicSentimentAssertions, type PublicSentimentAuditInput } from './publicSentimentAssertions';
import { formatFactValue } from './formatFactValue';
import { assertsBuildableAcreage, isSurveyFootprintMention, checkQuantitySourceRoles, type QuantitySourceRoles } from './quantitySourceRoles';
import { absentFactMayGovern, governanceWindow, lineAt } from './absentFactDomain';
import { isSerializedRecord } from '../audit/reportTextExtract';
import { numericPartOf, roundsToStatedPrecision } from './statedPrecision';
// SHARED with the two audit reconcilers (tier-2e + R-79). The
// three engines must decide "is this MW the facility's?" identically or they
// emit contradictory guidance for one sentence.
import { NON_FACILITY_MW_CONTEXT_RE } from '../audit/capacityPolysemy';

/* ── Output types ────────────────────────────────────────────────────── */

/**
 * Binding-behaviour version of this validator, stamped onto
 * `qaFindings.grounding.validatorVersion` so a replay can tell which binding
 * rules produced a saved verdict. A saved verdict WITHOUT a stamp predates v2.
 *
 * v2 (C2): owner-noted phase BESS ratings and cited islanded-scenario
 * estimates bind to their evidence; a percentage that is itself an area share
 * of the site is a separate (ungoverned, figure-anchored) subject; DAM `kb-`
 * refs bind only to the current knowledge entry with an identical source
 * string; an unverified generator-queue benchmark keeps blocking with a
 * benchmark-specific message.
 *
 * Phase addition/cumulative binding (`phaseCapacity.<N>.*` scope) is
 * UNCHANGED from v1 in v2: the C2 phase-role rework (delta / clause-carry /
 * cumulative-marker binding) was descoped, so a phase-sentence verdict is the
 * same under v1 and v2.
 *
 * v3 (quality holds, 2026-09-30): ONE narrow phase-capacity scope change,
 * the work plan's default for the held Holly Ridge phase-note sentences (e87
 * acceptance; QA-01 Q1: add only the noun-form addition cue, flagged to the
 * owner as the single deviation from the descoped C2 binding). The NOUN form
 * "addition <N> MW" / "<N> MW (IT-load|load) addition" is the phase addition,
 * as the verb "adds" already was. It does not apply, and production's reading
 * stays, when the figure is measured against a named baseline phase ("…
 * relative to Phase 1"), when the addition is to the utility's system (the
 * planning sense: "a 1,750 MW load addition to the Entergy system"), or when
 * the figure is the declared campus total. Every figure still compares
 * exactly against the phase fact it binds to, so a wrong figure keeps
 * blocking. The "Phase <k>, which adds" comma carry and the negated-status
 * exemption were reviewed out: they reopen the descoped C2 phase-role rework
 * without an owner go, and reading "non-firm" as a negation passed false
 * utility-supply claims. The C2 "(+N MW this phase)" delta binding (owner
 * decision D04 / backlog D20-B, CODE-11) is NOT part of v3; it takes the next
 * label, `grounding-validator-v4`. The `phaseRoleBinding.test.ts`
 * differential lists the corpus rows whose verdict v3 changes.
 *
 * v3-tariff-units-v1: complete recurring USD/kW-month and USD/kW-year
 * units do not bind to one-time capital costs. Phase binding stays at v3.
 */
// v4 closes D04's explicit parenthetical delta only. General phase clause
// carry, named-baseline arithmetic and utility-system roles remain unchanged.
export const GROUNDING_VALIDATOR_VERSION = 'grounding-validator-v4-phase-delta-v1-tariff-units-v1-grid-import-model-v1-campus-comparison-v1-frozen-bom-nameplate-v1-wind-resource-cf-v2-frozen-bom-transformer-role-v1-system-mva-role-v1-annual-facility-duty-v1-onsite-transformer-modifier-v1-source-reserve-comparison-v1-contingency-quantity-role-v1-ordinary-source-assertion-role-v1-source-design-load-margin-v1-modeled-project-budget-v1-annual-model-metrics-v1-qualified-ref-identity-v1-source-financial-planning-energy-price-v1-attributed-ppa-absence-v1-equipment-slack-clock-v1-accepted-current-brief-annual-metrics-v1-context-queue-heuristic-v1-document-duration-quote-v1-non-grid-interconnect-domain-v1-developer-grid-capacity-v1';

/**
 * What a blocking mention was read as, when that changes how it must be
 * repaired (never whether it blocks):
 *  - `generation-capacity-relabel` — a governed IT/facility load figure is
 *    presented as plant/generation capacity; keep the load role noun.
 *  - `generator-queue-benchmark` — a duration framed as a generator-cohort
 *    queue benchmark that no cited current source verifies.
 *  - `modeled-grid-import-margin` — an attempted fictional normal-case tuple
 *    is unbound, inconsistent or promoted to a utility service quantity.
 *  - `phase-delta-binding` — a closed owner phase label/cell states the wrong
 *    increment or cumulative load; keep that explicit phase quantity role.
 */
export type GroundingAssertionRole = 'context-only-queue-heuristic' | 'context-only-queue-heuristic-cohort-mislabel' | 'generation-capacity-relabel' | 'generator-queue-benchmark' | 'modeled-grid-import-margin' | 'source-design-load-margin' | 'source-financial-planning-energy-price' | 'modeled-project-budget' | 'modeled-annual-financial-metric' | 'custom-brief-source-annual-financial-metric' | 'phase-delta-binding' | 'campus-resource-comparison';

export type GroundingViolationKind =
  | 'value-mismatch'
  | 'invented-number'
  | 'invented-entity'
  // Lightweight, dependency-free NER (P2-GOLD P4 / Part B): a capitalized named
  // entity asserted in prose that has NO peer in the canonical fact set —
  // catches fabricated/renamed entities `checkEntities` (sibling-swap only)
  // misses. DEFAULT severity `'warn'` (advisory, non-blocking) so it never
  // changes the prod grounding gate's blocking behaviour unattended.
  | 'invented-entity-hallucination'
  | 'unlabeled-estimate'
  // Structural citation-gate kinds, folded in so one result carries them all.
  | 'dead-pointer'
  | 'orphan-reference';

export interface GroundingViolation {
  kind: GroundingViolationKind;
  severity: 'block' | 'warn';
  /** Human-readable, single-line explanation. */
  message: string;
  /** The value found in the narrative (verbatim), for diagnostics. */
  observed: string;
  /** The canonical fact this value should have matched, when known. */
  factId?: string;
  /** The canonical value the narrative should have used, when known. */
  expected?: string;
  /** Best-effort section attribution, when the caller scoped the text. */
  sectionId?: string;
  /**
   * The verbatim sentence the figure was found in.
   *
   * Every live violation used to persist with `sectionId: null` AND no
   * excerpt, so a hold banner could say 'Narrative states "10 MW" for
   * grid.headroomMw' about a 20-page report and no surface — reader, audit
   * ledger, repair prompt — could point at WHERE. It also made the two
   * reconcilers impossible to cross-check on a span. Stamped at emission, the
   * way `internalNumberReconciler` already does for its mentions.
   */
  excerpt?: string;
  /** Repair-relevant reading of the mention; see {@link GroundingAssertionRole}. */
  assertionRole?: GroundingAssertionRole;
}

export interface GroundingResult {
  /** True when there are NO violations of any kind. */
  clean: boolean;
  violations: GroundingViolation[];
  /** Convenience: violations whose severity is `block`. */
  blocking: GroundingViolation[];
  stats: {
    numericFacts: number;
    entityFacts: number;
    numericMentions: number;
    entityMentions: number;
    valueMismatches: number;
    inventedNumbers: number;
    inventedEntities: number;
    /** Part B — NER-flagged capitalized entities absent from the fact set. */
    inventedEntityHallucinations: number;
    unlabeledEstimates: number;
    deadPointers: number;
    orphanReferences: number;
    /** D04 closed parenthetical phase quantities inspected; no CFS adoption. */
    phaseDeltaBindings?: number;
    phaseDeltaMismatches?: number;
  };
}

/* ── Tolerance bands ─────────────────────────────────────────────────── */

/**
 * Default fractional tolerance for a numeric match: 0.5%. Tighter than the
 * R-67 cross-mention bands because here we compare against an AUTHORITATIVE
 * value, not two prose mentions — a 0.5% drift is rounding, anything larger
 * is a substitution.
 */
const DEFAULT_TOLERANCE_PCT = 0.005;

/** Per-unit tolerance overrides (fractional). Units are the store's display units. */
const UNIT_TOLERANCE: Record<string, number> = {
  MW: 0.01, // power rounds to whole MW
  MVA: 0.01,
  acres: 0.05, // boundary-sweep acreage is inherently fuzzy (matches R-67 acreage band)
  km: 0.02,
  // Durations are quoted in whole months, so a year value carries ~±1 month of
  // rounding (1/36 ≈ 3% of a typical 3-year queue). The 0.5% default would
  // police durations to ±11 days. Not looser: durations cluster densely, and at
  // 5% the near-miss band reached 15% — wide enough to swallow an unrelated
  // "30.7 months" of equipment slack sitting 14.7% from a 3-year queue.
  years: 0.03,
  /*
   * MONEY / PERCENT / ENERGY (FIN-1, audit 3.8) — 1%.
   *
   * The 0.5% default polices a $142,412,000 NPV to ±$712k, which every honest
   * "$142M" in prose breaches on rounding alone. 1% is the band that
   * scale-rounded money actually lives in, and it is nowhere near loose enough
   * to swallow a substitution: the golden case ($95/MWh stated against a
   * stamped $78/MWh) sits 21.8% away. One-decimal rounding of a rate or a
   * percentage is handled exactly, not by widening — see
   * `roundsToStatedPrecision`.
   */
  USD: 0.01,
  'USD/MWh': 0.01,
  'USD/kW': 0.01,
  '%': 0.01,
  MWh: 0.01,
  // PUE uses the default 0.5%.
};

/** Two numeric values match when within the (unit-aware) fractional tolerance. */
function numericMatch(a: number, b: number, unit: string | undefined, tolerancePct: number): boolean {
  const tol = unit && unit in UNIT_TOLERANCE ? UNIT_TOLERANCE[unit]! : tolerancePct;
  const denom = Math.max(Math.abs(a), Math.abs(b), 1);
  return Math.abs(a - b) / denom <= tol;
}

/** The effective fractional tolerance for a unit (override or default). */
function unitTolerance(unit: string | undefined, tolerancePct: number): number {
  return unit && unit in UNIT_TOLERANCE ? UNIT_TOLERANCE[unit]! : tolerancePct;
}

/**
 * FIX 1 — the near-miss band for MULTI-VALUED units, in multiples of the
 * unit tolerance.
 *
 * When a unit backs SEVERAL distinct governed values (the power set 300 /
 * 230.77; the land set 1170.1 / 800 / 700–1,100) the unit is demonstrably
 * POLYSEMOUS — many different quantities share it — so an unmatched prose
 * value is NOT automatically a substitution of a governed fact. It may be a
 * legitimate DERIVED figure (a 250 MW deficit = 300 required − 50 available)
 * or a quantity the store simply does not govern (a "within 50 km" radius).
 * Asserting "the only permitted value is 230.77 MW" against those is actively
 * wrong (the live Redhead false-positive storm: 48 value-mismatches on a
 * correct report).
 *
 * Discrimination rule (documented trade-off):
 *   - within NEAR_MISS_FACTOR × tolerance of the NEAREST governed value ⇒ it
 *     reads as a garbled/substituted version OF that fact ⇒ value-mismatch
 *     (BLOCK). The wrong-980-acres case sits at 2.2× the acres tolerance and
 *     keeps blocking; wrong-950 at 2.7×.
 *   - farther than that from EVERY candidate ⇒ a different quantity ⇒ fall
 *     through to the unlabeled-estimate handling (WARN when unqualified,
 *     never a block). The 250 MW deficit sits at 7.7× the MW tolerance.
 *   - units with a SINGLE governed value keep today's strict behaviour (any
 *     unmatched same-unit mention blocks) UNLESS the unit is polysemous by
 *     DOMAIN — see `DOMAIN_POLYSEMOUS_UNITS`.
 */
export const NEAR_MISS_FACTOR = 3;

/**
 * Units whose polysemy is a property of the DOMAIN, not of how many facts the
 * store happens to hold.
 *
 * `distinctValues >= 2` (above) infers polysemy from the store's CONTENTS. That
 * inference is sound when it fires and unsound when it does not: a unit backing
 * one governed value is not thereby unambiguous — the store simply governs one
 * of the many quantities the unit measures. Absence of evidence is not evidence
 * of absence.
 *
 * MEASURED (2026-08-05, live `interconnection` body): `ix.distanceKm` is the
 * only governed km fact, so `distinctValues === 1` and EVERY other distance in
 * the prose blocked against it — 20 blocking violations, all false:
 *   "50 km"   — the radius searched for other IX facilities
 *   "5-6 km"  — the long-haul carrier corridor offset
 *   "8.52 km" / "5 miles" — the last-mile fiber build length (a CAD polyline)
 * The report stated the governed fact CORRECTLY ("the nearest IX … is 204.9 km
 * away") in the same prose and was blocked anyway. The same report's v1.22.0
 * ancestor, whose store happened to hold a SECOND km value, raised exactly one
 * km violation — the genuine "200 km" near-miss of 204.9. Same unit, same
 * domain, opposite outcomes decided by store cardinality alone.
 *
 * Why distance and not MW/acres: a site report's MW and acreage figures have a
 * DOMINANT referent (the campus load, the parcel) that the catalog governs, so
 * an unmatched same-unit figure most plausibly restates it. Distance names no
 * subject at all — it measures a RELATION between two things, and prose cites
 * fiber routes, pipeline offsets, search radii and haul roads that the catalog
 * (4 point-of-interest proximities) will never govern. The unit is a measure,
 * not a subject.
 *
 * This does NOT loosen the gate to a blanket warn: a mention within the
 * near-miss band still blocks (the "200 km" case), and so does one whose prose
 * NAMES the governed fact's subject — see `namesGovernedSubjectNearby`.
 *
 * `years` joins on the same reasoning: a duration measures the interval between
 * two events and names neither, while the catalog governs exactly ONE duration
 * (`grid.queueYears`). Prose cites permitting windows, construction schedules,
 * PPA terms, equipment lead times and pre-energization windows — the live
 * v1.22.1 body blocked "12-24 months for permitting and construction" and
 * "6–12 months pre-energization" against the interconnection queue.
 *
 * `subjectAttributable` — whether prose naming the fact's distinctive token
 * reliably means the figure IS that fact (see `namesGovernedSubjectNearby`).
 *   km    TRUE.  `substationDistKm` → "substation", an ENDPOINT of the measured
 *                distance; naming it next to a figure means that figure is the
 *                distance to it.
 *   years FALSE. `grid.queueYears` → "queue", a TOPIC word. Measured across the
 *                saved bodies, prose uses it for industry benchmarks ("Average
 *                US interconnection queue time: 5+ years (2023)", "FERC Order
 *                2023 reforms aim to reduce to 2-3 years") as readily as for
 *                this project's queue, with identical surface cues. Attribution
 *                is therefore not demonstrable, and the truth-only gate warns
 *                rather than blocking. A garbled restatement of the canonical
 *                still blocks via the near-miss band.
 */
const DOMAIN_POLYSEMOUS_UNITS = new Map<string, { subjectAttributable: boolean }>([
  ['km', { subjectAttributable: true }],
  ['years', { subjectAttributable: false }],
  // A system apparent-power requirement and an individual transformer's
  // nameplate rating are both stated in MVA. The store currently governs the
  // former (`power.apparentMva`), while supply-chain prose routinely states the
  // latter. Treat a far, unattributed component rating as contextual; prose
  // that names "apparent" power still binds to the governed system fact.
  ['MVA', { subjectAttributable: true }],
  /*
   * `MWh` joins on the duration reasoning (FIN-1, audit 3.8): an energy
   * quantity names no subject. The catalog governs exactly ONE
   * (`finance.annualMwh`, the model's annual generation) while report prose
   * cites monthly output, per-phase energy, curtailed energy, contracted
   * offtake volume and datacentre consumption. Left monosemous, the single
   * governed value would have blocked every one of them — the `ix.distanceKm`
   * failure shape. `subjectAttributable: false`: the factId's only distinctive
   * token is "annual", a scoping word rather than a subject.
   */
  ['MWh', { subjectAttributable: false }],
]);

/* ── Subject-keyed units (FIN-1, audit 3.8) ──────────────────────────── */

/**
 * Units whose polysemy is resolved by NAMING THE SUBJECT, not by counting
 * store values.
 *
 * `%` and `USD` are the extreme case of the `DOMAIN_POLYSEMOUS_UNITS` problem.
 * A report's percentages are IRR, WACC, hurdle, capacity factor, availability,
 * escalation, tax rate, debt share and wetland coverage; its dollar figures are
 * NPV, CAPEX, OPEX, a deposit, a tax credit and a land price. Treating either
 * as one class would let a capacity factor "contradict" an IRR — the defect
 * this rule exists to prevent — while the blunt warn-everything fallback would
 * leave a substituted IRR unflagged.
 *
 * THE RULE, and why it is NOT the reverted v1.24.0 MW classifier:
 *
 *  1. A mention is ATTRIBUTED only when its ±`SUBJECT_WINDOW` prose names
 *     EXACTLY ONE governed subject. Two subjects in the window (the engine's
 *     own "IRR (5.2%) is more than 2 pts below the 8.0% hurdle") is
 *     AMBIGUOUS, not a coin-flip: nearest-marker guessing is what made the MW
 *     classifier block correct sentences against the wrong fact.
 *  2. Attribution only ever NARROWS to same-subject facts. When the narrowed
 *     set is EMPTY the mention is ungoverned and the checker goes SILENT — it
 *     never falls back to a different subject's value. So the worst outcome of
 *     a mis-read subject is a missed catch, never a false accusation.
 *  3. `requireSubject: true` (`%`, `USD`) — no subject ⇒ no candidates. These
 *     units have no dominant referent; comparing an unlabelled figure against
 *     an arbitrary same-unit fact is the `ix.distanceKm` false-positive storm.
 *     `requireSubject: false` (`USD/MWh`, `USD/kW`) — a rate DOES have a
 *     dominant referent (this project's cost of energy / capital intensity),
 *     and the store governs its siblings explicitly (PPA price, fuel cost), so
 *     an unattributed rate keeps the ordinary store-polysemy behaviour.
 */
const SUBJECT_KEYED_UNITS = new Map<string, { requireSubject: boolean; comparativeVeto?: boolean }>([
  ['%', { requireSubject: true }],
  ['USD', { requireSubject: true }],
  // `comparativeVeto` — these two units are the ones the BTM technology
  // comparison enumerates per option, and they are `requireSubject: false`, so
  // an unattributed rate would otherwise fall through to the whole same-unit
  // set and collide with this project's governed rate. See
  // `COMPARATIVE_FRAMING_RE`.
  ['USD/MWh', { requireSubject: false, comparativeVeto: true }],
  ['USD/kW', { requireSubject: false, comparativeVeto: true }],
]);

/**
 * `factId` → the subject it measures, for the subject-keyed units.
 *
 * Subjects only ever have to discriminate WITHIN one unit class, so the total
 * CAPEX (`USD`) and the CAPEX intensity (`USD/kW`) share the subject `capex`:
 * the unit already tells them apart, and splitting them would mean a prose
 * "capital cost of $1,850/kW" failed to attribute to either.
 */
const FACT_SUBJECT: Readonly<Record<string, string>> = {
  // percent
  'finance.irrPct': 'irr',
  'finance.hurdleRatePct': 'hurdle',
  'finance.waccPct': 'wacc',
  capacityFactorPct: 'capacity-factor',
  'wind.resourceCapacityFactorPct': 'wind-resource-capacity-factor',
  // money
  'finance.npvUsd': 'npv',
  'finance.capexTotalUsd': 'capex',
  // rates
  'finance.lcoeUsdPerMwh': 'lcoe',
  'lcoe.blendedPerMwhUsd': 'lcoe',
  ppaPricePerMwhUsd: 'ppa',
  fuelCostPerMwhUsd: 'fuel',
  'finance.capexPerKwUsd': 'capex',
  capexPerKwUsd: 'capex',
};

/**
 * Prose markers for each subject. TERMS OF ART ONLY — every entry is a phrase
 * whose presence is about that quantity and nothing else. Deliberately
 * excludes topic words ("finance", "cost", "rate", "return"), the same
 * discipline `QUEUE_DOMAIN_RE` applies to durations.
 *
 * SUBJECTS THE STORE DOES NOT GOVERN ARE LISTED ANYWAY — deposit, OPEX,
 * revenue, tax credits, escalation, availability. Listing them makes the
 * classifier STRICTER, not looser: a window naming both "deposit" and "CAPEX"
 * becomes ambiguous and goes silent, where omitting "deposit" would have let
 * the engine's own "Procurement deposit $12M = 2.4% of the $500M CAPEX
 * package" sentence read its deposit as a contradiction of the CAPEX total.
 * Every name added here is a false accusation removed.
 */
const SUBJECT_MARKERS: ReadonlyArray<readonly [string, RegExp]> = [
  ['irr', /\bIRR\b|internal rate of return/i],
  ['hurdle', /\bhurdle\b|target return|required return/i],
  ['wacc', /\bWACC\b|weighted[- ]average cost of capital|discount rate|cost of capital/i],
  ['capacity-factor', /capacity factor/i],
  // Legacy resource wording remains a distinct ambiguous topic. Explicit
  // figure-local wind-resource CF wording is bound separately below, to the
  // saved estimated wind fact; it cannot borrow the project's finance CF.
  ['renewable-resource', /\b(?:wind|solar) resources?\b|\b(?:wind|solar) potentials?\b|\b(?:wind|solar) capacity factor\b/i],
  ['availability', /\bavailability\b|\buptime\b/i],
  ['npv', /\bNPV\b|net present value/i],
  ['capex', /\bCAPEX\b|capital expenditure|(?<!risk[- ])\bcapital cost|installed cost|overnight cost|capital intensity|per[- ]kW\b|\/\s?kW\b/i],
  // A line-item / mitigation cost is not the project's TOTAL CAPEX. Naming it
  // as a separate subject makes a nearby total-CAPEX marker ambiguous (and
  // therefore un-attributable) instead of mapping e.g. "<$1M in direct costs"
  // to the site's $80M finance.capexTotalUsd stamp.
  ['component-cost', /\b(?:direct|mitigation|remediation|diligence|permitting|study|environmental) costs?\b/i],
  ['opex', /\bOPEX\b|operating cost|O&M|operations and maintenance/i],
  ['lcoe', /\bLCOE\b|level(?:i[sz]ed|ised|ized) cost/i],
  ['ppa', /\bPPA\b|power purchase|offtake price/i],
  ['fuel', /\bfuel (?:cost|price)/i],
  ['tariff', /\btariff\b|retail\s+(?:rate|price|benchmark|sales)|industrial\s+(?:average|rate|benchmark)/i],
  ['all-in-rate', /\ball[- ]in\b/i],
  ['deposit', /\bdeposit\b/i],
  ['revenue', /\brevenue\b|gross margin|\bEBITDA\b/i],
  ['tax-credit', /\bITC\b|\bPTC\b|tax credit|investment tax|production tax/i],
  ['tax-rate', /tax rate/i],
  ['payback', /\bpayback\b/i],
  ['project-life', /design life|project life/i],
  ['escalation', /escalat/i],
  ['debt-share', /debt[- :]to[- ]equity|debt share|gearing|leverage/i],
  ['contingency', /contingency/i],
  // The ungoverned `site-share` subject is deliberately NOT a window marker:
  // it is anchored to the figure itself — see `isSiteAreaShareAt`.
];

/**
 * A percentage that IS an area share of the site — the figure itself is
 * followed by "of the (<N>-acre) site / parcel / study area / property" or by
 * "lies / is / falls within … floodplain / flood zone / wetland", or it sits
 * in "portion / share / fraction of the site (<N>%)".
 *
 * The resulting `site-share` subject is ungoverned (no percentage fact
 * carries it), so it is never compared against anything. It only makes a
 * figure that ALSO sits near a finance marker ambiguous — "the most
 * significant hurdle is constructability, as 34% of the site lies within the
 * floodplain" — which goes silent under rule 1 instead of accusing the absent
 * hurdle rate. The bare `hurdle` marker is deliberately unchanged.
 *
 * FIGURE-ANCHORED, never window-wide: a site word merely NEAR a finance
 * figure ("The equity hurdle rate is 15%, reflecting wetland permitting
 * risk", "Total CAPEX of $900M includes floodplain fill") must not make a
 * governed hurdle / IRR / CAPEX / NPV figure ambiguous; those keep being
 * compared. Only percentages can be area shares.
 */
const SITE_AREA_NOUN = String.raw`(?:site|parcel|study\s+area|property)`;
const SITE_SHARE_AFTER_RE = new RegExp(
  String.raw`^\)?\s+(?:of\s+the\s+(?:[\d,.]+[- ]acre\s+)?${SITE_AREA_NOUN}\b`
  + String.raw`|(?:of\s+the\s+\w+\s+)?(?:lies|lying|is|are|falls?|falling)\s+within\s+(?:the\s+|an?\s+)?(?:\S+\s+){0,3}(?:floodplains?|flood\s+zones?|wetlands?)\b)`,
  'i',
);
const SITE_SHARE_BEFORE_RE = new RegExp(
  String.raw`\b(?:portion|share|fraction)\s+of\s+the\s+(?:[\d,.]+[- ]acre\s+)?${SITE_AREA_NOUN}\s*\(\s*$`,
  'i',
);

function isSiteAreaShareAt(clean: string, index: number, raw: string): boolean {
  if (!/^\d[\d,]*\.?\d*\s?(?:%|percent|pct)$/i.test(raw)) return false;
  const end = index + raw.length;
  return SITE_SHARE_AFTER_RE.test(clean.slice(end, end + 120))
    || SITE_SHARE_BEFORE_RE.test(clean.slice(Math.max(0, index - 80), index));
}

/**
 * ±chars of prose consulted for the subject. Proximity, not sentence
 * membership: a serialized record row or a long enumerating sentence lends a
 * far-away marker to a figure it has nothing to do with (the pipeline/kV-line
 * case that forced `governanceWindow` to use proximity).
 */
const SUBJECT_WINDOW = 48;

/**
 * COMPARATIVE FRAMING — the figure is one of several ALTERNATIVES being
 * enumerated, not a statement about this project.
 *
 * Sibling of the MW `isComparisonBoundPrefix` guard: same idea, different
 * syntax. `capacity-factor` and `lcoe` are PER-TECHNOLOGY quantities, and the
 * BTM technology-comparison surfaces enumerate them by design —
 * "- Technology: SMR | Capacity factor: 95% | LCOE: $110/MWh". The store
 * governs ONE of each (this project's), so an enumerated row would otherwise
 * "contradict" it on every comparison table the platform renders.
 *
 * Deliberately keyed to comparison SYNTAX, not to technology words: vetoing on
 * "gas" or "solar" would silence the plain project statement ("the gas plant's
 * LCOE is $95/MWh"), which is exactly the figure this gate exists to check.
 */
const COMPARATIVE_FRAMING_RE =
  /\balternativ\w*|\bcomparison\b|\bcompared\s+(?:with|to)\b|\bbenchmark\w*|\bby contrast\b|\bversus\b|\bvs\.?(?=[\s)])|\boption\s+[A-Z0-9]|\bfor reference\b|technology\s*:/i;

/** Subjects whose attribution is refused inside comparative framing. */
const SUBJECT_ATTRIBUTION_VETO: Readonly<Record<string, RegExp>> = {
  'capacity-factor': COMPARATIVE_FRAMING_RE,
  lcoe: COMPARATIVE_FRAMING_RE,
};

/** Bind the percentage itself, never a finding title or distant wind word.
 * The native resource estimate and project/financial CF remain independent,
 * including when both appear in one finding. Wrong or absent wind values are
 * checked by the ordinary same-subject numeric path, not exempted. */
function capacityFactorRoleAt(clean: string, index: number, raw: string): string | undefined {
  if (!/^\d[\d,]*\.?\d*\s?(?:%|percent|pct)$/i.test(raw)) return undefined;
  const range = sentenceRangeAt(clean, index);
  const before = clean.slice(Math.max(range.start, index - 180), index)
    .split(/[;,\n|]|\b(?:but|while|whereas|unlike|despite|however|although)\b/i).pop() ?? '';
  const after = clean.slice(index + raw.length, Math.min(range.end, index + raw.length + 100));
  const plainCfAfter = /^\s*capacity\s+factor\b/i.test(after);
  // Direct project/finance attribution takes precedence over a wind word.
  if (/\b(?:project|financial|finance[- ]model|plant)\s+capacity\s+factor\s*(?:(?:is|of|at)\s+|[:=]\s*)?(?:an?\s+)?(?:estimated\s+)?$/i.test(before)
    || /^\s*(?:project|financial|finance[- ]model|plant)\s+capacity\s+factor\b/i.test(after)
    || /^\s*capacity\s+factor\s+(?:for|of)\s+(?:the\s+)?(?:project|financial\s+model|plant)\b/i.test(after)
    || (plainCfAfter && /\b(?:project|financial\s+model|plant)(?:['’]s)?\s+(?:assumes?|uses?|has)\s+(?:an?\s+)?(?:estimated\s+)?$/i.test(before))) {
    return 'capacity-factor';
  }
  const windCfBefore = /\bwind(?:[- ]resource)?\s+capacity\s+factor\s*(?:(?:is|of|at)\s+|[:=]\s*)?(?:an?\s+)?(?:estimated\s+)?$/i.test(before);
  const windSpeedWithBefore = /\bwind\s+speed\b[^;,\n|]*\bwith\s+(?:an?\s+)?(?:estimated\s+)?$/i.test(before);
  const windResourcesBefore = /\bwind\s+resources?\s*(?:\(\s*|(?:with|of)\s+(?:an?\s+)?(?:estimated\s+)?)$/i.test(before);
  const resourceCfAfter = /^\s*(?:wind[- ]resource\s+)?capacity\s+factor\b/i.test(after);
  return windCfBefore || ((windSpeedWithBefore || windResourcesBefore) && resourceCfAfter)
    ? 'wind-resource-capacity-factor' : undefined;
}

/**
 * The single subject named around `index`, or undefined when the prose names
 * none — or names TWO, which is not attributable (see rule 1 above). `raw` is
 * the mention's surface token, for the figure-anchored `site-share` subject.
 */
function classifySubjectAt(clean: string, index: number, raw: string): string | undefined {
  const window = proximityAt(clean, index, SUBJECT_WINDOW);
  const capacityFactorRole = capacityFactorRoleAt(clean, index, raw);
  if (capacityFactorRole !== undefined) {
    const veto = SUBJECT_ATTRIBUTION_VETO[capacityFactorRole];
    return veto && veto.test(window) ? undefined : capacityFactorRole;
  }
  const hits = new Set<string>();
  for (const [subject, re] of SUBJECT_MARKERS) {
    if (re.test(window)) hits.add(subject);
  }
  if (isSiteAreaShareAt(clean, index, raw)) hits.add('site-share');
  if (hits.size !== 1) return undefined;
  const subject = [...hits][0]!;
  const veto = SUBJECT_ATTRIBUTION_VETO[subject];
  return veto && veto.test(window) ? undefined : subject;
}

/**
 * Narrow a same-unit candidate list to the mention's subject.
 *
 * Returns the ORIGINAL list untouched for every unit that is not subject-keyed
 * — MW, km, acres, years and MVA keep their behaviour byte-for-byte.
 */
function narrowToSubject<T extends { factId: string }>(
  unit: string,
  candidates: readonly T[],
  subject: string | undefined,
  comparative = false,
): readonly T[] {
  const rule = SUBJECT_KEYED_UNITS.get(unit);
  if (!rule) return candidates;
  // Enumerated alternatives are not claims about this project's quantity.
  if (rule.comparativeVeto && comparative) return [];
  if (subject !== undefined) return candidates.filter((c) => FACT_SUBJECT[c.factId] === subject);
  // No attributable subject: a unit with no dominant referent governs nothing
  // here; one with a dominant referent keeps the whole same-unit set.
  return rule.requireSubject ? [] : candidates;
}

/**
 * The verbatim sentence containing `index`, for violation locators.
 *
 * Sentence, not a fixed character window: the operator is shown this string as
 * the evidence for a block, and a window clipped mid-clause ("...estimate of
 * 26 MW on the 34.5kV line and 4.13 MW on") reads as a different claim than
 * the one the author wrote. Falls back to a bounded window when the prose has
 * no sentence punctuation (tables, bullet rows).
 */
function sentenceRangeAt(text: string, index: number, maxLen = 400): { start: number; end: number } {
  const from = Math.max(0, index - maxLen);
  const before = text.slice(from, index);
  const after = text.slice(index, index + maxLen);
  // Start after the last sentence terminator or hard line break before the hit.
  const startRel = Math.max(
    before.lastIndexOf('. '),
    before.lastIndexOf('\n'),
    before.lastIndexOf('! '),
    before.lastIndexOf('? '),
  );
  const start = startRel >= 0 ? from + startRel + 1 : from;
  // End at the first terminator after it. A decimal point ("4.13") is not a
  // terminator — require the following space/newline/end.
  const endMatch = after.match(/[.!?](?=\s|$)|\n/);
  const end = endMatch?.index !== undefined
    ? index + endMatch.index + 1
    : Math.min(text.length, index + maxLen);
  return { start, end };
}

function sentenceAt(text: string, index: number, maxLen = 400): string {
  const { start, end } = sentenceRangeAt(text, index, maxLen);
  return text.slice(start, end).trim();
}

/**
 * Text within `radius` characters of `index`, CLIPPED to the enclosing sentence.
 *
 * Used for absent-fact domain governance, where sentence-wide marker matching is
 * too coarse. Live (2026-08-17): one sentence enumerated four assets —
 * "an existing Entergy 500kV line (1.43 km away), a proposed 230kV line
 *  (0.11 km away), … and the Tennessee Gas Pipeline crossing the parcel"
 * — so the word `Pipeline`, 124 characters away, lent its governed domain to
 * every kV-line distance in the sentence and blocked all of them.
 *
 * Clipping to the sentence matters as much as the radius: without it a marker in
 * the PRECEDING sentence ("The nearest substation is remote. A line runs 1.43 km
 * away.") would be pulled in, which is the same membership error one scope up.
 */
function proximityAt(text: string, index: number, radius = 80): string {
  const { start, end } = sentenceRangeAt(text, index);
  return text.slice(Math.max(start, index - radius), Math.min(end, index + radius));
}

/**
 * True when `sentence` restates `value` AS A QUANTITY IN THE SAME UNIT.
 *
 * UNIT-AWARE BY NECESSITY. A digit-only test is not a restatement test: it
 * cannot tell "5.2 km" from "$5.2 million", "5.2 miles" or "5.2%". Measured
 * with the digit-only version, all of these silently cleared a real
 * substitution — including the golden Moss Bluff claim, whose own comment says
 * it must keep blocking:
 *
 *   "At an estimated $5.2 million cost, the substation lies 3.0 km away."
 *   "The substation lies 3.0 km (5.2 miles by road) from the parcel."
 *   "...40 MW of hosting capacity [Ref 26]"   <- a CITATION INDEX
 *
 * Re-tokenising the sentence and requiring a same-unit mention within
 * tolerance closes all of them: a dollar amount, a percentage, a citation
 * marker and a mile figure are each either untagged or tagged to another unit.
 *
 * This is what `internalNumberReconciler`'s co-occurrence rule actually
 * guarantees — its two values come from the SAME canonical key, so they share
 * unit and role by construction. The digit-only version cited it as precedent
 * without inheriting that guarantee.
 */
function sentenceRestatesGovernedValue(
  sentence: string,
  value: number,
  unit: string,
  tolerancePct: number,
): boolean {
  if (!Number.isFinite(value)) return false;
  return extractTaggedNumbers(sentence).some(
    (n) => n.unit === unit && numericMatch(n.value, value, unit, tolerancePct),
  );
}


/* ── Absent-fact assertion detection (FIX 1) ─────────────────────────────
 *
 * "It will deliver a firm 49 MW" must BLOCK even when other MW facts exist:
 * the dedicated fact (firmCapacity.targetMw) is ABSENT — the evidence never
 * set a firm MW. Detection: an UNMATCHED same-unit mention whose nearby text
 * carries a DISTINCTIVE keyword of an absent fact (derived from its label +
 * factId, generic tokens excluded) is an invented-number, not a mismatch.
 * Negated keywords ("indicated, not firm") do not fire.
 */

/**
 * Label/factId tokens too generic to attribute a mention to an absent fact.
 * Unit words ("acres") are here because the factId camel-split leaks them
 * (`buildableAcres` → "acres") and EVERY same-unit mention co-locates its
 * unit word — it discriminates nothing. Provenance words ("developer",
 * "stated") likewise describe the source, not the quantity.
 */
const GENERIC_LABEL_TOKENS = new Set<string>([
  'nearest', 'grid', 'power', 'site', 'area', 'total', 'capacity', 'target',
  'distance', 'dist', 'estimated', 'typical', 'module', 'output', 'land',
  'under', 'over', 'with', 'from', 'technology', 'stated', 'jurisdictional',
  'acres', 'acre', 'miles', 'mile', 'years', 'year', 'developer',
  /*
   * FIN-1 / audit 3.8 — the financial topic words. Every `finance.*` factId
   * camel-splits to "finance", and the engine's own action plan says
   * "owner: Finance lead", so a single "finance" hit would have let an absent
   * NPV accuse any nearby dollar figure. Same class as "queue" in
   * `absentFactDomain`: a TOPIC word is not evidence about a quantity.
   * "project" and "model" are here for the same reason; the DISCRIMINATING
   * tokens survive ("lcoe", "payback", "wacc", "capex", "hurdle").
   */
  'finance', 'financial', 'project', 'model', 'rate',
]);

/** Distinctive keywords for an absent fact ("Firm capacity target" → firm). */
function absentAssertionKeywords(factId: string, label: string): string[] {
  const camelSplit = factId.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[._]/g, ' ');
  const tokens = `${label} ${camelSplit}`
    .toLowerCase()
    .split(/[^a-z]+/)
    .filter((t) => t.length >= 4 && !GENERIC_LABEL_TOKENS.has(t));
  return [...new Set(tokens)];
}

/** Negators immediately before a keyword suppress the hit ("not firm"). */
const KEYWORD_NEGATION_RE = /(?:\b(?:not|no|non|never|without)\b|n['’]t)[-–—\s,;:()]*(?:\w+[-–—\s,;:()]*){0,2}$/i;

/**
 * True when `keyword` appears as a whole word, UN-negated, within ±window
 * chars of `index` in `text`.
 */
function hasUnnegatedKeywordNearby(
  text: string,
  index: number,
  keyword: string,
  window = 60,
): boolean {
  const start = Math.max(0, index - window);
  const slice = text.slice(start, index + window);
  const re = new RegExp(`\\b${keyword}\\b`, 'gi');
  let m: RegExpExecArray | null;
  while ((m = re.exec(slice)) !== null) {
    const before = slice.slice(Math.max(0, m.index - 28), m.index);
    if (!KEYWORD_NEGATION_RE.test(before)) return true;
  }
  return false;
}

/**
 * True when the prose around `index` NAMES the subject of `factId` — the
 * attribution guard for `DOMAIN_POLYSEMOUS_UNITS`.
 *
 * "The site interconnects at the Moss Bluff substation, 3.0 km to the north"
 * says *substation* next to the figure while `substationDistKm` is 5.2: that is
 * a substitution of the governed quantity and must keep blocking, even though
 * km is polysemous by domain. "…within a 50 km radius" names no governed
 * subject and must not.
 *
 * Keywords come from the factId alone — `NumericFactIndexEntry` carries no
 * label — via the same derivation the absent-fact assertion check uses, so the
 * two attribution paths stay consistent. A factId yielding NO distinctive token
 * (`ix.distanceKm` → "ix" too short, "distance" generic, "km" too short) can
 * never be attributed, so its far mentions always fall through. That is the
 * deliberate fail-open direction: with nothing in the prose tying the figure to
 * the fact, a block would assert an untruth the evidence does not support.
 */
function namesGovernedSubjectNearby(factId: string, text: string, index: number): boolean {
  return absentAssertionKeywords(factId, '').some((kw) =>
    hasUnnegatedKeywordNearby(text, index, kw),
  );
}

/**
 * A study CASE / phase / threshold is not the site's current grid headroom.
 *
 * The live Redhead SIS says that a future topology was evaluated at 50, 100,
 * 200, and 300 MW and that overloads start above 200 MW. A legacy portfolio
 * workbook separately supplied a 194.3 MW ATC value. Because 200 is within the
 * generic MW near-miss band of 194.3, every quoted SIS scenario was falsely
 * attributed to grid.headroomMw and held the report.
 *
 * This veto is intentionally two-sided:
 *  - scenario/threshold language is required; a bare different MW still checks;
 *  - explicit headroom/ATC language defeats the veto, so "headroom is 200 MW"
 *    remains a blocking mismatch.
 *
 * It changes attribution only. The scenario number remains subject to the
 * citation and internal-consistency gates.
 */
const MW_SCENARIO_OR_THRESHOLD_RE =
  /\b(?:phase|load ramp|scenario|study case|sensitivity|threshold|scal(?:e|ing)|above|below|greater than|less than|at least|at most|or more|or less|new overloads?|trigger(?:s|ed)?|caus(?:e|es|ed)|requires?)\b/i;
const EXPLICIT_HEADROOM_RE =
  /\b(?:grid )?headroom\b|hosting capacity|available transfer capacity|\bATC\b/i;

function isHeadroomScenarioMention(sentence: string, factId: string): boolean {
  if (factId !== 'grid.headroomMw' && factId !== 'grid.headroomIndicatedMw') return false;
  return MW_SCENARIO_OR_THRESHOLD_RE.test(sentence) && !EXPLICIT_HEADROOM_RE.test(sentence);
}

/* ── Number extraction (unit-tagged + bare) ──────────────────────────── */

/**
 * Strip `[Ref N]` citation tokens so a 1-based ref index is never mistaken
 * for a datum (mirrors the editorial-grounding number scan).
 */
/**
 * Blank out `[Ref N]` citation tokens WITHOUT changing the string's length.
 *
 * Length preservation is load-bearing (2026-08-14). `extractTaggedNumbers`
 * indexes into the RAW narrative, while every window scan — the qualifier
 * check, the absent-fact keyword scan, the governed-subject test, and now the
 * MW subject classifier and violation excerpts — reads `clean` at that same
 * index. Collapsing each token to one space shortened `clean` by ~7 characters
 * per citation, so on citation-dense prose (our reports carry dozens) the
 * windows slid progressively LEFT of the figure they were meant to describe
 * and scanned unrelated text. Same-length blanking keeps every index valid.
 */
function stripRefTokens(text: string): string {
  return text.replace(new RegExp(NUMBERED_REF_TOKEN_RE.source, NUMBERED_REF_TOKEN_RE.flags), (m) => ' '.repeat(m.length));
}

/** A number lifted from prose, tagged with the store-unit its co-located unit implies. */
interface TaggedNumber {
  raw: string;
  /** The store display-unit this maps to (`'MW'`, `'km'`, `'acres'`, …), or undefined for bare. */
  unit?: string;
  value: number;
  /** ± character window around the match, for the unlabeled-estimate qualifier scan. */
  index: number;
  /**
   * Lower bound when the value closes a stated range ("12-24 months" → 12, in
   * the SAME normalized unit as `value`). A range that BRACKETS a governed
   * value is consistent with it — see the bracket check in `checkNumerics`.
   */
  rangeLow?: number;
}

/* ── Money / percent / energy scale (FIN-1, audit 3.8) ───────────────── */

/**
 * Magnitude suffix → multiplier, for money written at scale.
 *
 * THE POINT OF THIS TABLE: `$0.142B`, `$142M` and `142,000,000` are the SAME
 * number. The financial stamp persists `npvUsd` in PLAIN USD (142000000, not
 * 142), so a comparator that assumed "$" figures were millions would find a
 * 1e6-scale contradiction in every correct sentence. Scale is normalised at
 * PARSE time — before any comparison — in this engine and, in lockstep, in
 * `internalNumberReconciler` (which imports these helpers rather than
 * re-deriving them: two engines that disagree about what "$142M" means emit
 * contradictory guidance for one figure).
 */
const MONEY_SCALE: ReadonlyArray<[RegExp, number]> = [
  [/^(?:t|tn|trillion)$/i, 1e12],
  [/^(?:b|bn|billion)$/i, 1e9],
  [/^(?:m|mm|million)$/i, 1e6],
  [/^(?:k|thousand)$/i, 1e3],
];

/**
 * Parse a USD magnitude to PLAIN dollars. `suffix` is the (optional) scale word
 * captured next to the figure. Returns null for a non-finite token.
 */
export function parseUsdAmount(raw: string, suffix?: string): number | null {
  const n = parseFloat(raw.replace(/[$,\s]/g, ''));
  if (!Number.isFinite(n)) return null;
  const token = (suffix ?? '').trim();
  if (!token) return n;
  const hit = MONEY_SCALE.find(([re]) => re.test(token));
  return hit ? n * hit[1] : n;
}

/** Parse a percentage to POINTS (13.4% → 13.4, never 0.134). */
export function parsePercentPoints(raw: string): number | null {
  const n = parseFloat(raw.replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}

/** Parse an energy quantity to MWh (`GWh` ×1e3, `TWh` ×1e6). */
export function parseEnergyMwh(raw: string, unit?: string): number | null {
  const n = parseFloat(raw.replace(/,/g, ''));
  if (!Number.isFinite(n)) return null;
  const u = (unit ?? 'mwh').toLowerCase();
  if (u.startsWith('t')) return n * 1e6;
  if (u.startsWith('g')) return n * 1e3;
  return n;
}

/**
 * Unit → store display-unit + parser. Co-location of value + unit is required
 * (a bare "350" never cross-matches a km against an MW). The store's numeric
 * facts carry these display units (`power.declaredMw` → `'MW'`, `siteArea.acres`
 * → `'acres'`, `substationDistKm` → `'km'`, `finance.npvUsd` → `'USD'`).
 *
 * `display` overrides the default `"<number> <unit>"` echo for the money and
 * percent classes, whose surface form carries the `$` / `%` on the OTHER side
 * of the digits: without it a violation about `$78/MWh` read `Narrative states
 * "78 MWh"`, naming a unit (and a quantity) the author never wrote.
 */
const UNIT_PATTERNS: Array<{
  re: RegExp;
  unit: string;
  parse: (raw: string, unit: string) => number | null;
  display?: (m: RegExpExecArray) => string;
}> = [
  { re: /(\d[\d,]*\.?\d*)\s?(gw|gigawatts?)\b/gi, unit: 'MW', parse: (raw) => parseCapacityMw(raw, 'gw') },
  { re: /(\d[\d,]*\.?\d*)\s?(mva)\b/gi, unit: 'MVA', parse: (raw) => num(raw) },
  { re: /(\d[\d,]*\.?\d*)\s?(mw|megawatts?)\b/gi, unit: 'MW', parse: (raw) => parseCapacityMw(raw, 'mw') },
  { re: /(\d[\d,]*\.?\d*)\s?(km|kilometers?|kilometres?)\b/gi, unit: 'km', parse: (raw) => parseDistanceKm(raw, 'km') },
  { re: /(\d[\d,]*\.?\d*)\s?(mi|miles?)\b/gi, unit: 'km', parse: (raw) => parseDistanceKm(raw, 'mi') },
  /*
   * ACRE-FEET IS A VOLUME, NOT AN AREA (2026-08-14). Without the lookahead
   * "1,613 acre-feet/year" tokenised as "1,613 acres" and value-mismatched
   * against the governed site acreage — a BLOCKING truth violation, fired on
   * a correct sentence, on any report that states a water volume. Measured
   * against a verified siteArea.acres = 800:
   *   "Annual consumptive use is 1,613 acre-feet/year" -> block/value-mismatch
   *     'Narrative states "1,613 acre" for siteArea.acres; the only permitted
   *      value is 800 acres.'
   * Water-resource narratives state acre-feet routinely, so this hard-held
   * them platform-wide. Acre-feet is left UNGOVERNED rather than folded into
   * an area unit it does not share a dimension with.
   */
  { re: /(\d[\d,]*\.?\d*)\s?(acres?|ac\.?)(?!-?\s?(?:feet|ft\b|foot)|\s*(?:(?:of\s+exclusion\s+zones?\s+)?(?:\/\s*|\s+per\s+)(?:(?:\d[\d,.]*\s*(?:to|[-–—])\s*)?\d[\d,.]*\s*(?:mw|megawatts?|gw|gigawatts?|kw|kilowatts?)\s+)?(?:mw|megawatts?|gw|gigawatts?|kw|kilowatts?|turbines?)\b))\b/gi, unit: 'acres', parse: (raw) => parseAreaAcres(raw, 'ac') },
  // X-SED-6 — the hyphenated adjectival form ("a 1,100-acre parcel", "the
  // 1,170.1-acre site"): the `\s?` pattern above cannot cross the hyphen.
  // Same volume guard as above: "a 1,613-acre-foot allocation" is not an area.
  { re: /(\d[\d,]*\.?\d*)-(acre|ac)(?!-?\s?(?:feet|ft\b|foot)|\s*(?:(?:of\s+exclusion\s+zones?\s+)?(?:\/\s*|\s+per\s+)(?:(?:\d[\d,.]*\s*(?:to|[-–—])\s*)?\d[\d,.]*\s*(?:mw|megawatts?|gw|gigawatts?|kw|kilowatts?)\s+)?(?:mw|megawatts?|gw|gigawatts?|kw|kilowatts?|turbines?)\b))\b/gi, unit: 'acres', parse: (raw) => parseAreaAcres(raw, 'ac') },
  { re: /(\d[\d,]*\.?\d*)\s?(ha|hectares?)\b/gi, unit: 'acres', parse: (raw) => parseAreaAcres(raw, 'ha') },
  // Durations → the store's `years` display unit (`grid.queueYears`). Without
  // these the interconnection queue was ungoverned: a fabricated "the queue is
  // 5 years" against a canonical 1.5 rode through unchecked.
  { re: /(\d[\d,]*\.?\d*)\s?(months?|mos?)\b/gi, unit: 'years', parse: (raw) => parseDurationYears(raw, 'mo') },
  { re: /(\d[\d,]*\.?\d*)\s?(years?|yrs?)\b/gi, unit: 'years', parse: (raw) => parseDurationYears(raw, 'yr') },

  /* ── MONEY (FIN-1, audit 3.8) ────────────────────────────────────────
   *
   * RATES BEFORE MAGNITUDES, and the magnitude pattern REFUSES a rate
   * (`(?!\s*(?:\/|per\b))`). `$78/MWh` is a levelised cost, `$78` is a sum of
   * money; folding them into one class would compare an LCOE against an NPV.
   * The three classes stay dimensionally separate, exactly as km/acres do.
   */
  {
    // `$78/MWh`, `$78 per MWh` — LCOE / PPA / fuel / market rates.
    re: /\$\s?(\d[\d,]*\.?\d*)\s?(?:\/\s?|\s?per\s+)(MWh|megawatt[- ]hours?)\b/gi,
    unit: 'USD/MWh',
    parse: (raw) => parseUsdAmount(raw),
    display: (m) => m[0]!,
  },
  {
    // `$1,850/kW` — CAPEX intensity. `(?!h)` keeps `$/kWh` out (an energy
    // price). Extraction also checks the complete local denominator below:
    // `$/kW/month` is a recurring demand rate, not capital intensity.
    re: /\$\s?(\d[\d,]*\.?\d*)\s?(?:\/\s?|\s?per\s+)(kW)\b(?!h)/gi,
    unit: 'USD/kW',
    parse: (raw) => parseUsdAmount(raw),
    display: (m) => m[0]!,
  },
  {
    // `$142M`, `$0.142B`, `$1,850`, `USD 142 million` — a SUM of money.
    //
    // `(?![\d.,])` after the figure is LOAD-BEARING, not cosmetic: without it
    // the engine backtracks a rejected `$78/MWh` into a match on `$7`,
    // manufacturing a seven-dollar sum out of a levelised cost.
    re: /(?:\$|\bUSD\s)\s?(\d[\d,]*\.?\d*)(?![\d.,])(?:\s?(trillion|billion|million|thousand|tn|bn|mm|[tbmk])\b)?(?!\s*(?:\/|per\b))/gi,
    unit: 'USD',
    parse: (raw, suffix) => parseUsdAmount(raw, suffix),
    display: (m) => m[0]!.trim(),
  },

  /* ── PERCENT (FIN-1, audit 3.8) ──────────────────────────────────────
   *
   * `%` is the most polysemous unit in the corpus — IRR, WACC, hurdle,
   * capacity factor, availability, escalation, tax, debt share and wetland
   * coverage all wear it. It is therefore SUBJECT-KEYED (see
   * `SUBJECT_KEYED_UNITS`): a percentage whose prose does not unambiguously
   * name one governed subject is not compared against anything at all.
   * "Percentage points" (`pts`) is deliberately unmatched — a difference
   * between two rates is not a rate.
   */
  {
    re: /(\d[\d,]*\.?\d*)\s?(%|percent|pct)(?![\w])/gi,
    unit: '%',
    parse: (raw) => parsePercentPoints(raw),
    display: (m) => m[0]!.trim(),
  },

  /* ── ENERGY (FIN-1, audit 3.8) — annual generation. ─────────────────── */
  { re: /(\d[\d,]*\.?\d*)\s?(MWh|megawatt[- ]hours?)\b/gi, unit: 'MWh', parse: (raw) => parseEnergyMwh(raw, 'mwh') },
  { re: /(\d[\d,]*\.?\d*)\s?(GWh|gigawatt[- ]hours?)\b/gi, unit: 'MWh', parse: (raw) => parseEnergyMwh(raw, 'gwh') },
  { re: /(\d[\d,]*\.?\d*)\s?(TWh|terawatt[- ]hours?)\b/gi, unit: 'MWh', parse: (raw) => parseEnergyMwh(raw, 'twh') },
];

/**
 * A range LOWER bound stated immediately before the matched value ("12-24
 * months", "5 to 10 years"). Only the number adjacent to the unit is extracted
 * by `UNIT_PATTERNS`, so the opening bound has to be recovered by looking back.
 */
const RANGE_LOW_RE = /(\d[\d,]*\.?\d*)\s*(?:[-–—]|to)\s*$/;

/**
 * Tail that proves a plain-dollar token is actually the OPENING bound of a
 * rate range (`$600-$1,000/kW`, `USD 600 to USD 1,000 per kW`).
 *
 * The closing bound is deliberately parsed by the dimensioned USD/kW or
 * USD/MWh pattern above, and `RANGE_LOW_RE` restores this opening value on that
 * tagged rate. Allowing the plain-USD pattern to also emit `$600` would give
 * one surface range two incompatible dimensions and could falsely accuse an
 * absent or different project-total CAPEX fact. This guard is syntax-only: a
 * non-comparative project rate range remains a USD/kW claim and is still
 * checked (or blocked when that canonical rate is absent).
 */
const MONEY_RATE_RANGE_TAIL_RE =
  /^\s*(?:[-–—]|to)\s*(?:(?:\$|\bUSD\s)\s*)?\d[\d,]*\.?\d*\s*(?:\/\s*|\s+per\s+)(?:kW\b(?!h)|MWh\b)/i;

/**
 * A time denominator belongs to this rate token, never the whole sentence.
 * A compact unit hyphen is distinct from a spaced punctuation dash introducing
 * the construction schedule ("CAPEX $/kW — year 1").
 */
const RECURRING_CAPACITY_RATE_TAIL_RE =
  /^(?:(?:\s*\/\s*|[-–—]|\s+per\s+)(?:months?|mos?|monthly|years?|yrs?|annual(?:ly)?|annum)\b|[ \t]+(?:monthly|annually)\b(?=[ \t]*(?:[,.;)]|$)))/i;

function num(raw: string): number | null {
  const n = parseFloat(raw.replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}

/** Extract unit-tagged numbers (co-located value+unit only) with their offsets. */
function extractTaggedNumbers(text: string): TaggedNumber[] {
  const out: TaggedNumber[] = [];
  const clean = stripRefTokens(text);
  for (const { re, unit, parse, display } of UNIT_PATTERNS) {
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(clean)) !== null) {
      // Preserve the time dimension of demand tariffs and fixed O&M. These
      // recurring rates have no capital-intensity fact in the current store;
      // declining that attribution does not verify the tariff or its source.
      if (
        unit === 'USD/kW'
        && RECURRING_CAPACITY_RATE_TAIL_RE.test(clean.slice(m.index + m[0]!.length))
      ) {
        continue;
      }
      // A lower range bound inherits the unit printed on the closing bound.
      // Do not also emit it as a total-dollar amount. The dimensioned closing
      // pattern recovers it through `RANGE_LOW_RE`, or declines the entire
      // recurring rate above. Neither case turns its opening bound into USD.
      if (
        unit === 'USD'
        && MONEY_RATE_RANGE_TAIL_RE.test(clean.slice(m.index + m[0]!.length))
      ) {
        continue;
      }
      const value = parse(m[1]!, m[2]!);
      if (value === null) continue;
      // The opening bound is normalized through the SAME parser, so "12-24
      // months" yields 1.0–2.0 years rather than 12–2.
      const lowMatch = RANGE_LOW_RE.exec(clean.slice(0, m.index));
      const rangeLow = lowMatch ? parse(lowMatch[1]!, m[2]!) : null;
      out.push({
        raw: display ? display(m) : `${m[1]} ${m[2]}`,
        unit,
        value,
        index: m.index,
        ...(rangeLow !== null && rangeLow < value ? { rangeLow } : {}),
      });
    }
  }
  return out;
}

/**
 * Compare rounding in the unit the author actually stated.
 *
 * Tagged values are normalized before matching (`5 miles` → 8.0467 km), but
 * precision belongs to the source token. Comparing the raw `5` directly with
 * a canonical 8.52 km made honest whole-mile prose fail. The ratio between the
 * parsed value and raw token converts the governed value back to the stated
 * unit; it also keeps GW, hectares, months and scaled money consistent.
 */
function roundsToMentionPrecision(mention: TaggedNumber, governed: number): boolean {
  const numeric = numericPartOf(mention.raw);
  if (numeric === null) return false;
  const statedValue = num(numeric);
  if (statedValue === null) return false;
  if (statedValue === 0 || mention.value === 0) {
    return roundsToStatedPrecision(numeric, governed);
  }
  const canonicalPerStatedUnit = mention.value / statedValue;
  if (!Number.isFinite(canonicalPerStatedUnit) || canonicalPerStatedUnit === 0) return false;
  return roundsToStatedPrecision(numeric, governed / canonicalPerStatedUnit);
}

/**
 * Bind an MVA mention to the governed facility/system apparent-power demand.
 *
 * MVA is inherently polysemous in data-centre reports: the same unit describes
 * both campus service requirements and individual transformers, generators,
 * UPS blocks, or other component nameplates. Component ratings stay
 * contextual, while clear site/facility/system requirement prose must still
 * be checked against `power.apparentMva` when "apparent" is omitted.
 */
function isSystemApparentPowerMention(
  text: string,
  index: number,
  factId: string,
): boolean {
  if (factId !== 'power.apparentMva') return false;
  const { start, end } = sentenceRangeAt(text, index);
  const sentence = text.slice(start, end);
  const localIndex = Math.max(0, index - start);
  const before = sentence.slice(Math.max(0, localIndex - 100), localIndex);
  const fromValue = sentence.slice(localIndex, Math.min(sentence.length, localIndex + 120));
  const valueToken = /^\d[\d,]*\.?\d*\s*MVA\b/i.exec(fromValue)?.[0] ?? '';
  const afterValue = fromValue.slice(valueToken.length);
  const componentBefore = /\b(?:transformers?|xfmrs?|generators?|gensets?|units?|modules?|components?|equipment|ups|switchgear)(?:[-\s]+(?:nameplate|rating|capacity))?(?:\s+requirement)?(?:\s+(?:is|of|at|rated))?\s*[:=]?\s*$/i.test(before)
    || /\b(?:transformer|xfmr|generator|genset|ups|switchgear)[-\s]+capacity(?:\s+requirement)?(?:\s+(?:is|of|at))?\s*[:=]?\s*$/i.test(before);
  const componentAfter = /^\s*(?:[-\s]*(?:rated|class))?[-\s]*(?:(?:nameplate|component)\s+)?(?:power\s+)?(?:transformers?|xfmrs?|generators?|gensets?|units?|modules?|components?|equipment|ups|switchgear|rating)\b/i.test(afterValue);
  if (componentBefore || componentAfter) {
    return false;
  }
  if (/\bapparent[-\s]+power\b/i.test(sentence)) return true;
  return /\b(?:facility|site|campus|project|system)\b/i.test(sentence)
    && /\b(?:requires?|requirement|demand|load|service|needs?|needed)\b/i.test(sentence);
}

/* ── Uncertainty qualifiers (unlabeled-estimate check) ───────────────── */

/*
 * VOCABULARY (widened 2026-08-16). Every addition below was measured as a LIVE
 * false positive — prose that hedged in plain English while the gate reported it
 * as an unqualified hard fact:
 *
 *   "- Capacity MW: Up to 50 MW"                              → `up to`
 *   "…50 MW … indicated by the developer [Ref 3]"             → `indicat` *
 *   "Critical Power Deficit: 50 MW Indicated Availability"    → `indicat`
 *   "the unverified available power indication … is only 50"  → `indicat`
 *   "The developer states a plan to connect … 26 MW"          → `developer states`
 *
 * (*) the old pattern held `indicative` only, which does not match "indicated",
 * "indication" or "indicates" — three of the five live misses came from that one
 * missing suffix. `indicat` covers the whole family. `claimed` had the same
 * defect: it missed the NOUN in "The developer's claim of 26 MW … is not
 * substantiated by any formal study", about as hedged as a sentence can be.
 *
 * `open question` is here because an OPEN-QUESTION row is interrogative — a
 * figure inside "Open Question: Has a formal hosting capacity analysis been
 * performed to validate the 26 MW?" is not asserted at all, so an
 * unqualified-assertion finding against it can never be right.
 *
 * Widening a QUALIFIER vocabulary is attribution, not weakening: it does not
 * disable a check, it stops the check misreading hedged prose as assertive. The
 * remaining error is also the cheap one — `unlabeled-estimate` is NOT a truth
 * kind, so over-crediting a hedge costs a labelling nit, while under-crediting
 * one manufactures a block against an honest sentence.
 */
const QUALIFIER_RE =
  /\b(estimat|approx|approximate|roughly|about|around|circa|nominal|assum|projected|indicat|unverified|unconfirmed|preliminary|provisional|order[- ]of[- ]magnitude|ballpark|up to|anticipat|developer[- ]stated|developer[- ]declared|developer[- ]reported|developer states|user[- ](?:provided|stated|supplied|declared)|per the developer|according to|claim|unsubstantiated|not substantiated|open question|stated by|~)/i;
/** A range / tilde near the value also counts as a qualifier ("280–320 MW", "~1.25"). */
const RANGE_NEAR_RE = /[~–—-]\s*\d|±/;

/**
 * True when a qualifier governs the value at `index`.
 *
 * SCOPE (widened 2026-08-16): a flat ±60-char window is the wrong shape for the
 * SERIALIZED FIELD ROWS this gate reads. Reports render as
 * `- Title: … | Detail: … | Source: …`, and the hedge routinely sits in a sibling
 * field of the SAME row, further than 60 chars from the figure it governs — live:
 * "- Title: Massive 500 MW Grid Power Deficit | Detail: … the unverified
 * available power indication from the utility is only 50 MW", where `unverified`
 * is ~90 chars upstream and was missed.
 *
 * The enclosing sentence is the right unit: `sentenceAt` bounds on hard line
 * breaks, so one serialized row IS one sentence and the widening cannot bleed
 * into a neighbouring row or paragraph.
 *
 * RANGE_NEAR_RE stays window-local on purpose — a range elsewhere in the sentence
 * ("6-12 months … 50 MW") must not excuse an unrelated point value.
 */
function hasNearbyQualifier(text: string, index: number, window = 60): boolean {
  const start = Math.max(0, index - window);
  const slice = text.slice(start, index + window);
  if (QUALIFIER_RE.test(slice) || RANGE_NEAR_RE.test(slice)) return true;
  const sentence = sentenceAt(text, index);
  if (QUALIFIER_RE.test(sentence)) return true;
  // The three additional model-status words must govern this assertion, not
  // a contrasting or separately coordinated assertion in the same sentence.
  const bounds = sentenceRangeAt(text, index);
  const scoped = text.slice(bounds.start, bounds.end);
  const relativeIndex = index - bounds.start;
  let clauseStart = 0;
  let clauseEnd = scoped.length;
  const barriers = /[;|\r\n]|,(?!\d)|\b(?:and|but|however|while|whereas|although|yet|nevertheless|unlike|despite)\b/gi;
  for (const barrier of scoped.matchAll(barriers)) {
    if (barrier.index! < relativeIndex) clauseStart = barrier.index! + barrier[0].length;
    else { clauseEnd = barrier.index!; break; }
  }
  const clause = scoped.slice(clauseStart, clauseEnd);
  const numberIndex = relativeIndex - clauseStart;
  for (const qualifier of clause.matchAll(/\b(?:modeled|modelled|fictional)\b/gi)) {
    if (Math.abs(qualifier.index! - numberIndex) > window) continue;
    const before = clause.slice(0, qualifier.index);
    const after = clause.slice(qualifier.index! + qualifier[0].length);
    if (/\b(?:no|not|never|without|neither)\b(?:[\s-]+[a-z]+){0,3}[\s-]*$/i.test(before)) continue;
    if (/^\s+(?:(?:case|scenario|supply|capacity|headroom|service|basis|projection)\s+){0,3}(?:(?:is|are|was|were)\s+)?(?:not|never|no longer|withdrawn|rejected|superseded|obsolete)\b/i.test(after)) continue;
    return true;
  }
  return false;
}

/* ── Engine-deterministic spans (FIN-1, audit 3.8) ───────────────────── */

/**
 * Sentence shapes the FINANCIAL ENGINE writes, verbatim.
 *
 * `buildFinancialAnalysis` / `scoreSiteFinancial` / `buildFinancialActionPlan`
 * emit fixed templates into `financialAnalysis.rationale`,
 * `.bindingConstraints` and `.actionPlan.actions`, which
 * `applyFinancialAnalysisResultToReport` copies into `conclusion.topRisks`,
 * `.keyConditions`, `.nextActions` and `.btmDecisionSummary`. They are ENGINE
 * OUTPUT, not narrative: they carry no `[Ref N]` because their source is the
 * deterministic model that produced the verdict the report is about, and
 * demanding an uncertainty qualifier ("approximately") on the engine's own
 * banding statement would ask the report to hedge its own arithmetic.
 *
 * EACH ENTRY IS ANCHORED AT THE START OF THE SPAN (or, for the action plan, on
 * the machine token `parseOwnerDeadline` extracts) so it names one template
 * rather than a topic.
 */
const ENGINE_DETERMINISTIC_SPANS: readonly RegExp[] = [
  // scoreSiteFinancial — banding rationale + marginal levers.
  /^NPV is negative \(\$/i,
  /^NPV is non-negative but the verdict is held to Conditional by /i,
  /^NPV is break-even \(\$0\)/i,
  /^Positive NPV \(\$/i,
  /^(?:Project )?IRR \(/i,
  /^Payback \(/i,
  /^Monte-Carlo P\(NPV>0\) for this project is /i,
  /^For reference, the benchmark pathway comparison shows P\(NPV>0\) of /i,
  // buildFinancialAnalysis — single-basis procurement-deposit sentences.
  /^Procurement deposit \$/i,
  /^Superseded an inconsistent deposit-share that implied /i,
  // buildFinancialActionPlan — verdict headline + owner/deadline actions.
  /^Financial verdict withheld — /i,
  /^Financially Viable — proceed to financing close\./i,
  /^(?:Unfinanceable as structured|Conditional) — /i,
  /\bowner:\s*[^;]{2,40};\s*deadline:\s*[^.]{2,40}\./i,
];

/** Leading list markers / quotes a serializer puts in front of a stamp string. */
const SPAN_LEAD_IN_RE = /^[\s\-*•>"“'`]+/;

/**
 * True when `span` IS one of the engine's deterministic strings.
 *
 * SCOPE — this exempts a span from the CITATION/LABELLING class only
 * (`unlabeled-estimate`: "no uncertainty qualifier / cite its source"). It is
 * never consulted for `value-mismatch` or `invented-number`. That boundary is
 * what keeps the exemption narrow enough to be safe: prose that copies an
 * engine template but states a DIFFERENT number than the stamp still blocks on
 * the value, so nothing can be laundered by imitating the template — the most
 * an imitator can win is silence about a figure that already matches the
 * canonical fact.
 */
export function isEngineDeterministicSpan(span: string): boolean {
  const text = span.replace(SPAN_LEAD_IN_RE, '').trim();
  if (!text) return false;
  return ENGINE_DETERMINISTIC_SPANS.some((re) => re.test(text));
}

/* ── Scoped deterministic numeric facts ──────────────────────────────── */

function escapeRegexLiteral(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Markdown H3 that owns `index`, or null when the mention is not in one. */
function h3At(text: string, index: number): string | null {
  const prefix = text.slice(0, Math.max(0, index));
  const headings = [...prefix.matchAll(/^(#{1,3})\s+(.+?)\s*$/gm)];
  const nearest = headings[headings.length - 1];
  return nearest?.[1] === '###' ? nearest[2]!.trim() : null;
}

type FirmCapacityN1Metric = Extract<
  NonNullable<NumericFactIndexEntry['numericGroundingScope']>,
  { kind: 'firm-capacity-n1' }
>['metric'];

/**
 * The prose immediately governing one normalized MW token.
 *
 * `lineAt` is intentionally not used here. A stored markdown body can be one
 * physical line containing the entire report, so a distant "Shortfall" label
 * would otherwise lend the shortfall role to every MW value on that line.
 */
const MW_SENTENCE_START_RE = /(?<!\b(?:vs|v|cf|est|approx|ca)|e\.g|i\.e)[.!?](?= )|\n/gi;
const MW_SENTENCE_END_RE = /(?<!\b(?:vs|v|cf|est|approx|ca)|e\.g|i\.e)[.!?](?=\s|$)|\n/i;

function mwSentenceRangeAt(text: string, index: number, maxLen = 400): { start: number; end: number } {
  const from = Math.max(0, index - maxLen);
  const before = text.slice(from, index);
  const after = text.slice(index, index + maxLen);
  const beforeTerms = [...before.matchAll(MW_SENTENCE_START_RE)];
  const lastTerm = beforeTerms.at(-1);
  const start = lastTerm?.index !== undefined ? from + lastTerm.index + 1 : from;
  const endMatch = MW_SENTENCE_END_RE.exec(after);
  const end = endMatch?.index !== undefined
    ? index + endMatch.index + 1
    : Math.min(text.length, index + maxLen);
  return { start, end };
}

function scopedMwMentionContext(text: string, index: number): {
  sentence: string;
  before: string;
  afterValue: string;
} {
  const { start, end } = mwSentenceRangeAt(text, index);
  const sentence = text.slice(start, end);
  const localIndex = Math.max(0, index - start);
  const before = sentence.slice(Math.max(0, localIndex - 96), localIndex);
  const fromValue = sentence.slice(localIndex, Math.min(sentence.length, localIndex + 120));
  const valueToken = /^\d[\d,]*\.?\d*\s*(?:MW|megawatts?)\b/i.exec(fromValue)?.[0] ?? '';
  return {
    sentence,
    before,
    afterValue: fromValue.slice(valueToken.length),
  };
}

function isFirmCapacityN1Context(
  text: string,
  index: number,
  metric: FirmCapacityN1Metric,
): boolean {
  const { sentence, before, afterValue } = scopedMwMentionContext(text, index);
  // N-0 and N-1 can be contrasted in one sentence. An explicit N-0 on this
  // quantity's assertion must not inherit the later/earlier N-1 clause.
  const assertionBarrier = /,(?!\d)|[;|()]|\b(?:but|however|while|whereas|although|though|unlike|despite)\b/i;
  const clauseBarrier = /[;|()]|,\s*(?:but|however|while|whereas|although|though|unlike|despite|and\s+subject\s+to)\b/i;
  const otherMwMention = /\d[\d,]*(?:\.\d+)?\s*(?:MW|megawatts?)\b/i;
  const beforeAssertion = (before.split(assertionBarrier).at(-1) ?? '').split(otherMwMention).at(-1) ?? '';
  const afterAssertion = (afterValue.split(assertionBarrier)[0] ?? '').split(otherMwMention)[0] ?? '';
  const beforeClause = before.split(clauseBarrier).at(-1) ?? before;
  const beforeCriterion = [...beforeAssertion.matchAll(/\bn[- ]?([01])\b/gi)].at(-1)?.[1]
    ?? [...beforeClause.matchAll(/\bn[- ]?([01])\b/gi)].at(-1)?.[1];
  const afterCriterion = /\bn[- ]?([01])\b/i.exec(afterAssertion)?.[1];
  // A directly attached criterion qualifies this value rather than a prior
  // heading; e.g. 'N-0 ... 46.6 MW under N-1' cannot use N-0 credit.
  const attachedCriterion = /^\s*(?:under|as)\s+(?:the\s+)?(?:quoted\s+)?["“‘']?n[- ]?([01])\b/i.exec(afterAssertion)?.[1];
  if ((attachedCriterion ?? beforeCriterion ?? afterCriterion) === '0') return false;
  const inStructuredN1 = /^firm\s+capacity\s+n[- ]?1$/i.test(h3At(text, index) ?? '');
  const afterClause = afterValue.split(clauseBarrier)[0] ?? afterValue;
  const n1ScopeText = clauseBarrier.test(sentence) ? `${beforeClause} ${afterClause}` : sentence;
  const hasN1Basis = inStructuredN1
    || /\bn[- ]?1\b/i.test(n1ScopeText)
    || (/\b(?:loss|lost|outage|contingency)\b/i.test(n1ScopeText)
      && /\b(?:remaining|required|requirement|shortfall)\b/i.test(n1ScopeText))
    || (/\bremaining\b/i.test(n1ScopeText)
      && /\b(?:required|requirement|shortfall)\b/i.test(n1ScopeText));
  if (!hasN1Basis) return false;

  // Bind the metric phrase to THIS number. This is intentionally stricter than
  // sentence membership: an N-1 sentence commonly contains nameplate,
  // remaining-firm, required-firm and shortfall MW side by side.
  switch (metric) {
    case 'firm-deliverable-n1':
      return /(?:\bfirm[-\s]?deliverable(?:\s+(?:under\s+)?["“‘\']?n[- ]?1["”’\']?)?(?:\s+(?:capacity|mw))?|\bremaining\s+firm\s+capacity)(?:\s+(?:is|of|equals?))?\s*[:=]?\s*$/i.test(before)
        || /^\s*(?:of\s+)?firm[-\s]?deliverable(?:\s+(?:capacity|grid\s+capacity))?\b/i.test(afterValue);
    case 'largest-contingency':
      return /\blargest\s+(?:unit|contingency)(?:\s+mw)?\s*[:=]?\s*$/i.test(before)
        || (/\b(?:loss|lost|outage)\b/i.test(sentence)
          && /\bcontributes?\s*$/i.test(before)
          && /^\s*(?:of\s+)?firm\s+capacity\b/i.test(afterValue))
        || (/\bloss\b[^.!?]{0,80}\blargest\s+(?:generation\s+)?block\b/i.test(sentence)
          && /^\s*firm\s+contribution\b/i.test(afterValue));
    case 'required-firm-n1':
      return /\brequired\s+firm(?:\s+(?:capacity|mw))?\s*[:=]?\s*$/i.test(before)
        || (/\brequired\s*$/i.test(before)
          && /^\s*(?:of\s+)?(?:firm(?:\s+(?:capacity|load|requirement))?|reserve[- ]padded\s+(?:firm\s+)?requirement)\b/i.test(afterValue))
        || /^\s*reserve[- ]padded\s+(?:firm\s+)?requirement\b/i.test(afterValue)
        || /^\s*required\s+firm\b/i.test(afterValue);
    case 'shortfall-n1':
      return /\bshortfall(?:\s+(?:of|is|equals?))?\s*[:=]?\s*$/i.test(before)
        || /^\s*(?:of\s+)?(?:firm[-\s]+capacity[-\s]+|firm[-\s]+)?shortfall\b/i.test(afterValue);
    default:
      return false;
  }
}

/**
 * Whether this number itself is asserted as the authored firm-capacity target.
 *
 * The generic absent-fact keyword scan is intentionally broad, but `firm` is
 * unusually overloaded: N-1 arithmetic, transformer/source ratings and the
 * required reserve margin all use it without creating a project target. Bind
 * `firm` directly to the number so those neighbouring roles cannot fabricate
 * a `firmCapacity.targetMw` assertion. The fail-closed golden shape — "a firm
 * 49 MW" — and an explicit "firm capacity target is 49 MW" remain assertions.
 */
function assertsFirmCapacityTargetAt(text: string, index: number): boolean {
  const { before, afterValue } = scopedMwMentionContext(text, index);
  const beforeMatch = /(?:\bfirm|\b(?:firm\s+capacity\s+target|target\s+firm\s+capacity)(?:\s+(?:is|of|at))?\s*[:=]?)\s*$/i.exec(before);
  const assertedBefore = beforeMatch != null
    && !KEYWORD_NEGATION_RE.test(before.slice(Math.max(0, beforeMatch.index - 28), beforeMatch.index));
  const assertedAfter = /^\s*(?:(?:firm\s+capacity\s+target|target\s+firm\s+capacity)\b|of\s+firm\s+accredited\s+capacity\b)/i
    .test(afterValue);
  return assertedBefore || assertedAfter;
}

/**
 * Whether a numeric fact whose value belongs to a deterministic phase-
 * feasibility calculation may compete for this mention.
 *
 * The scope is deliberately semantic, not a blanket table exemption: the
 * line must identify the same phase AND the matching metric.  This lets the
 * generated `capacityCreditDeepDive.perPhase[]` rows use the platform's own
 * arithmetic while keeping a generic claim such as "a firm 49 MW of
 * accredited capacity" governed by the (possibly absent) authored
 * `firmCapacity.targetMw` fact.
 */
/** A serialized phase row may contain multiline water/staffing prose before
 * its trailing Phase cell. Do not lose that row identity at a prose newline. */
function phaseRecordAt(text: string, index: number): string {
  const starts = [...text.slice(0, index + 1).matchAll(/(?:^|\n)-\s+(?:Capacity MW|Description|Phase|Title):\s*/g)];
  const start = starts.at(-1)?.index;
  if (start == null) return lineAt(text, index);
  const next = /\n(?:#{1,4}\s|[-*]\s+[A-Z][A-Za-z ]{0,35}:\s)/g;
  next.lastIndex = start + starts.at(-1)![0].length;
  const end = next.exec(text)?.index ?? text.length;
  if (index >= end || end - start > 40_000) return lineAt(text, index);
  const record = text.slice(start, end);
  return isSerializedRecord(record) && /(?:^|\|)\s*Phase:\s*Phase\s*\d+\b/i.test(record)
    ? record : lineAt(text, index);
}

/** The owner phase whose serialized row (`… | Phase: Phase N | …`) contains
 * `index`, or undefined outside such a row. Row identity only — a phase named
 * in ordinary prose is not a row. */
function serializedPhaseRowAt(text: string, index: number): number | undefined {
  const line = phaseRecordAt(text, index);
  if (!isSerializedRecord(line)) return undefined;
  const phase = /(?:^|\|)\s*(?:-\s*)?Phase:\s*Phase\s*(\d+)\b/i.exec(line)?.[1];
  return phase == null ? undefined : Number(phase);
}

function isZeroGridPlanningCredit(text: string, index: number, length: number): boolean {
  const prefix = text.slice(Math.max(0, index - 250), index).split(/[.!?;\n|]/).at(-1) ?? '';
  const suffix = text.slice(index + length, index + length + 180).split(/[.!?;\n|]/)[0] ?? '';
  return /\b(?:project|screening|plan)\s+should\s+conservatively\s+assume\s*$/i.test(prefix)
    && /^\s+of\s+firm\s+(?:grid\s+)?capacity\s+is\s+available\s+from\s+the\s+grid(?:\s+for\s+(?:initial\s+)?planning)?\s*(?:\[Ref[^\]]+\]\s*)*$/i.test(suffix.trimEnd());
}

/**
 * grounding-validator-v3 — the NOUN form of a phase addition, directly
 * adjacent to the figure: "addition <N> MW" / "<N> MW (IT-load|load|demand)
 * addition". The owner's own phase notes read "Working IT-demand addition
 * 500 MW; cumulative campus IT-load requirement 750 MW", and production held
 * the restated addition as a cumulative mismatch (Holly Ridge, e87). "In
 * addition, the campus reaches 750 MW" is not the noun cue (not adjacent).
 *
 * Production's (cumulative) reading stays when:
 *  - the figure is measured against a NAMED baseline phase ("600 MW load
 *    addition relative to Phase 1"): the owner addition is relative to the
 *    previous phase;
 *  - the addition is to the utility's system, the planning sense of a large
 *    load addition, where the figure is what the utility must serve by then
 *    ("By Phase 3 the project represents a 1,750 MW load addition to the
 *    Entergy system", "… for the utility to plan around");
 *  - the figure is the declared campus total, which no phase of a multi-phase
 *    plan adds on its own ("Phase 3 completes the campus's 1,750 MW load
 *    addition").
 */
function isOwnerPhaseAdditionNoun(
  text: string,
  index: number,
  localBefore: string,
  localAfter: string,
  campusTotalMw: number | undefined,
): boolean {
  const noun = /\baddition\s*[:=]?\s*$/i.test(localBefore)
    || /^\s*(?:(?:IT|facility)[- ](?:load|demand)\s+|load\s+|demand\s+)?addition\b/i.test(localAfter);
  if (!noun) return false;
  if (/\b(?:relative\s+to|vs\.?|versus|over|compared\s+(?:to|with)|above|beyond)\s+(?:the\s+)?phase\s*\d+/i.test(localAfter)) return false;
  if (/^\s*(?:(?:IT|facility)[- ](?:load|demand)\s+|load\s+|demand\s+)?addition\s+(?:to|for|on|onto|in|within|at)\s+(?:(?:the|its|their|a)\s+)?(?:[\w&.'\u2019-]+\s+){0,3}?(?:utility|utilities|grid|system|network|transmission|interconnection|ISO|RTO|balancing\s+authority|(?:electric\s+)?co-?op(?:erative)?|service\s+territory)\b/i.test(localAfter)) return false;
  const figure = Number(/^\d[\d,]*(?:\.\d+)?/.exec(text.slice(index))?.[0]?.replace(/,/g, ''));
  if (campusTotalMw != null && Number.isFinite(figure) && Math.abs(figure - campusTotalMw) <= 0.5) return false;
  return true;
}

const UNCONFIRMED_POWER_BASIS_DISCLAIMER_PATTERNS: readonly RegExp[] = [
  /(?:,?\s*(?:with|including)\s+(?:an?\s+)?(?:explicit\s+)?(?:note|caveat|qualification|disclaimer)\s+that\s+)?\bwhether\b[^.!?\n]{0,160}?\b(?:IT|facility)\b[^.!?\n]{0,120}?\b(?:unconfirmed|unspecified|undetermined|unknown|ambiguous|unclear|unverified|not\s+(?:yet\s+)?(?:confirmed|specified|determined|established|defined|stated|verified|known))\b/gi,
  /\b(?:unconfirmed|unspecified|undetermined|unknown|ambiguous|unclear|not\s+(?:yet\s+)?(?:confirmed|specified|determined|established|defined|stated|verified|known))\s+(?:whether\b[^.!?\n]{0,160}?\b(?:IT|facility)\b[^.!?\n]{0,100}|(?:(?:net\s+)?IT\s*(?:\/|or|vs\.?|versus|and)\s*(?:gross\s+)?facility|(?:gross\s+)?facility\s*(?:\/|or|vs\.?|versus|and)\s*(?:net\s+)?IT)(?:\s+(?:load|power|capacity|demand|basis|split|breakdown))*)/gi,
  /\b(?:(?:net\s+)?IT\s*(?:\/|or|vs\.?|versus|and)\s*(?:gross\s+)?facility|(?:gross\s+)?facility\s*(?:\/|or|vs\.?|versus|and)\s*(?:net\s+)?IT)(?:\s+(?:load|power|capacity|demand|basis|split|breakdown))*\s+(?:is|are|remains?|was|were)?\s*(?:unconfirmed|unspecified|undetermined|unknown|ambiguous|unclear|not\s+(?:yet\s+)?(?:confirmed|specified|determined|established|defined|stated|verified|known))\b/gi,
];

function maskUnconfirmedPowerBasisDisclaimer(text: string): string {
  let masked = text;
  for (const pattern of UNCONFIRMED_POWER_BASIS_DISCLAIMER_PATTERNS) {
    masked = masked.replace(pattern, (match) => ' '.repeat(match.length));
  }
  return masked;
}

function hasUnconfirmedPowerBasisDisclaimer(text: string): boolean {
  return UNCONFIRMED_POWER_BASIS_DISCLAIMER_PATTERNS.some((pattern) => {
    pattern.lastIndex = 0;
    return pattern.test(text);
  });
}

function numericGroundingScopeMatches(
  fact: NumericFactIndexEntry,
  text: string,
  index: number,
  numericSourceEvidence?: NumericSourceEvidence,
  /** The declared campus MW (`power.declaredMw`, point value), for the phase addition noun cue. */
  campusTotalMw?: number,
): boolean {
  const scope = fact.numericGroundingScope;
  if (!scope) return true;
  if (scope.kind === 'firm-capacity-n1') {
    return isFirmCapacityN1Context(text, index, scope.metric);
  }
  if (scope.kind === 'screening-load') {
    const { sentence, before, afterValue } = scopedMwMentionContext(text, index);
    if (!/\b(?:modeled|modelled|screening|conditional|assumed|assuming|sensitivity)\b/i.test(sentence)) return false;
    if (/\b(?:committed|secured|guaranteed|approved|firm|commissioned|delivered|operating|operational|BESS|battery|turbine|nameplate)\b/i.test(sentence)) return false;
    const record = phaseRecordAt(text, index);
    const phase = /\bphase\s*(\d+)\b/i.exec(sentence)?.[1]
      ?? /(?:^|\|)\s*Phase:\s*Phase\s*(\d+)\b/i.exec(record)?.[1];
    if (scope.phaseSequence != null ? Number(phase) !== scope.phaseSequence : phase != null) return false;
    const metricBefore = before.replace(/\d[\d,]*(?:\.\d+)?\s*[–—-]\s*$/, '');
    const metric = /\bIT[- ]load(?:\s+(?:is|of|equals?))?\s*[:=]?\s*$/i.test(metricBefore)
      || /^\s*(?:of\s+)?IT[- ]load\b/i.test(afterValue) ? 'it'
      : /\bfacility[- ](?:load|demand|heat)(?:\s+(?:is|of|equals?))?\s*[:=]?\s*$/i.test(metricBefore)
        || /^\s*(?:of\s+)?facility[- ](?:load|demand|heat)\b/i.test(afterValue) ? 'facility' : null;
    if (metric !== scope.metric) return false;
    const localInputCase = /\bIT[- ]load(?:[- ]screening)?\s+case(?:\s+(?:is|of|equals?))?\s*[:=]?\s*$/i.test(metricBefore)
      || /^\s*(?:of\s+)?IT[- ]load(?:[- ]screening)?\s+case\b/i.test(afterValue) ? 'it'
      : /\bfacility[- ](?:demand|load)(?:[- ]screening)?\s+case(?:\s+(?:is|of|equals?))?\s*[:=]?\s*$/i.test(metricBefore)
        || /^\s*(?:of\s+)?facility[- ](?:demand|load)(?:[- ]screening)?\s+case\b/i.test(afterValue) ? 'facility' : null;
    const inputCase = localInputCase
      ?? (/\bIT[- ]load(?:[- ]screening)?\s+case\b/i.test(sentence) ? 'it'
        : /\bfacility[- ](?:demand|load)(?:[- ]screening)?\s+case\b/i.test(sentence) ? 'facility' : null);
    // The deterministic disclosure names both sides of the conversion, rather
    // than repeating "IT-load case" in its modeled-basis sentence. Require
    // its explicit PUE and utilization context; ordinary facility assertions
    // still cannot borrow a conditional endpoint.
    const disclosedBasis = /^Modeled basis: PUE\b/i.test(sentence.trim())
      && /\bannual utilization\b/i.test(sentence)
      && /\bIT load\b/i.test(sentence) && /\bfacility heat\b/i.test(sentence);
    const recordStart = text.indexOf(record, Math.max(0, index - record.length));
    const recordIndex = index - recordStart;
    const waterStart = record.lastIndexOf('\n\nModeled phase water screening\n', recordIndex);
    const waterEnd = record.indexOf('\nThese cumulative phase cases and alternative cooling cases are not additive.', waterStart);
    const phaseDisclosure = waterStart >= 0 && recordIndex > waterStart && waterEnd > recordIndex;
    const header = phaseDisclosure
      ? [...record.slice(waterStart, recordIndex + 80).matchAll(/(?:^|\n)Modeled\s+[\d,]+(?:\.\d+)?(?:[–-][\d,]+(?:\.\d+)?)?\s+MW\s+(IT|facility)\s+load\./gi)].at(-1)?.[1]?.toLowerCase()
      : undefined;
    return inputCase === scope.inputBasis || (header === scope.inputBasis)
      || (disclosedBasis && phase == null && scope.phaseSequence == null);
  }
  if (scope.kind === 'phase-capacity') {
    const { sentence, before, afterValue } = scopedMwMentionContext(text, index);
    const line = phaseRecordAt(text, index);
    // Canonical JSON sorts keys: the phase field can follow the capacity and
    // several description sentences. Use its row identity for capacity cells;
    // a separate cell can explicitly refer to another owner phase.
    const rowPhase = isSerializedRecord(line)
      ? /(?:^|\|)\s*(?:-\s*)?Phase:\s*Phase\s*(\d+)\b/i.exec(line)?.[1]
      : undefined;
    const localBefore = before.split('|').at(-1) ?? before;
    const localAfter = afterValue.split('|')[0]!.split(/\b\d[\d,]*(?:\.\d+)?\s*MW\b/i)[0]!;
    const beforeSinceLastMw = localBefore
      .split(/\b\d[\d,]*(?:\.\d+)?\s*MW\b(?:\s+(?:cumulative|incremental|additional|total|facility|IT|campus|load|demand|capacity|\s)*(?:in|for|at)?\s*\(?phase\s*\d+\)?)?/i)
      .at(-1) ?? localBefore;
    const phaseBefore = /\bphase\s*(\d+)\b[^\d.;,]{0,75}$/i.exec(beforeSinceLastMw)?.[1];
    const phaseAfter = /^\s*(?:\(\s*)?phase\s*(\d+)\b/i.exec(localAfter)?.[1];
    const ordinals = ['first', 'second', 'third', 'fourth', 'fifth'];
    const ordinal = /\b(initial|first|second|third|fourth|fifth)\s+(?:(?:(?:construction|development|build[- ]out)\s+)?phase\b[^.;]{0,65}|$)/i.exec(before)?.[1]?.toLowerCase();
    const ordinalAfter = /^\s*(initial|first|second|third|fourth|fifth)\s+phase\b/i.exec(afterValue)?.[1]?.toLowerCase();
    const ordinalLabel = ordinal ?? ordinalAfter;
    const ordinalPhase = ordinalLabel === 'initial' ? 1 : ordinalLabel ? ordinals.indexOf(ordinalLabel) + 1 : undefined;
    // An explicitly initial sequence names consecutive owner phases. Bind by
    // list position, never by finding a matching value somewhere in the store.
    const phaseList = /\b(?:initial|first)\s+((?:\d[\d,]*(?:\.\d+)?\s*MW\s*(?:(?:,\s*)?(?:and\s+)?))+?)\s+phases\b/i.exec(sentence);
    const numberInSentence = index - sentenceRangeAt(text, index).start;
    const listPhase = phaseList && [...phaseList[0].matchAll(/\d[\d,]*(?:\.\d+)?\s*MW\b/gi)]
      .findIndex(token => phaseList.index + token.index! === numberInSentence);
    const listedPhase = listPhase != null && listPhase >= 0 ? listPhase + 1 : undefined;
    const claimPhase = phaseClaimSequenceAt(text, index, numericSourceEvidence);
    const namedPhase = phaseAfter ?? phaseBefore ?? rowPhase ?? listedPhase ?? ordinalPhase ?? claimPhase;
    const target = phaseTargetAt(text, index, numericSourceEvidence, namedPhase == null ? undefined : Number(namedPhase));
    if (target && (target.phaseSequence == null || (namedPhase != null && Number(namedPhase) !== target.phaseSequence))) return false;
    if (Number(namedPhase ?? target?.phaseSequence) !== scope.phaseSequence) return false;
    // An explicit phase in this cell takes precedence over the row label.
    // Capacity cells still inherit their own row identity, never another cell.
    const nearby = maskUnconfirmedPowerBasisDisclaimer(`${localBefore.slice(-90)} ${localAfter.split(/,\s*(?:while|whereas)\b/i)[0]!.slice(0,90)}`);
    // A planned phase number does not establish delivered power, an increment,
    // equipment nameplate, or a different electrical load boundary.
    if (/\b(?:committed|secured|guaranteed|approved|firm|margin|deficits?|shortfalls?|surplus|commissioned|delivered|operating|operational)\b/i.test(nearby)) return false;
    if (/^(?:\s*\([^)]*\))?(?:\s*,?\s*(?:and|or)?\s*\d[\d,]*(?:\.\d+)?\s*MW\b(?:\s*\([^)]*\))?)*\s+(?:[\w-]+\s+){0,2}(?:deficits?|shortfalls?|margins?|surplus|gaps?)\b/i.test(afterValue)) return false;
    const equipmentServesLoad = /\b(?:(?:serve|support|meet|supply)\s+(?:(?:a|its|the)\s+)?(?:cumulative\s+)?|as\s+a\s+proposed\s+supply\s+option\s+for\s+the\s+)$/i.test(localBefore)
      && /^\s*(?:(?:IT|facility|campus)[- ])?(?:load|demand)\b/i.test(localAfter);
    const proposedPlantForPhase = phaseAfter != null && /\b(?:proposals?|proposed|planned)\b/i.test(localBefore)
      && /\bplant\s+for\s+(?:its|the|our)\s*$/i.test(localBefore)
      && /^\s*phase\s*\d+\s*(?:,|[.;]|$)/i.test(localAfter);
    if ((/\b(?:BESS|battery|turbine|nameplate|diesel|generators?|gensets?|standby)\b/i.test(nearby) || /^\s*(?:of\s+)?grid[- ]import\b/i.test(localAfter)) && !equipmentServesLoad && !proposedPlantForPhase) return false;
    if (/^\s*(?:(?:of\s+)?(?:proposed\s+)?on[- ]site\s+gas\s+turbines?\b|(?:\d+(?:\.\d+)?\s*kV\s+)?(?:utility\s+)?(?:feeds?|feeders?)\b|(?:(?:site[- ]deliverable|service|utility|contracted)\s+)?allocation\b|ceiling\b|DC\b|UPS\b|PBR\b|Permit\s+by\s+Rule\b|Standard\s+Permit\b|(?:air[- ]permit|interconnection|ERCOT|FIS|screening)\s+(?:application|stud(?:y|ies)|filing|request)\b|stud(?:y|ies)\b)/i.test(localAfter)
      || /\bload[- ]service\s+agreement(?:\s+for)?(?:\s+a)?\s*$/i.test(localBefore)) return false;
    const isAddition = (rowPhase != null && /^\s*Title:\s*Phase\s*\d+\s*:\s*\+\s*$/i.test(localBefore))
      || /\b(?:adds?|adding|additional|addition\s+of|increment(?:al)?|added)\b[^.;()]{0,35}$/i.test(localBefore)
      || /^\s*(?:of\s+)?(?:additional|incremental)\b/i.test(localAfter)
      || isOwnerPhaseAdditionNoun(text, index, localBefore, localAfter, campusTotalMw)
      // A named phase row's '500 MW Expansion' is its addition;
      // 'Expansion to 750 MW' still names the cumulative milestone.
      || (rowPhase != null && /^\s*(?:phase\s*\d+\s+)?(?:final\s+)?expansion\b/i.test(localAfter));
    if ((scope.metric === 'addition') !== isAddition) return false;
    if (/\bIT[- ](?:load|demand|power)\b/i.test(nearby) && scope.powerBasis !== 'it-load') return false;
    if (/\bfacility[- ](?:load|demand|power)\b/i.test(nearby) && scope.powerBasis !== 'facility') return false;
    return true;
  }
  if (scope.kind !== 'phase-feasibility') return false;

  const line = lineAt(text, index);
  // Ordinary prose paragraphs can discuss several phases in separate
  // sentences on one physical line. Scope the number to its containing
  // sentence so an "ultimate 550 MW" sentence does not inherit "Phase 2"
  // from an earlier sentence. Flattened object rows deliberately keep their
  // full-line context because phase label and metric are separate pipe fields.
  const serializedRecord = isSerializedRecord(line);
  const sentenceBounds = mwSentenceRangeAt(text, index, 1000);
  const sentence = text.slice(sentenceBounds.start, sentenceBounds.end).trim();
  const rawContext = serializedRecord
    ? line.trim()
    : (sentence || line.trim());
  const comparativeBaselinePhaseRe =
    /\b(?:over|relative\s+to|vs\.?|versus|compared\s+(?:to|with)|above|beyond|from|after)\s+(?:the\s+)?phase\s*\d+\b/gi;
  const context = serializedRecord
    ? rawContext
    : rawContext.replace(comparativeBaselinePhaseRe, (m) => ' '.repeat(m.length));
  const metricContext = scopedMwMentionContext(text, index);
  const phaseNumber = escapeRegexLiteral(String(scope.phaseSequence));
  const phaseBySequence = new RegExp(
    `\\bphase(?:\\s+label\\s*:\\s*(?:phase\\s+)?)?\\s*${phaseNumber}\\b`,
    'i',
  ).test(context);
  const normalizedLabel = scope.phaseLabel.trim().toLowerCase();
  const phaseByLabel = normalizedLabel.length >= 3
    && context.toLowerCase().includes(normalizedLabel);
  if (!phaseBySequence && !phaseByLabel) return false;

  if (!serializedRecord) {
    const maskedSlice = text
      .slice(sentenceBounds.start, sentenceBounds.end)
      .replace(comparativeBaselinePhaseRe, (m) => ' '.repeat(m.length));
    const phaseMentions = [
      ...maskedSlice.matchAll(/\bphase(?:\s+label\s*:\s*(?:phase\s+)?)?\s*(\d+)\b/gi),
    ];
    const distinctPhases = new Set(phaseMentions.map((m) => Number(m[1])));
    if (distinctPhases.size > 1) {
      const relIndex = Math.max(0, index - sentenceBounds.start);
      const maskedAfter = metricContext.afterValue.replace(comparativeBaselinePhaseRe, ' ');
      const attachedAfterPhase = /^\s*(?:for|in|of|at|during|under)\s+phase\s*(\d+)\b/i.exec(
        maskedAfter,
      )?.[1];
      let governingPhase: number | undefined =
        attachedAfterPhase != null ? Number(attachedAfterPhase) : undefined;
      if (governingPhase == null) {
        const beforeMentions = phaseMentions.filter((m) => (m.index ?? 0) <= relIndex);
        const chosen = beforeMentions.at(-1) ?? phaseMentions[0];
        if (chosen) governingPhase = Number(chosen[1]);
      }
      if (governingPhase != null && governingPhase !== scope.phaseSequence) {
        return false;
      }
    }
  }

  switch (scope.metric) {
    case 'facility-load':
      if (
        /\b(?:adds?|adding|additional|addition\s+of|increment(?:al)?|added)\b[^.;()]{0,35}$/i.test(
          metricContext.before,
        ) ||
        /^\s*(?:of\s+)?(?:additional|incremental)\b/i.test(metricContext.afterValue)
      ) {
        return false;
      }
      return /\bfacility[-\s]+load(?:\s+(?:mw|to\s+serve))?(?:\s+(?:is|of|equals?))?\s*[:=]?\s*$/i
        .test(metricContext.before)
        || /^\s*(?:of\s+)?facility[-\s]+load\b/i.test(metricContext.afterValue);
    case 'required-firm':
      return /\brequired\s+firm(?:\s+(?:mw|capacity))?\s*[:=]?\s*$/i
        .test(metricContext.before)
        || /^\s*(?:of\s+)?required\s+firm\b/i.test(metricContext.afterValue);
    case 'firm-deliverable':
      // Phase-feasibility credit is N-0, so it cannot validate an explicitly
      // N-1 value merely because the same paragraph names the phase.
      if (isFirmCapacityN1Context(text, index, 'firm-deliverable-n1')) return false;
      if (/^\s*(?:of\s+)?firm[-\s]?deliverable\s+grid\b/i.test(metricContext.afterValue)) return false;
      return /\bfirm[-\s]?deliverable(?:\s+(?:mw|capacity))?(?:\s+(?:is|of|equals?))?\s*[:=]?\s*$/i
        .test(metricContext.before)
        || /^\s*(?:of\s+)?firm[-\s]?deliverable\b/i.test(metricContext.afterValue);
    case 'firm-margin':
      return /\b(?:firm(?:-capacity)?\s+|planning-default(?:\s+firm)?\s+)?margin(?:\s+(?:mw|is|of|equals?))?\s*[:=]?\s*$/i
        .test(metricContext.before)
        || /^\s*(?:of\s+)?(?:(?:firm(?:-capacity)?|planning-default(?:\s+firm)?)\s+)?margin\b/i.test(metricContext.afterValue);
    case 'source-capacity':
    case 'source-firm': {
      const componentLabel = scope.componentLabel?.trim();
      if (!componentLabel) return false;
      const sourceBefore = metricContext.before.split(/[;,(]|\b(?:alongside|plus|whereas|while|vs\.?|versus)\b/i).at(-1) ?? metricContext.before;
      const sourceAfter = metricContext.afterValue.split(/[;,()]|\b(?:alongside|plus|with|and|or|whereas|while|vs\.?|versus)\b/i)[0] ?? metricContext.afterValue;
      const namesComponent = new RegExp(`\\b${escapeRegexLiteral(componentLabel)}\\b`, 'i')
        .test(`${sourceBefore} ${sourceAfter}`);
      if (!namesComponent) return false;
      return scope.metric === 'source-capacity'
        ? /^\s*@\s*ELCC\b|^\s*(?:nameplate\s+)?capacity\b/i.test(metricContext.afterValue)
        : /^\s*(?:of\s+)?firm(?:\s+(?:capacity|contribution))?\b/i.test(metricContext.afterValue);
    }
    default:
      return false;
  }
}

/* ── Entity matching ─────────────────────────────────────────────────── */

/** Loose normalize for entity comparison: collapse whitespace, lower-case, drop punctuation. */
function entityKey(s: string): string {
  return s
    .replace(/[.,;:()"'`]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/**
 * True when the narrative "mentions" the canonical entity — its normalized
 * key appears as a whole-token-bounded substring of the normalized narrative.
 * Used to decide the entity fact is being asserted at all before policing it.
 */
function narrativeMentions(narrativeKey: string, entityKeyToFind: string): boolean {
  if (!entityKeyToFind) return false;
  return narrativeKey.includes(entityKeyToFind);
}

/* ── The numeric gate ────────────────────────────────────────────────── */

/** ISO/RTO market operators a narrative might assert from geography priors. */
const ISO_RTO_LEXICON = ['MISO', 'PJM', 'ERCOT', 'SPP', 'CAISO', 'NYISO', 'ISO-NE', 'AESO', 'IESO'];

/** Window qualifiers that make an ISO mention conditional rather than asserted. */
const ISO_CONDITIONAL_RE =
  /\b(not yet|unconfirmed|to be (?:confirmed|determined)|if\b.{0,40}(?:confirmed|selected|established)|candidate|likely|presum|expected|anticipated|pending confirmation|were?\s+it\b)\b/i;

/**
 * Absent-ISO assertion check — when the canonical `isoRegion` fact is ABSENT,
 * any unqualified ISO/RTO name asserted in the narrative is a blocking
 * `invented-entity` violation (one per ISO name per checked text, so a
 * repeated "MISO" never storms). A mention inside a conditional/qualified
 * window ("if MISO is confirmed as the ISO…") is allowed.
 */
function checkAbsentIsoAssertions(
  narrativeText: string,
  store: CanonicalFactStore,
  sectionId: string | undefined,
): GroundingViolation[] {
  const fact = store.facts.get('isoRegion');
  if (!fact || fact.status !== 'absent') return [];
  const clean = stripRefTokens(narrativeText);
  const out: GroundingViolation[] = [];
  for (const name of ISO_RTO_LEXICON) {
    const re = new RegExp(`\\b${name.replace('-', '[-\\s]?')}\\b`, 'g');
    let m: RegExpExecArray | null;
    while ((m = re.exec(clean)) !== null) {
      const window = clean.slice(Math.max(0, m.index - 120), m.index + name.length + 120);
      if (QUALIFIER_RE.test(window) || ISO_CONDITIONAL_RE.test(window)) continue;
      out.push({
        kind: 'invented-entity',
        severity: 'block',
        message:
          `Narrative asserts "${name}" as this project's ISO/RTO, but the canonical isoRegion fact is `
          + 'ABSENT (not established). Do not name an ISO/RTO — write "the applicable ISO/RTO (not yet '
          + 'confirmed)" and describe queue/study steps generically.',
        observed: name,
        factId: 'isoRegion',
        ...(sectionId ? { sectionId } : {}),
      });
      break;
    }
  }
  return out;
}

/** A generic interconnection cost bracket is not the declared campus load.
 * Require threshold grammar attached to this token and generic cost/load
 * framing. A cited or benchmark-adjacent assertion about THIS site's load
 * remains governed; this does not validate the associated cost estimate. */
function isGenericInterconnectionCostThreshold(text: string, index: number): boolean {
  const prefix = text.slice(Math.max(0, index - 140), index);
  const costBound = /(?:\bcosts?\s+(?:(?:of\s+)?(?:transmission|interconnection|substation)\s+upgrades?\s+)?(?:(?:of\s+)?\$[\d.,\s–—-]+(?:million|m|billion|b)?\s+)?|\b(?:transmission|interconnection|substation)\s+upgrades?\s+)for\s+(?:loads?\s+)?(?:exceeding|above|over|greater\s+than|more\s+than|[>≥])\s*$/i.test(prefix);
  if (!costBound) return false;
  const context = sentenceAt(text, index);
  if (/\b(?:this|the|our|proposed|planned)\s+(?:site|project|campus|facility|development)(?:['’]s)?\s+(?:load|capacity|demand|requirement)|\bdeclared\s+(?:load|capacity|demand|requirement)/i.test(context)) return false;
  return /\b(?:interconnection|transmission|substation|upgrades?)\b/i.test(context)
    && (/\b(?:costs?|price|pricing|expense|average)\b|\$\s*\d/i.test(context));
}

/**
 * A duration framed as a generator-cohort queue benchmark that reached the
 * absent-queue accusation. It already failed the source-verified benchmark
 * path (`isSupportedSourceRoleNumber`), so it KEEPS BLOCKING — this changes
 * only what the violation says: the benchmark is unverified, not that this
 * site's queue duration was invented.
 */
function isUnverifiedQueueBenchmark(t: TaggedNumber, clean: string): boolean {
  return t.unit === 'years' && isGeneratorQueueBenchmarkFraming(clean, t.index);
}

function unverifiedQueueBenchmarkViolation(
  t: TaggedNumber,
  narrativeText: string,
  sectionId: string | undefined,
): GroundingViolation {
  return {
    kind: 'invented-number',
    severity: 'block',
    assertionRole: 'generator-queue-benchmark',
    message: `"${t.raw}" is presented as a generator interconnection-queue benchmark, but the benchmark is not source-verified: no cited current knowledge source states it in this form, and it is not evidence of this site's queue duration (grid.queueYears is ABSENT).`,
    observed: t.raw,
    excerpt: sentenceAt(narrativeText, t.index),
    factId: 'grid.queueYears',
    ...(sectionId ? { sectionId } : {}),
  };
}

function checkNumerics(
  narrativeText: string,
  numericFacts: NumericFactIndexEntry[],
  absentNumericUnits: Map<string, string[]>,
  /** ALL absent facts per unit (unpruned) — for absent-fact assertion detection. */
  absentFactsByUnit: Map<string, Array<{ factId: string; label: string }>>,
  tolerancePct: number,
  sectionId: string | undefined,
  numericSourceEvidence?: NumericSourceEvidence,
  frozenBomSubtotalSource?: FrozenBomSubtotalSource,
  siteId?: string,
): { violations: GroundingViolation[]; mentions: number; phaseDeltaBindings: number } {
  const violations: GroundingViolation[] = [];
  const clean = stripRefTokens(narrativeText);
  const tagged = extractTaggedNumbers(narrativeText);
  let phaseDeltaBindings = 0;
  /** FIX 1 — fall-through warns dedupe per (unit, value) so a repeated derived
   *  figure ("250 MW" × 20) surfaces ONCE, never a storm. */
  const fallThroughSeen = new Set<string>();

  // Only callers that already found an absent queue fact invoke this role.
  const acceptQueueContext = (t: TaggedNumber): boolean => {
    if (t.unit !== 'years') return false;
    const role = contextOnlyQueueHeuristicAt({ text: narrativeText, ...t, evidence: numericSourceEvidence });
    if (!role) return false;
    const key = `queue-context:${role}:${t.rangeLow}:${t.value}`;
    if (!fallThroughSeen.has(key)) {
      fallThroughSeen.add(key);
      const estimate = numericSourceEvidence!.contextOnlyQueueEstimate!;
      violations.push(role === 'context' ? {
        kind: 'unlabeled-estimate', severity: 'warn', assertionRole: 'context-only-queue-heuristic',
        observed: t.raw, excerpt: sentenceAt(narrativeText, t.index),
        message: 'The cited duration restates a context-only proximity screening heuristic; it establishes neither a generator-population statistic nor this site’s load-service timetable.',
        ...(sectionId ? { sectionId } : {}),
      } : {
        kind: 'invented-number', severity: 'block', factId: 'grid.queueYears',
        assertionRole: 'context-only-queue-heuristic-cohort-mislabel', observed: t.raw,
        expected: `Context-only proximity screening heuristic (SED basis proximity-heuristic): ${estimate.label}; not a generation-project statistic and not this site's load-service timetable`,
        excerpt: sentenceAt(narrativeText, t.index),
        message: 'The figure restates the SED proximity screening heuristic (substation-capacity class) but labels it a generation-project statistic the source does not state; remove the population label (or duration) and keep the disclaimer and citation.',
        ...(sectionId ? { sectionId } : {}),
      });
    }
    return true;
  };

  // Group governed numeric facts by unit so a tagged number is compared only
  // against same-unit facts. A unit may back several facts (the power triplet
  // shares MW: declared / IT-load / facility). A mention matches if it equals
  // ANY same-unit canonical value within tolerance.
  const factsByUnit = new Map<string, NumericFactIndexEntry[]>();
  for (const f of numericFacts) {
    if (!f.unit) continue;
    const list = factsByUnit.get(f.unit) ?? [];
    list.push(f);
    factsByUnit.set(f.unit, list);
  }
  // The declared campus MW as a point value (a declared band has no single
  // total), for the v3 phase addition noun cue (`isOwnerPhaseAdditionNoun`).
  const declaredEntries = numericFacts.filter(f => f.factId === 'power.declaredMw' && f.unit === 'MW');
  const campusTotalMw = declaredEntries.length === 1 && !declaredEntries[0]!.bandDisplay
    ? declaredEntries[0]!.value : undefined;

  for (const t of tagged) {
    if (!t.unit) continue;
    const bomSubtotal = checkSourceBoundBomSubtotal({ text: narrativeText, ...t,
      unit: t.unit, siteId: siteId ?? '', source: frozenBomSubtotalSource });
    if (bomSubtotal) {
      if (!bomSubtotal.supported) violations.push({ kind: 'invented-number', severity: 'block',
        observed: t.raw, excerpt: sentenceAt(narrativeText, t.index),
        ...(bomSubtotal.expected !== undefined ? { expected: `${bomSubtotal.expected} USD BOM subtotal` } : {}),
        message: 'The explicit BOM subtotal does not match its own cited, verified supply-chain snapshot in this site/SED frame, or is asserted as a different cost role. The whole recorded plan cannot establish quoted/contracted/awarded/paid/EPC prices, a phase subtotal, total project CAPEX or contingency.',
        ...(sectionId ? { sectionId } : {}) });
      continue;
    }
    const phaseDelta = t.unit === 'MW' ? phaseDeltaRoleAt(clean, t.index) : undefined;
    if (phaseDelta) {
      phaseDeltaBindings++;
      const fact = numericFacts.find(candidate => {
        const scope = candidate.numericGroundingScope;
        return candidate.unit === 'MW' && scope?.kind === 'phase-capacity'
          && scope.phaseSequence === phaseDelta.phaseSequence && scope.metric === phaseDelta.metric
          && (!phaseDelta.powerBasis || scope.powerBasis === phaseDelta.powerBasis);
      });
      if (!fact || (!numericMatch(t.value, fact.value, 'MW', tolerancePct) && !roundsToMentionPrecision(t, fact.value))) {
        violations.push({ kind: fact ? 'value-mismatch' : 'invented-number', severity: 'block',
          assertionRole: 'phase-delta-binding', observed: t.raw,
          ...(fact ? { factId: fact.factId, expected: `${formatFactValue(fact.value)} MW` } : {
            expected: `No frozen Phase ${phaseDelta.phaseSequence} ${phaseDelta.metric} load at this electrical boundary.`,
          }), excerpt: sentenceAt(narrativeText, t.index),
          message: `The closed Phase ${phaseDelta.phaseSequence} parenthetical binds ${t.raw} to its ${phaseDelta.metric} load, not the campus total, another phase, a baseline comparison or utility capacity.${fact ? ` Its canonical value is ${formatFactValue(fact.value)} MW.` : ' That frozen owner quantity is unavailable.'}`,
          ...(sectionId ? { sectionId } : {}) });
      }
      continue;
    }
    const campusAdvisory = checkCampusResourceAdvisoryNumber({ text: narrativeText, index: t.index, unit: t.unit, statements: numericSourceEvidence?.campusAdvisoryStatements });
    if (campusAdvisory) {
      if (!campusAdvisory.supported) violations.push({ kind: 'invented-number', severity: 'block', assertionRole: 'campus-resource-comparison', observed: t.raw, excerpt: sentenceAt(narrativeText, t.index), message: 'The attempted campus comparison does not match the exact frozen conditional case-ceiling statement and electrical boundary.', ...(sectionId ? { sectionId } : {}) });
      continue;
    }
    const financialPlanningPrice = checkSourceFinancialPlanningEnergyPrice({ text: narrativeText, ...t, unit: t.unit,
      evidence: numericSourceEvidence?.financialPlanningEnergyPrices });
    if (financialPlanningPrice) {
      if (!financialPlanningPrice.supported) violations.push({ kind: 'invented-number', severity: 'block',
        assertionRole: 'source-financial-planning-energy-price', observed: t.raw, expected: financialPlanningPrice.expected,
        excerpt: sentenceAt(narrativeText, t.index),
        message: 'The attempted financial planning energy price does not bind the exact current frozen source estimate and its own document citation, or upgrades its financial role.',
        ...(sectionId ? { sectionId } : {}) });
      continue;
    }
    const briefAnnualMetric = checkCustomBriefAnnualFinancialMetric({ text: narrativeText, ...t, unit: t.unit,
      evidence: numericSourceEvidence?.customBriefAnnualFinancialMetrics });
    if (briefAnnualMetric) {
      if (!briefAnnualMetric.supported) violations.push({ kind: 'invented-number', severity: 'block',
        assertionRole: 'custom-brief-source-annual-financial-metric', observed: t.raw, expected: briefAnnualMetric.expected,
        excerpt: sentenceAt(narrativeText, t.index),
        message: 'The quoted Financial annual metric lacks its exact independently admitted current source, frozen model result, own inferred report citation, estimated scope or reference-year payback clock.',
        ...(sectionId ? { sectionId } : {}) });
      continue;
    }
    const modeledAnnualMetric = checkModeledAnnualFinancialMetric({ text: narrativeText, ...t, unit: t.unit,
      evidence: numericSourceEvidence?.modeledAnnualFinancialMetrics });
    if (modeledAnnualMetric) {
      if (!modeledAnnualMetric.supported) violations.push({ kind: 'invented-number', severity: 'block',
        assertionRole: 'modeled-annual-financial-metric', observed: t.raw, expected: modeledAnnualMetric.expected,
        excerpt: sentenceAt(narrativeText, t.index),
        message: 'The attempted annual-model payback or capital intensity conflicts with its frozen model, reference-year clock, sole declared facility capacity basis, or estimated scope.',
        ...(sectionId ? { sectionId } : {}) });
      continue;
    }
    const modeledProjectBudget = checkModeledProjectBudgetNumber({ text: narrativeText, ...t, unit: t.unit,
      evidence: numericSourceEvidence?.modeledProjectBudget });
    if (modeledProjectBudget) {
      if (!modeledProjectBudget.supported) violations.push({ kind: 'invented-number', severity: 'block',
        assertionRole: 'modeled-project-budget', observed: t.raw, expected: modeledProjectBudget.expected,
        excerpt: sentenceAt(narrativeText, t.index),
        message: 'The attempted modeled whole-project budget does not bind the frozen same-owner/site/SED annual model, or upgrades its estimated budget role.',
        ...(sectionId ? { sectionId } : {}) });
      continue;
    }
    const designLoadMargin = checkSourceStatedDesignLoadMargin({ text: narrativeText, ...t, unit: t.unit,
      evidence: numericSourceEvidence?.sourceStatedDesignLoadMargins });
    if (designLoadMargin) {
      if (!designLoadMargin.supported) violations.push({ kind: 'invented-number', severity: 'block',
        assertionRole: 'source-design-load-margin', observed: t.raw, expected: designLoadMargin.expected,
        excerpt: sentenceAt(narrativeText, t.index),
        message: 'The attempted source-stated design load margin does not bind the exact frozen same-phase surviving-service allocation/demand/margin tuple, or upgrades its design role.',
        ...(sectionId ? { sectionId } : {}) });
      continue;
    }
    const gridImportMargin = checkModeledGridImportMargin({ text: narrativeText, ...t, unit: t.unit,
      evidence: numericSourceEvidence?.modeledGridImportMargins });
    if (gridImportMargin) {
      if (!gridImportMargin.supported) violations.push({
        kind: 'invented-number', severity: 'block', assertionRole: 'modeled-grid-import-margin',
        observed: t.raw, expected: gridImportMargin.expected, excerpt: sentenceAt(narrativeText, t.index),
        message: 'The attempted modeled grid-import margin does not bind an exact frozen same-phase normal-case import/load/margin tuple, or overstates its source role.',
        ...(sectionId ? { sectionId } : {}),
      });
      continue;
    }
    if (t.unit === 'acres' && isSourceCitedSolarLandEstimate({ text: narrativeText, ...t, evidence: numericSourceEvidence })) {
      const key = `solar-land-estimate:${t.value}`;
      if (!fallThroughSeen.has(key)) {
        fallThroughSeen.add(key);
        violations.push({ kind: 'unlabeled-estimate', severity: 'warn', observed: t.raw,
          message: `"${t.raw}" is an illustrative solar land requirement, not measured site acreage. Its cited land-intensity benchmark does not establish engineered firm capacity or usable land; validate the generation and test-fit assumptions before treating it as a requirement.`,
          excerpt: sentenceAt(narrativeText, t.index), ...(sectionId ? { sectionId } : {}),
        });
      }
      continue;
    }
    if (isSupportedSourceRoleNumber({ text: narrativeText, ...t, unit: t.unit, evidence: numericSourceEvidence })) continue;
    if (t.unit === 'years' && isSourceQuotedDocumentDuration({ text: narrativeText, ...t, evidence: numericSourceEvidence })) continue;
    // The source-bound complete equipment label is independent of the site's
    // apparent-power fact, including when that system fact is absent.
    if (isFrozenBomNameplateMention({ text: narrativeText, index: t.index, unit: t.unit, evidence: numericSourceEvidence })) continue;
    if (isFrozenBomTransformerRating({ text: narrativeText, index: t.index, unit: t.unit, evidence: numericSourceEvidence })) continue;
    if (isSourceCitedTransformerRating({ text: narrativeText, index: t.index, unit: t.unit, evidence: numericSourceEvidence })) continue;
    // An uncited proposal BESS rating in an owner phase row binds to that
    // phase's frozen owner notes (exact power AND energy), never to campus load.
    if (t.unit === 'MW' && isOwnerRecordedPhaseBatteryRating({ text: clean, ...t, unit: t.unit,
      evidence: numericSourceEvidence, rowPhase: serializedPhaseRowAt(clean, t.index) })) continue;
    const tribalDistance = t.unit === 'km'
      ? sourceCitedTribalHeadquartersDistance({ text: narrativeText, ...t, evidence: numericSourceEvidence }) : undefined;
    if (tribalDistance) {
      if (!tribalDistance.matches) violations.push({ kind: 'value-mismatch', severity: 'block', observed: t.raw,
        expected: tribalDistance.expected, excerpt: sentenceAt(narrativeText, t.index),
        message: `The cited frozen research records ${tribalDistance.expected}; the report states ${t.raw}. Preserve the research quantity, unit and qualification.`,
        ...(sectionId ? { sectionId } : {}) });
      continue;
    }
    if (isOpenQuestionNumber(clean, t.index)) continue;
    if (t.unit === 'years' && (isDeclaredScheduleTarget(clean, t.index, t.raw.length)
      || isRegionalGeneratorQueueBenchmark(clean, t.index))) continue;
    // A deliberately zero grid-credit planning case is not a measured claim
    // of zero utility headroom. Keep nonzero assumptions and actual absence
    // assertions in the ordinary evidence checks.
    if (t.unit === 'MW' && t.value === 0 && (t.rangeLow == null || t.rangeLow === 0)
      && isZeroGridPlanningCredit(clean, t.index, t.raw.length)) continue;
    if (t.unit === 'MW' && (isGenericInterconnectionCostThreshold(clean, t.index) || isOtherProjectCapacityMention(clean, t.index, t.raw.length))) continue;
    // A historical study footprint has a separate role from the governed
    // parcel/wetlands totals. Its source citation is checked independently.
    if (t.unit === 'acres' && isSurveyFootprintMention(clean, t.index)) continue;
    const allSameUnitFacts = factsByUnit.get(t.unit);
    if (t.unit === 'MW' && isAssertedGenerationCapacity(clean, t.index, t.raw.length)) {
      const loadFact = allSameUnitFacts?.find(fact => numericMatch(t.value, fact.value, 'MW', tolerancePct)
        && (fact.factId === 'power.itLoadMw' || fact.factId === 'power.facilityMw'
          || (fact.numericGroundingScope?.kind === 'phase-capacity' && fact.numericGroundingScope.powerBasis != null
            && numericGroundingScopeMatches(fact, clean, t.index, undefined, campusTotalMw))));
      const generationFact = allSameUnitFacts?.some(fact => numericMatch(t.value, fact.value, 'MW', tolerancePct)
        && fact.numericGroundingScope?.kind === 'phase-feasibility'
        && ['source-capacity', 'source-firm'].includes(fact.numericGroundingScope.metric)
        && numericGroundingScopeMatches(fact, clean, t.index, undefined, campusTotalMw));
      if (loadFact && !generationFact) {
        violations.push({ kind: 'value-mismatch', severity: 'block', factId: loadFact.factId,
          assertionRole: 'generation-capacity-relabel',
          observed: `${t.raw} generation capacity`, excerpt: sentenceAt(narrativeText, t.index),
          expected: 'Recorded IT/facility load; generation equipment capacity requires a separate sizing basis.',
          message: `The report presents ${t.raw} as plant/generation capacity, but the matching governed value describes IT/facility load. Preserve that electrical boundary; state any generation option as an explicitly attributed or conditional planning case until separate equipment sizing is evidenced.`,
          ...(sectionId ? { sectionId } : {}) });
        continue;
      }
    }

    if (t.unit === 'MW' && isDeveloperStatedGridCapacity({ text: narrativeText, ...t, evidence: numericSourceEvidence })) continue;

    /*
     * FIN-1 / audit 3.8 — the SUBJECT this mention names, for the money and
     * percent classes. `undefined` for every other unit (the map is empty for
     * them), so MW / km / acres / years / MVA are untouched. See
     * `SUBJECT_KEYED_UNITS` for why a percentage must be keyed on its subject
     * and not on its unit.
     */
    const subjectKeyed = SUBJECT_KEYED_UNITS.has(t.unit);
    const subject = subjectKeyed ? classifySubjectAt(clean, t.index, t.raw) : undefined;
    const comparative = subjectKeyed
      && COMPARATIVE_FRAMING_RE.test(proximityAt(clean, t.index, SUBJECT_WINDOW));
    // Explicit facility/system apparent power is a different quantity from
    // every equipment rating. A same-valued sibling must not satisfy it.
    // Reuse the existing role predicate; component/logistics prose is unchanged.
    const systemMvaRole = t.unit === 'MVA'
      && isSystemApparentPowerMention(clean, t.index, 'power.apparentMva');
    const sameUnitFacts: readonly NumericFactIndexEntry[] =
      narrowToSubject(t.unit, allSameUnitFacts ?? [], subject, comparative)
        .filter(fact => !systemMvaRole || fact.factId === 'power.apparentMva');

    // (a) absent-leakage: the unit's only fact(s) are absent → no value exists.
    //
    // DOMAIN GATE (2026-08-16) — an ABSENT fact has no value, so the only thing
    // linking it to this mention is a shared unit. For duration units that link
    // is far too weak to sustain a block: `grid.queueYears` is the sole holder of
    // unit `years`, so on a site with no queue estimate EVERY duration in the
    // report was accused of being invented — 38 live blocks across 4 reports,
    // all four held, every sampled excerpt a permitting / conservation /
    // market-lookback timeline rather than an interconnection queue.
    //
    // `absentFactMayGovern` requires the sentence to tie the mention to the
    // absent fact's domain. It removes NO candidate fact (there are none) — it
    // decides only whether a valueless fact is entitled to accuse. Units other
    // than durations are unaffected.
    //
    // SUBJECT-NARROWED for the money/percent classes on the same reasoning: an
    // absent IRR must not accuse a capacity-factor percentage of being
    // invented merely because both are written with a `%`.
    const absentDomainWindow = governanceWindow(
      proximityAt(narrativeText, t.index), lineAt(narrativeText, t.index), isSerializedRecord,
    );
    // The unit-only map is pruned whenever ANY percentage fact is present.
    // A present financial CF does not supply a missing wind-resource CF (or
    // vice versa). For these two explicitly attributed CF roles, consult the
    // unpruned absent facts before the no-present-subject exit below.
    const capacityFactorRole = t.unit === '%'
      && (subject === 'capacity-factor' || subject === 'wind-resource-capacity-factor');
    // An explicitly named PPA rate retains its own absent authority even when
    // a present fuel/LCOE sibling pruned the unit-only absence map. The owner
    // financial planning estimate above never creates a PPA fact.
    const attributedPpaRate = t.unit === 'USD/MWh' && subject === 'ppa';
    const absentCandidates = capacityFactorRole || systemMvaRole || attributedPpaRate
      ? absentFactsByUnit.get(t.unit) ?? []
      : (absentNumericUnits.get(t.unit) ?? []).map((factId) => ({ factId }));
    const absentFactIds = narrowToSubject(
      t.unit,
      absentCandidates,
      subject,
      comparative,
    ).filter(c => (!systemMvaRole || c.factId === 'power.apparentMva')
      && (c.factId !== 'siteArea.buildableAcres' || assertsBuildableAcreage(clean, t.index, t.raw.length))
      && absentFactMayGovern(t.unit, absentDomainWindow, c.factId)).map((c) => c.factId);
    const noPresentFact = sameUnitFacts.length === 0;
    if (
      noPresentFact
      && absentFactIds.length > 0
    ) {
      if (absentFactIds.includes('grid.queueYears') && acceptQueueContext(t)) continue;
      if (absentFactIds.includes('grid.queueYears') && isUnverifiedQueueBenchmark(t, clean)) {
        violations.push(unverifiedQueueBenchmarkViolation(t, narrativeText, sectionId));
        continue;
      }
      const absentExcerpt = sentenceAt(narrativeText, t.index);
      const validAbsentFactIds = absentFactIds.filter(
        (fid) => validateSemanticFactBinding({ factId: fid, claimText: absentExcerpt, observedText: t.raw }).ok,
      );
      if (validAbsentFactIds.length > 0) {
        violations.push({
          kind: 'invented-number',
          severity: 'block',
          message: `Narrative asserts "${t.raw}" but the governed fact(s) ${validAbsentFactIds.join(', ')} for unit ${t.unit} are ABSENT (no value at any tier) — value invented.`,
          observed: t.raw,
          excerpt: absentExcerpt,
          factId: validAbsentFactIds[0],
          ...(sectionId ? { sectionId } : {}),
        });
      }
      continue;
    }
    if (sameUnitFacts.length === 0) continue; // no governed fact for this unit / subject

    /*
     * SUBJECT COMPATIBILITY (2026-08-14, Triple Threat RCA) — the fix for the
     * grounding-vs-tier-2e CONTRADICTION.
     *
     * `DOMAIN_POLYSEMOUS_UNITS` deliberately excludes MW on the premise
     * (line ~236) that "a site report's MW figures have a DOMINANT referent
     * (the campus load) that the catalog governs, so an unmatched same-unit
     * figure most plausibly restates it". That premise holds only while the
     * campus-load fact is PRESENT. On a phase-plan / range-bucket site
     * `power.declaredMw` is deliberately absent ("a range never reconciles
     * into a scalar" — projectIntent.ts), so the store's ONLY MW fact was
     * `grid.headroomMw`, and every project-capacity mention collapsed onto it:
     *
     *   live feasibility, 10 MW edge datacenter, 26 MW line headroom
     *   → ~20 blocks: 'Narrative states "10 MW" for grid.headroomMw; the only
     *     permitted value is 26 MW.'
     *
     * while tier-2e simultaneously resolved the same figures to 10 and offered
     * "Settled → 10". Applying either verdict corrupted the other's quantity.
     *
     * Grid headroom is not the facility's capacity — it belongs to a
     * CONDUCTOR. So a non-facility MW fact may only be attributed to a mention
     * whose sentence is non-facility MW context, and a facility MW fact only
     * to one that is not. Both engines now decide this from the SAME shared
     * predicate, so they cannot again assert opposite truths about one figure.
     *
     * Dropping to an empty candidate set is the honest outcome — the store
     * governs no fact for this quantity — and falls through to the ungoverned
     * path rather than inventing a mismatch.
     */
    /*
     * NO SUBJECT PRE-FILTER (2026-08-15) — reverts the 2026-08-14 nearest-marker
     * MW classifier, which is LIVE IN PRODUCTION AND REGRESSING.
     *
     * A controlled same-prose replay (6 report types x 2 bodies x 2 code
     * versions, production-faithful store) measured the cost: +88
     * value-mismatches across 12 real bodies, every one false. 60 of 90 fired
     * on the platform's OWN "Intent Context:" boilerplate — flipped by the
     * single token "Grid import" — a five-violation-per-report floor that no
     * regeneration can clear, and it turned a CLEAN power-availability report
     * into a 9-block hold.
     *
     * MECHANISM: filtering BEFORE matching can delete the very fact a figure
     * exactly equals, then block that figure against a different one. Redhead
     * governs power.declaredMw = 550 and the prose says "securing the full
     * 550 MW from the grid" — blocked against grid.headroomIndicatedMw = 50.
     *
     * Three attempts to keep the classifier and guard its edges each failed
     * adversarial review, every one introducing a NEW way to hold a correct
     * report. This returns to the candidate semantics that shipped for months
     * (v1.23.0): compare against EVERY same-unit fact and let STORE polysemy
     * (`distinctValues`, below) decide block-vs-warn.
     *
     * The classifier was never load-bearing for the contradiction it was
     * written for — the declared-band fact is what grounds the original Triple
     * Threat "10 MW" case, and the tier-2e half of that fix lives in the
     * SHARED `NON_FACILITY_MW_CONTEXT_RE`, which both reconcilers still use.
     *
     * ACCEPTED TRADE-OFF, identical to v1.23.0: where the store holds two or
     * more values of one unit, an unmatched figure WARNS rather than blocks —
     * so a subject swap and a wild invention both surface as advisories.
     */
    const roleScopedFacts = sameUnitFacts.filter(
      (fact) => fact.numericGroundingScope != null
        && numericGroundingScopeMatches(fact, clean, t.index, numericSourceEvidence, campusTotalMw),
    );
    const compatibleFacts = roleScopedFacts.length > 0
      ? roleScopedFacts
      : sameUnitFacts.filter((fact) => fact.numericGroundingScope == null);

    // X-SED-6 — the same-unit candidate set spans ALL facts of the unit
    // (siteArea.acres + buildableAcres + underControl bounds + wetlands all
    // compete for an "acres" mention). NEAREST match within tolerance wins,
    // so a prose "800 acres (buildable)" attributes to the buildable fact,
    // never a mismatch against the surveyed boundary.
    const byDistance = [...compatibleFacts].sort(
      (a, b) =>
        Math.abs(a.value - t.value) / Math.max(Math.abs(a.value), 1) -
        Math.abs(b.value - t.value) / Math.max(Math.abs(b.value), 1),
    );
    const scopedScreeningRange = compatibleFacts.length > 0
      && compatibleFacts.every(fact => fact.numericGroundingScope?.kind === 'screening-load');
    if (scopedScreeningRange && t.rangeLow !== undefined) {
      // Expanded endpoint facts retain their canonical factId. Match BOTH
      // endpoints to one case; a union across cases fabricates a range and
      // rejects each valid case whenever alternative PUE/load screens exist.
      const cases = new Map<string, typeof compatibleFacts>();
      for (const fact of compatibleFacts) {
        const endpoints = cases.get(fact.factId) ?? [];
        endpoints.push(fact);
        cases.set(fact.factId, endpoints);
      }
      const ranges = [...cases.values()].map(endpoints => ({
        factId: endpoints[0]!.factId,
        low: Math.min(...endpoints.map(fact => fact.value)),
        high: Math.max(...endpoints.map(fact => fact.value)),
      }));
      const lowMention = { ...t, value: t.rangeLow, raw: RANGE_LOW_RE.exec(clean.slice(0, t.index))?.[1] ?? String(t.rangeLow) };
      const matchingRange = ranges.some(({ low, high }) =>
        (numericMatch(t.rangeLow!, low, t.unit, tolerancePct) || roundsToMentionPrecision(lowMention, low))
        && (numericMatch(t.value, high, t.unit, tolerancePct) || roundsToMentionPrecision(t, high)));
      if (!matchingRange) {
        const observed = `${formatFactValue(t.rangeLow)}–${formatFactValue(t.value)} ${t.unit}`;
        const expected = ranges.map(({ low, high }) => `${formatFactValue(low)}–${formatFactValue(high)} ${t.unit}`).join(' or ');
        violations.push({ kind: 'value-mismatch', severity: 'block', observed, expected,
          factId: byDistance[0]!.factId, excerpt: sentenceAt(narrativeText, t.index),
          message: `Narrative states "${observed}" for the scoped screening load; each range must match one frozen modeled case: ${expected}.`,
          ...(sectionId ? { sectionId } : {}) });
        continue;
      }
    }
    // A stated RANGE that brackets a governed value is CONSISTENT with it, not
    // a substitution of it. `grid.queueYears` (1.5) is literally derived as the
    // midpoint of the SED's "12-24 months", so blocking the prose that quotes
    // that range would demand the report be less faithful to its own evidence.
    // Exclusion still blocks: the golden "700–1,100 acres" band does not
    // contain the 1,170.1-acre boundary and remains a value-mismatch.
    const hasExplicitLeadingMinus =
      t.rangeLow === undefined && /(?:^|[\s(:=])[-−]$/.test(clean.slice(0, t.index));
    const signedMentionForFact = (f: NumericFactIndexEntry): TaggedNumber =>
      hasExplicitLeadingMinus &&
      f.numericGroundingScope?.kind === 'phase-feasibility' &&
      f.numericGroundingScope.metric === 'firm-margin' &&
      f.value < 0
        ? { ...t, value: -t.value, raw: `-${t.raw}` }
        : t;
    const matchingFact =
      byDistance.length === 0
        ? undefined
        : byDistance.find((f) => {
            const m = signedMentionForFact(f);
            return numericMatch(m.value, f.value, m.unit, tolerancePct);
          })
      // Stated-precision rounding (2026-08-13): "3 km" (zero decimals) is
      // honest rounding of a governed 2.9 km — the prose's own precision
      // decides, so "3.0 km" against 5.2 (the golden substitution case)
      // still fails: 5.2 does not round to 3.0 at one decimal.
      ?? byDistance.find((f) => roundsToMentionPrecision(signedMentionForFact(f), f.value))
      ?? (t.rangeLow !== undefined
        ? byDistance.find((f) => f.value >= t.rangeLow! && f.value <= t.value)
        : undefined);
    if (!matchingFact) {
      // Closest SUBJECT-COMPATIBLE fact by relative error, for the "expected"
      // hint. Undefined when the store governs no fact for this subject.
      const closest = byDistance[0];

      // (i) FIX 1 — ABSENT-fact assertion: the mention matches no governed
      // value but its nearby text names an ABSENT same-unit fact ("a firm
      // 49 MW" while firmCapacity.targetMw is ABSENT). The evidence never set
      // that quantity ⇒ invented-number, regardless of the near-miss band.
      //
      // SUBJECT-FILTERED for the same reason the present-fact set is: a
      // headroom sentence must not be able to assert an absent FACILITY fact,
      // nor a facility sentence an absent headroom one. This arm deliberately
      // survives an empty compatible PRESENT set — dropping it there would
      // trade the false mismatch for a blind spot, which is the one thing the
      // polysemy fixes must never do.
      //
      // FIN-1 / audit 3.8 — subject-narrowed for the money/percent classes
      // too. The keyword derivation alone cannot separate them: every
      // `finance.*` factId camel-splits to the topic word "finance", so
      // without this an absent NPV would accuse any `$` figure sitting near
      // the phrase "Finance lead" in the engine's own action plan.
      const absentSiblings = narrowToSubject(
        t.unit,
        absentFactsByUnit.get(t.unit) ?? [],
        subject,
        comparative,
      );
      const hasRoleScopedPresentFact = compatibleFacts.some(
        (f) => f.numericGroundingScope != null,
      );
      const sentenceWithMention = sentenceAt(clean, t.index);
      const hasPowerBasisDisclaimer = hasUnconfirmedPowerBasisDisclaimer(sentenceWithMention);
      const cleanForAbsentKeywords = hasPowerBasisDisclaimer
        ? maskUnconfirmedPowerBasisDisclaimer(clean)
        : clean;
      const assertedAbsent = hasRoleScopedPresentFact ? undefined : absentSiblings.find((f) => {
        if (f.factId === 'siteArea.buildableAcres') {
          return assertsBuildableAcreage(clean, t.index, t.raw.length);
        }
        if (f.factId === 'firmCapacity.targetMw') {
          return assertsFirmCapacityTargetAt(clean, t.index);
        }
        if (hasPowerBasisDisclaimer && (f.factId === 'power.itLoadMw' || f.factId === 'power.facilityMw')) {
          const maskedWindow = proximityAt(cleanForAbsentKeywords, t.index);
          const matchesBasis = f.factId === 'power.itLoadMw'
            ? /\bIT[- ](?:load|power|capacity|demand)\b/i.test(maskedWindow)
            : /\bfacility[- ](?:load|power|capacity|demand)\b/i.test(maskedWindow);
          if (!matchesBasis) return false;
          return validateSemanticFactBinding({
            factId: f.factId,
            claimText: sentenceAt(narrativeText, t.index),
            observedText: t.raw,
          }).ok;
        }
        // Multi-token facts need TWO distinct keyword hits (2026-08-13,
        // Redhead RCA): a single generic token cross-matches domain terms of
        // art — "firm transportation agreements with Kinder Morgan …
        // >300 MW gas turbine" attributed the GAS program size to the absent
        // electrical firmCapacity.targetMw on the word "firm" alone. The
        // golden "a firm 49 MW of accredited capacity" keeps blocking: both
        // "firm" AND "capacity" sit in-window. Single-token facts keep the
        // one-hit rule — there is nothing else to corroborate with.
        const kws = absentAssertionKeywords(f.factId, f.label);
        const hits = kws.filter((kw) => hasUnnegatedKeywordNearby(cleanForAbsentKeywords, t.index, kw)).length;
        if (!(kws.length >= 2 ? hits >= 2 : hits >= 1)) return false;
        return validateSemanticFactBinding({
          factId: f.factId,
          claimText: sentenceAt(narrativeText, t.index),
          observedText: t.raw,
        }).ok;
      });
      if (assertedAbsent?.factId === 'grid.queueYears' && acceptQueueContext(t)) continue;
      if (assertedAbsent?.factId === 'grid.queueYears' && isUnverifiedQueueBenchmark(t, clean)) {
        violations.push(unverifiedQueueBenchmarkViolation(t, narrativeText, sectionId));
        continue;
      }
      if (assertedAbsent) {
        const excerpt = sentenceAt(narrativeText, t.index);
        violations.push({
          kind: 'invented-number',
          severity: 'block',
          message: `Narrative asserts "${t.raw}" for ${assertedAbsent.factId} ("${assertedAbsent.label}"), but that fact is ABSENT (no value at any tier) — value invented.`,
          observed: t.raw,
          excerpt,
          factId: assertedAbsent.factId,
          ...(sectionId ? { sectionId } : {}),
        });
        continue;
      }

      // No subject-compatible governed fact exists for this quantity (the
      // store holds MW facts, but all of them belong to the OTHER subject).
      // There is nothing to compare against — asserting a mismatch here is
      // exactly the contradiction this pass removes.
      if (!closest) continue;

      // A studied load case / phase threshold and grid headroom happen to share
      // MW, but they are not the same fact. Do not turn a close scenario value
      // (live: SIS 200 MW vs legacy 194.3 MW ATC) into a headroom substitution.
      if (
        t.unit === 'MW'
        && isHeadroomScenarioMention(sentenceAt(clean, t.index), closest.factId)
      ) {
        continue;
      }

      // (ii) FIX 1 — POLYSEMOUS unit + far from EVERY candidate ⇒ a derived or
      // contextual figure, not a substitution. Fall through to the
      // unlabeled-estimate handling: WARN when unqualified (deduped), never
      // block. See NEAR_MISS_FACTOR for the discrimination rationale and
      // DOMAIN_POLYSEMOUS_UNITS for why store cardinality cannot be the only
      // polysemy signal.
      /*
       * Counted over the SUBJECT-COMPATIBLE candidates, not every same-unit
       * fact (2026-08-14). Store polysemy is a statement about how many values
       * could plausibly claim THIS mention; a headroom fact the prose is
       * demonstrably not talking about is not a rival reading. Counting it
       * downgraded a genuine facility substitution ("The 500 MW campus"
       * against a governed 350 MW) from block to warn purely because the store
       * also held a 26 MW headroom — i.e. the subject split RESTORES blocking
       * power here rather than reducing it.
       *
       */
      // STORE polysemy, as before the classifier: counting over the filtered
      // candidates made a two-fact store look monosemous and turned derived
      // figures (a "500 MW gap" = 550 - 50) into hard blocks.
      //
      // FIN-1 / audit 3.8 — for a SUBJECT-KEYED unit the set has already been
      // narrowed to the one subject the prose unambiguously names, so the count
      // is per-subject by construction. That is deliberate and is the opposite
      // of the reverted MW classifier: narrowing here happened only because the
      // author WROTE the term of art ("IRR", "NPV"), and when it empties the set
      // the checker goes silent rather than blocking against a survivor.
      const distinctValues = new Set(
        // Unselected declared-phase values cannot broaden the legacy MW
        // comparison set or downgrade an unrelated campus assertion.
        (roleScopedFacts.length > 0 ? compatibleFacts : sameUnitFacts.filter(
          fact => fact.numericGroundingScope?.kind !== 'phase-capacity'
            && fact.numericGroundingScope?.kind !== 'screening-load',
        )).map((f) => f.value),
      ).size;
      const relDistance =
        Math.abs(closest.value - t.value) /
        Math.max(Math.abs(closest.value), Math.abs(t.value), 1);
      const tol = unitTolerance(t.unit, tolerancePct);
      // The store DEMONSTRATES polysemy (several governed values), or the
      // DOMAIN does (a unit that measures a relation, never a subject).
      // The two endpoints of one explicitly scoped modeled load are a range,
      // not competing meanings of MW. An out-of-range value stays a mismatch.
      const storePolysemous = distinctValues >= 2 && !scopedScreeningRange;
      const domainRule = DOMAIN_POLYSEMOUS_UNITS.get(t.unit);
      const domainPolysemous = domainRule !== undefined;
      // When only the domain rule applies, the store still governs a SINGLE
      // value for the unit, so prose that names that fact's subject next to the
      // figure is a substitution of it — keep blocking (the golden "Moss Bluff
      // substation, 3.0 km" claim against a canonical 5.2 km). Only for units
      // whose keywords actually name the quantity's subject — see the
      // `subjectAttributable` note on DOMAIN_POLYSEMOUS_UNITS.
      const namesClosestSubject = t.unit === 'MVA'
        ? isSystemApparentPowerMention(clean, t.index, closest.factId)
        : namesGovernedSubjectNearby(closest.factId, clean, t.index);
      const attributedToClosest =
        domainRule?.subjectAttributable === true
        && !storePolysemous
        && namesClosestSubject
        // A parenthetical road/route distance names the same endpoint but a
        // different relation from the governed straight-line/site distance.
        // Once source-unit rounding is correct, `5.2 miles by road` can no
        // longer masquerade as canonical `5.2 km`; keep it contextual rather
        // than manufacturing a second block beside the real km substitution.
        && !(t.unit === 'km'
          && /^\s*(?:by road|road distance|driving distance|route distance)\b/i
            .test(clean.slice(t.index + t.raw.length, t.index + t.raw.length + 40)));
      if (
        (storePolysemous || domainPolysemous)
        && !attributedToClosest
        && relDistance > NEAR_MISS_FACTOR * tol
      ) {
        const dedupeKey = `${t.unit}:${t.value}`;
        if (
          !hasNearbyQualifier(clean, t.index)
          && !isEngineDeterministicSpan(sentenceAt(clean, t.index))
          && !fallThroughSeen.has(dedupeKey)
        ) {
          fallThroughSeen.add(dedupeKey);
          violations.push({
            kind: 'unlabeled-estimate',
            severity: 'warn',
            // NO factId on purpose: this mention is attributed to NO governed
            // fact, and stamping the nearest one would let the on-mode
            // labeled-unverifiable upgrade block an ungoverned figure.
            message: `"${t.raw}" matches no canonical ${t.unit} value (nearest: ${formatFactValue(closest.value)} ${t.unit}, ${closest.factId}) — reads as a derived/contextual figure; label it as an estimate/derived value or cite its source.`,
            observed: t.raw,
            excerpt: sentenceAt(narrativeText, t.index),
            ...(sectionId ? { sectionId } : {}),
          });
        }
        continue;
      }

      /*
       * CO-OCCURRENCE (2026-08-14) — LOCKSTEP with `internalNumberReconciler`,
       * which has encoded this rule since 2026-08-05: "values that CO-OCCUR in
       * one excerpt are playing different roles, not contradicting".
       *
       * A substitution REPLACES the governed value. When the sentence states
       * the governed value ITSELF and then a second figure, the second is a
       * different quantity being enumerated alongside it — most often the same
       * measure on a different piece of infrastructure:
       *
       *   "the user-provided estimate of 26 MW on the 34.5kV line and
       *    4.13 MW on the 12kV line must be treated as preliminary"
       *
       * The store governs ONE headroom (the 34.5 kV line's 26 MW); the 12 kV
       * line's 4.13 MW is a SECOND conductor's headroom that the store does
       * not model, and calling it a substitution of the first was wrong.
       * Guarded tightly — the governed value must literally appear in the same
       * sentence — so a genuine substitution, which by definition does not
       * restate the value it replaces, still blocks.
       */
      // `clean`, not the raw narrative: a "[Ref 26]" citation index must not
      // be readable as a restatement of a governed 26.
      const sentence = sentenceAt(clean, t.index);
      // Wind resource CF and financial CF can co-occur with the same number.
      // Only a restatement of this same attributed CF subject can count; a
      // project percentage cannot excuse a wrong resource percentage, or vice versa.
      const cfSubject = closest.factId === 'wind.resourceCapacityFactorPct'
        || closest.factId === 'capacityFactorPct' ? FACT_SUBJECT[closest.factId] : undefined;
      const sentenceBounds = cfSubject ? sentenceRangeAt(clean, t.index) : undefined;
      const restatesGovernedValue = cfSubject && sentenceBounds
        ? tagged.some(other => other.unit === '%' && other.index >= sentenceBounds.start && other.index < sentenceBounds.end
          && numericMatch(other.value, closest.value, '%', tolerancePct)
          && classifySubjectAt(clean, other.index, other.raw) === cfSubject)
        : sentenceRestatesGovernedValue(sentence, closest.value, t.unit, tolerancePct);
      if (closest.numericGroundingScope?.kind !== 'phase-capacity'
        && closest.numericGroundingScope?.kind !== 'screening-load'
        && restatesGovernedValue) {
        continue;
      }


      // (iii) Near-miss of a governed value, a monosemous single-valued unit,
      // or a polysemous unit whose fact the prose NAMES: a
      // substituted/garbled figure ⇒ value-mismatch, as before.
      const mismatchExcerpt = sentenceAt(narrativeText, t.index);
      const mismatchExpected = closest.bandDisplay ?? `${formatFactValue(closest.value)} ${t.unit}`;
      if (
        closest.numericGroundingScope?.kind !== 'phase-capacity' &&
        !validateSemanticFactBinding({
          factId: closest.factId,
          claimText: mismatchExcerpt,
          observedText: t.raw,
          expectedText: mismatchExpected,
        }).ok
      ) {
        continue;
      }
      violations.push({
        kind: 'value-mismatch',
        severity: 'block',
        /*
         * BAND-AWARE (2026-08-14). For a range-bucket project the governed
         * fact is a DECLARED BAND, so "the only permitted value is 10 MW" is
         * false — and it is not merely cosmetic: this string reaches
         * `buildGroundingRepairPrompt`, and the truth-question builder offers
         * `expected` as a one-click "canonical evidence value" that writes
         * through to `power.data.declaredMw`. Accepting it would collapse the
         * user's range into a scalar, the exact thing the SED invariant
         * forbids. Name the band and ask for the band.
         */
        message: closest.bandDisplay
          ? `Narrative states "${t.raw}" for ${closest.factId}, but the declared project intent is a RANGE (${closest.bandDisplay}) — state the declared range, not a point value inside it.`
          : `Narrative states "${t.raw}" for ${closest.factId}; the only permitted value is ${formatFactValue(closest.value)} ${t.unit}.`,
        observed: t.raw,
        excerpt: sentenceAt(narrativeText, t.index),
        factId: closest.factId,
        // Never a bare scalar for a band fact — see the message note above.
        expected: closest.bandDisplay
          ? `${closest.bandDisplay} (declared range — state the range, not a point)`
          : `${formatFactValue(closest.value)} ${t.unit}`,
        ...(sectionId ? { sectionId } : {}),
      });
      continue;
    }

    // (b) unlabeled-estimate: matched, but the matching (nearest) fact is
    // non-verified and the value reads as hard fact with no nearby qualifier.
    //
    // FIN-1 / audit 3.8 — an ENGINE-DETERMINISTIC span is exempt. The financial
    // stamp's banding rationale ("Positive NPV ($142.0M); all financial levers
    // clear their thresholds with margin.") is the engine's own statement of
    // the number it just computed; demanding a hedge or a citation on it is
    // asking the report to doubt its own arithmetic. The exemption is scoped to
    // THIS labelling class — the figure still had to MATCH the canonical fact
    // to reach here, and a span that imitates the template while stating a
    // different number never gets this far (it exits at value-mismatch above).
    if (
      (matchingFact.status === 'estimated' || matchingFact.status === 'labeled-unverifiable') &&
      !hasNearbyQualifier(clean, t.index) &&
      !isEngineDeterministicSpan(sentenceAt(clean, t.index))
    ) {
      violations.push({
        kind: 'unlabeled-estimate',
        // labeled-unverifiable is harder: in on-mode the runner upgrades it to block.
        severity: 'warn',
        message: `"${t.raw}" (${matchingFact.factId}) is ${matchingFact.status} but stated without an uncertainty qualifier.`,
        observed: t.raw,
        excerpt: sentenceAt(narrativeText, t.index),
        factId: matchingFact.factId,
        ...(sectionId ? { sectionId } : {}),
      });
    }
  }

  return { violations, mentions: tagged.length, phaseDeltaBindings };
}

/* ── The entity gate ─────────────────────────────────────────────────── */

function checkEntities(
  narrativeText: string,
  entityFacts: EntityFactIndexEntry[],
  /** factId → the canonical entity value(s) by category-prefix, for invented-entity detection. */
  entityCategories: Map<string, EntityFactIndexEntry[]>,
  sectionId: string | undefined,
): { violations: GroundingViolation[]; mentions: number } {
  const violations: GroundingViolation[] = [];
  const narrativeKey = entityKey(stripRefTokens(narrativeText));
  let mentions = 0;

  // For each governed entity category (utility / ISO / operator), if the
  // narrative mentions the canonical value → fine. If it does NOT mention the
  // canonical value but DOES surface a different candidate for that category,
  // we cannot reliably NER arbitrary names here without false positives, so we
  // police the conservative, load-bearing case: the canonical value must be
  // present whenever the category's subject is discussed. We approximate
  // "discussed" via the fact label keyword. This keeps the gate fail-open for
  // prose that simply does not name the entity.
  for (const f of entityFacts) {
    const fk = entityKey(f.value);
    const present = narrativeMentions(narrativeKey, fk);
    if (present) {
      mentions += 1;
      if (f.status === 'estimated' || f.status === 'labeled-unverifiable') {
        // Unlabeled-estimate for an entity: present but no qualifier nearby.
        const idx = narrativeKey.indexOf(fk);
        if (idx >= 0 && !hasNearbyQualifier(narrativeKey, idx)) {
          violations.push({
            kind: 'unlabeled-estimate',
            severity: 'warn',
            message: `"${f.value}" (${f.factId}) is ${f.status} but stated without an uncertainty qualifier.`,
            observed: f.value,
            factId: f.factId,
            ...(sectionId ? { sectionId } : {}),
          });
        }
      }
    }
  }

  for (const [cat, candidates] of entityCategories.entries()) {
    for (const f of candidates) {
      const fk = entityKey(f.value);
      if (!narrativeMentions(narrativeKey, fk)) {
        const sibling = candidates.find(s => s.factId !== f.factId && narrativeMentions(narrativeKey, entityKey(s.value)));
        if (sibling) {
          violations.push({
            kind: 'invented-entity',
            severity: 'block',
            message: `Narrative names "${sibling.value}" but omits the canonical "${f.value}" for ${f.factId}.`,
            observed: sibling.value,
            factId: f.factId,
            expected: f.value,
            ...(sectionId ? { sectionId } : {}),
          });
        } else if (f.label && narrativeMentions(narrativeKey, entityKey(f.label))) {
          violations.push({
            kind: 'invented-entity',
            severity: 'warn',
            message: `Narrative discusses the ${f.label} but omits the verified canonical entity "${f.value}".`,
            observed: 'omitted',
            factId: f.factId,
            expected: f.value,
            ...(sectionId ? { sectionId } : {}),
          });
        }
      }
    }
  }

  return { violations, mentions };
}

/* ── Invented-entity NER (Part B) ────────────────────────────────────── */

/**
 * Candidate named-entity pattern (FIX 2): an optional lowercase `the ` then a
 * run of TWO to six Capitalized-then-lowercase words, optionally joined by a
 * lowercase `of` ("Army Corps of Engineers", "Gulf of Mexico"). Matches
 * "Entergy Louisiana", "the Bonneville Power Administration".
 *
 *  - ≥2 capitalized tokens BY CONSTRUCTION — a single capitalized word
 *    ("Concurrently", "Submit", "Available", a sentence-initial verb) is never
 *    a candidate. Single-word entities are deliberately unflaggable
 *    (precision over recall).
 *  - joiners are `[ \t]` ONLY — a candidate can never cross a newline
 *    ("Initial Permitting\nThis" was a live false positive).
 *  - deliberately does NOT match ALL-CAPS acronyms ("MISO", "HIFLD") —
 *    excluded by construction (`[A-Z][a-z]+` requires a lowercase tail).
 */
const NER_CANDIDATE_RE =
  /\b(?:the[ \t]+)?([A-Z][a-z]+(?:[ \t]+(?:of[ \t]+)?[A-Z][a-z]+){1,5})\b/g;

/**
 * FIX 2 (c) — generic TITLE-CASE business/process/report vocabulary. A
 * candidate whose significant tokens are ALL generic ("Due Diligence",
 * "System Impact Study", "Detailed Engineering", "Special Flood Hazard
 * Area", heading fragments, verdict-title fragments) is report furniture,
 * not a named entity. Token-level (not phrase-level) so compound headings
 * the enumeration cannot anticipate ("Critical Grid Constraints") still
 * decompose. Lowercase; matched against normalized candidate tokens.
 * Favors false-negatives: a real org made ENTIRELY of generic words is
 * unNERable — accepted (the substring/token allowlists still ground it when
 * the SED names it).
 */
const NER_GENERIC_TITLE_TOKENS = new Set<string>([
  // process / lifecycle
  'diligence', 'due', 'entitlement', 'permitting', 'permit', 'permits',
  'engineering', 'detailed', 'commissioning', 'construction', 'energization',
  'phased', 'phase', 'schedule', 'timeline', 'milestone', 'milestones',
  'submit', 'concurrently', 'initiate', 'immediately', 'formally',
  'simultaneously', 'prioritize', 'implement', 'commission', 'conduct',
  'engage', 'pursue', 'adopt', 'advance', 'develop', 'obtain', 'secure',
  'verify', 'proceed', 'ranked', 'surfaced', 'according', 'assuming',
  'complete', 'completed', 'finalize', 'prepare', 'execute', 'negotiate',
  'monitor', 'assess', 'evaluate', 'confirm', 'address',
  // report / document furniture
  'summary', 'overview', 'recommendations', 'recommendation', 'findings',
  'analysis', 'assessment', 'study', 'report', 'document', 'evidence',
  'section', 'appendix', 'title', 'key', 'top', 'next', 'actions', 'steps',
  'strengths', 'risks', 'risk', 'matrix', 'conditions', 'condition',
  'implications', 'context', 'intent', 'priority', 'criteria', 'indicated',
  'indication', 'severity', 'typed', 'available', 'availability', 'months',
  'preliminary', 'estimate', 'estimated', 'data', 'requires', 'require',
  // infrastructure / domain descriptors (generic, not names)
  'power', 'grid', 'electric', 'electrical', 'energy', 'utility',
  'utilities', 'municipal', 'substation', 'transmission', 'distribution',
  'interconnection', 'upgrades', 'upgrade', 'capacity', 'load', 'deficit',
  'headroom', 'facility', 'facilities', 'infrastructure', 'system',
  'strategy', 'generation', 'gas', 'natural', 'pipeline', 'water', 'supply',
  'wastewater', 'potable', 'cooling', 'fiber', 'fibre', 'internet',
  'exchange', 'latency', 'connect', 'direct', 'network', 'datacenter',
  'datacenters', 'transport', 'rail', 'road', 'port', 'ship', 'channel',
  'route', 'haul', 'heavy', 'interstate', 'highway', 'access', 'proximity',
  'logistics', 'logistical',
  // land / environment / regulatory descriptors
  'site', 'land', 'area', 'acreage', 'buildable', 'parcel', 'zoning',
  'rezoning', 'industrial', 'agricultural', 'residential', 'parish',
  'county', 'state', 'district', 'planning', 'regulatory', 'environmental',
  'wetland', 'wetlands', 'freshwater', 'emergent', 'flood', 'hazard',
  'special', 'floodplain', 'seismic', 'topography', 'terrain', 'slope',
  'climate', 'hurricane', 'weather', 'mitigation', 'habitat', 'species',
  'clean', 'act', 'army', 'corps', 'engineers', 'source', 'review',
  'quality', 'air',
  // finance / commercial descriptors
  'cost', 'costs', 'financial', 'economics', 'commercial', 'operation',
  'date', 'total', 'planned', 'current', 'budget', 'capital', 'viability',
  'viable', 'feasibility', 'development', 'project', 'success',
  // qualifier adjectives common in verdict titles
  'critical', 'major', 'significant', 'strong', 'excellent', 'moderate',
  'medium', 'high', 'low', 'long', 'short', 'extreme', 'extensive',
  'conditional', 'constrained', 'constrains', 'constraints', 'compounding',
  'crucially', 'likely', 'pending', 'anomalous', 'uncertainty', 'hot',
  'hardened', 'exposure', 'mandate', 'bridging', 'bypass', 'delays',
  'multiple', 'additional', 'onsite', 'developer', 'only', 'siting',
  'depends', 'physical', 'set', 'new', 'impact', 'level', 'order',
  'service', 'expansion', 'nearest',
  // approval / gap vocabulary in risk + pathway headings
  'application', 'applications', 'approval', 'approvals', 'alignment',
  'mismatch', 'confirmation', 'costly', 'overcoming', 'withdrawal',
  'shortfall', 'gap', 'ramp', 'requirement', 'requirements',
]);

/** Emission cap (FIX 2 (d)) — a systematic miss can never flood the count. */
const NER_MAX_EMISSIONS = 20;

/**
 * Capitalized words that routinely START a sentence or are common English
 * proper-ish words — excluded so a sentence-initial ordinary word ("The",
 * "This", "Site") is never mistaken for a named entity. Plus month names,
 * cardinal directions, and common report nouns. Favors false-negatives.
 */
const NER_STOPWORDS = new Set<string>([
  // sentence-start function / determiner / pronoun words
  'the', 'a', 'an', 'this', 'that', 'these', 'those', 'it', 'its', 'they',
  'we', 'our', 'their', 'his', 'her', 'he', 'she', 'his', 'i', 'you',
  'and', 'but', 'or', 'nor', 'for', 'so', 'yet', 'as', 'at', 'by', 'in',
  'of', 'on', 'to', 'up', 'if', 'is', 'are', 'was', 'were', 'be', 'been',
  'has', 'have', 'had', 'will', 'would', 'should', 'could', 'can', 'may',
  'might', 'must', 'do', 'does', 'did', 'not', 'no', 'all', 'any', 'each',
  'when', 'where', 'while', 'which', 'who', 'what', 'how', 'why', 'there',
  'here', 'then', 'than', 'because', 'although', 'however', 'therefore',
  'overall', 'meanwhile', 'additionally', 'furthermore', 'moreover',
  'notably', 'importantly', 'finally', 'first', 'second', 'third', 'next',
  'given', 'based', 'using', 'during', 'after', 'before', 'within', 'without',
  // months
  'january', 'february', 'march', 'april', 'may', 'june', 'july', 'august',
  'september', 'october', 'november', 'december',
  // common report nouns that are often capitalized at sentence start
  'site', 'project', 'report', 'section', 'figure', 'table', 'appendix',
  'estimated', 'estimate', 'note', 'see', 'per', 'phase', 'capacity',
  'power', 'grid', 'water', 'risk', 'risks', 'summary', 'analysis',
]);

/** A single alphabetic token from a canonical entity value, ≥3 chars, lowered. */
function canonicalTokens(value: string): string[] {
  return entityKey(value)
    .split(/\s+/)
    .filter((t) => t.length >= 3 && /^[a-z][a-z-]*$/.test(t));
}

/**
 * Lightweight, dependency-free NER (Part B, rebuilt by FIX 2). Extracts
 * capitalized named-entity candidates from the narrative and flags any that
 * have NO peer in the canonical fact set OR the store's context-entity
 * allowlist — i.e. a fabricated or renamed entity with no grounding anywhere
 * in the evidence. Complements `checkEntities` (which only catches the
 * sibling-SWAP case where a canonical value is omitted while an in-store
 * sibling is named).
 *
 * Precision-first (favors false-negatives over false-positives). The live
 * Redhead report produced a 192-violation storm from four defect classes,
 * each now closed structurally:
 *  (a) candidates NEVER cross a newline ([ \t]-only joiners + per-line scan)
 *      — "Initial Permitting\nThis" can no longer form;
 *  (b) the grounding corpus includes `store.contextEntities` (site name,
 *      address components, every entity-ish SED string) — the site's own
 *      name / town / parish / named operators are context, not fabrications;
 *  (c) shape gate: ≥2 capitalized tokens by construction (sentence-initial
 *      single words are structurally excluded), leading sentence-function
 *      stopwords are stripped, and a candidate whose significant tokens are
 *      ALL generic title-case business vocabulary ("Due Diligence",
 *      "Detailed Engineering", heading fragments) is report furniture;
 *  (d) emissions are CAPPED (`NER_MAX_EMISSIONS`) so a systematic miss can
 *      never flood the violation count again.
 *
 * Severity is ALWAYS `'warn'` here — escalation to `'block'` (if ever) is a
 * caller/mode decision, NOT this pure function's. NEVER throws.
 */
function checkInventedEntities(
  narrativeText: string,
  store: CanonicalFactStore,
  sectionId: string | undefined,
): GroundingViolation[] {
  const violations: GroundingViolation[] = [];
  try {
    // FAIL-OPEN discipline: the NER only runs when the store carries at least
    // one named-entity fact (or a context allowlist) to compare against. With
    // nothing to ground a proper noun, EVERY capitalized phrase would read as
    // "invented" — exactly the empty-input fail-open case the gate forbids.
    const entityFacts = store.entityIndex();
    const contextEntities = store.contextEntities ?? [];
    if (entityFacts.length === 0 && contextEntities.length === 0) return violations;

    // Build the grounding corpus: named-entity values + labels, every
    // non-absent string fact value + label (categorical values still ground a
    // mention), the site-name slot, and the FIX 2 (b) context-entity
    // allowlist. Keys ground by substring; ≥3-char tokens ground compounds.
    const canonicalKeys: string[] = [];
    const canonicalTokenSet = new Set<string>();
    const addCanonical = (raw: unknown): void => {
      if (typeof raw !== 'string') return;
      const key = entityKey(raw);
      // Keys shorter than 3 normalized chars ("LA", "c") are substring
      // shrapnel, not grounding evidence — they would ground ANY candidate
      // via the contains-direction check below.
      if (!key || key.length < 3) return;
      canonicalKeys.push(key);
      for (const t of canonicalTokens(raw)) canonicalTokenSet.add(t);
    };
    for (const f of entityFacts) {
      addCanonical(f.value);
      addCanonical(f.label);
    }
    for (const fact of store.facts.values()) {
      if (fact.status === 'absent') continue;
      if (typeof fact.value === 'string' && fact.value.trim() !== '') {
        addCanonical(fact.value);
        addCanonical(fact.label);
      }
    }
    // Site name (best-effort; folded in when the store carries it as a slot).
    addCanonical((store as unknown as { siteName?: unknown }).siteName);
    for (const ctx of contextEntities) addCanonical(ctx);

    const seen = new Set<string>();
    // Run the candidate scan over the REF-STRIPPED text so a `[Ref N]` token
    // never yields a spurious "Ref" entity candidate.
    const scanText = stripRefTokens(narrativeText);

    // FIX 2 (a) — per-line scan: belt-and-braces with the [ \t]-only joiners.
    for (const line of scanText.split('\n')) {
      if (violations.length >= NER_MAX_EMISSIONS) break;
      NER_CANDIDATE_RE.lastIndex = 0;
      let m: RegExpExecArray | null;
      while ((m = NER_CANDIDATE_RE.exec(line)) !== null) {
        if (violations.length >= NER_MAX_EMISSIONS) break;

        // Trim a leading "the ", then any leading sentence-function stopwords
        // ("While Louisiana …" → "Louisiana …"). A candidate that shrinks
        // below TWO tokens is a sentence-start artifact, not an entity.
        let words = m[1]!.trim().replace(/^the\s+/i, '').split(/[ \t]+/);
        while (words.length > 0 && NER_STOPWORDS.has(words[0]!.toLowerCase())) {
          words = words.slice(1);
        }
        if (words.length < 2) continue;
        const phrase = words.join(' ');

        const candKey = entityKey(phrase);
        if (!candKey || seen.has(candKey)) continue;

        // GROUNDED — skip when the candidate is covered by the corpus keys.
        // The contains-direction (candidate CONTAINS a corpus key) requires a
        // MULTI-WORD key: a single-token key ("grid") inside a longer phrase
        // ("Zephyr Grid Holdings") is not grounding evidence — that direction
        // over-grounded fabrications when the context allowlist grew large.
        const substringMatch = canonicalKeys.some(
          (ck) => ck.includes(candKey) || (ck.includes(' ') && candKey.includes(ck)),
        );
        if (substringMatch) continue;

        // FIX 2 (c) — coverage rule: every SIGNIFICANT token (≥3 alpha chars)
        // is stopword / generic-title / canonical-or-context vocabulary ⇒ a
        // generic business phrase or a fully-grounded compound ("Calcasieu
        // Ship Channel" = context token + generic tokens) ⇒ skip. NO
        // significant tokens ⇒ unassessable ⇒ skip.
        const significantTokens = canonicalTokens(phrase);
        if (significantTokens.length === 0) continue;
        const allCovered = significantTokens.every(
          (t) =>
            NER_STOPWORDS.has(t) ||
            NER_GENERIC_TITLE_TOKENS.has(t) ||
            canonicalTokenSet.has(t),
        );
        if (allCovered) continue;

        // The narrative DOES surface this entity (it is literally in the
        // prose), and nothing in the evidence grounds it ⇒ flag.
        seen.add(candKey);
        violations.push({
          kind: 'invented-entity-hallucination',
          severity: 'warn',
          message: `Narrative names "${phrase}", a proper-named entity with no peer in the canonical fact set or site context (possible fabrication / renamed entity).`,
          observed: phrase,
          ...(sectionId ? { sectionId } : {}),
        });
      }
    }
  } catch {
    // PURE + fail-open: a malformed input must never crash the gate.
    return violations;
  }
  return violations;
}

/* ── checkGrounding — the pure orchestrator ──────────────────────────── */

export interface CheckGroundingArgs {
  /** The assembled narrative body to scan (whole report, or one section). */
  narrativeText: string;
  /** The authoritative fact store. */
  store: CanonicalFactStore;
  numericSourceEvidence?: NumericSourceEvidence;
  frozenBomSubtotalSource?: FrozenBomSubtotalSource;
  quantitySourceRoles?: QuantitySourceRoles;
  publicSentimentAudit?: PublicSentimentAuditInput;
  /**
   * Optional exact FEMA parcel-polygon result. Kept flat so this pure gate
   * does not depend on the report/SED object graph. When present, it governs
   * ONLY absolute whole-parcel flood-zone prose; partial/intersection
   * statements remain outside this check.
   */
  floodAreaAnalysis?: {
    method?: string;
    parcelAreaAcres?: number;
    unmappedFraction?: number;
    closureErrorFraction?: number;
    zoneAreas?: readonly {
      zone: string;
      acres: number;
      fraction: number;
    }[];
    /** Disclosed geometry fallback tier; the analysis is not exact when set. */
    geometryTier?: 'single-sweep' | 'precision-snap';
  };
  /** Default fractional numeric tolerance (overridden per-unit). Defaults to 0.5%. */
  tolerancePct?: number;
  /** Optional section attribution stamped onto every violation. */
  sectionId?: string;
}

const EFFECTIVELY_WHOLE_PARCEL_FRACTION = 0.995;

/** FEMA NFIP zone tokens carried by the wired polygon provider. */
const FEMA_ZONE_TOKEN_RE =
  /\b(?:FEMA(?:'s)?\s+)?(?:flood\s+)?zone\s*[:=\-]?\s*["'“”‘’]?\s*(A(?:E|H|O|R|99)?|V(?:E)?|X(?:500)?|B|C|D)\b/i;
/** The same token, iterated: a sentence may name more than one zone. */
const FEMA_ZONE_TOKEN_G = new RegExp(FEMA_ZONE_TOKEN_RE.source, 'gi');

/**
 * Sentence bounds for the whole-parcel flood claim ONLY. The shared
 * sentenceRangeAt is used by every numeric, entity and absent-fact check, and
 * widening it there deletes unrelated violations; this splitter is local, so
 * its tolerance cannot reach them.
 *
 * It differs from the shared one in a single respect: an abbreviation or
 * citation period ("FIRM panel No. 48201C0125F", "rev. 2", "U.S. Army",
 * "see Fig. 3") does not end the sentence. Ending there would drop the zone
 * token out of the claim's sentence, and the claim would never be examined at
 * all. There is deliberately NO single-letter rule: the FEMA zone tokens are
 * themselves single letters, so treating "... zone X." as an initial would
 * merge the next sentence into the claim — and a question there would stand
 * the whole claim down.
 */
function floodSentenceAt(text: string, index: number, maxLen = 400): string {
  const from = Math.max(0, index - maxLen);
  let start = from;
  for (let i = from; i < index; i += 1) if (isFloodSentenceEnd(text, i)) start = i + 1;
  let end = Math.min(text.length, index + maxLen);
  for (let i = index; i < Math.min(text.length, index + maxLen); i += 1) {
    if (isFloodSentenceEnd(text, i)) { end = i + 1; break; }
  }
  return text.slice(start, end);
}

/**
 * Absolute whole-parcel language only. This deliberately does NOT match
 * "the site intersects Zone A", "a portion of the parcel", or a named
 * parcel/sub-area. Those are compatible with a mixed polygon result.
 */
const WHOLE_PARCEL_ASSERTION_RE =
  /\b(?:(?:the\s+)?(?:entire|whole)\s+(?:project\s+)?(?:site|parcel|property)|(?:all|100\s*%)\s+(?:of\s+)?(?:the\s+)?(?:project\s+)?(?:site|parcel|property)|(?:the\s+)?(?:project\s+)?(?:site|parcel|property)\s+(?:is|lies|sits|falls)\s+(?:located\s+)?(?:entirely|wholly))\b/gi;

function normalizedFloodZone(value: string): string {
  return value.trim().toUpperCase().replace(/[\s_\-]+/g, '');
}

function formatFloodAreaMix(
  area: NonNullable<CheckGroundingArgs['floodAreaAnalysis']>,
): string {
  const rows = (area.zoneAreas ?? [])
    .filter((zone) => Number.isFinite(zone.fraction) && zone.fraction >= 0)
    .slice()
    .sort((a, b) => b.fraction - a.fraction)
    .map((zone) => {
      const acres = Number.isFinite(zone.acres) ? ` ${zone.acres.toFixed(2)} ac` : '';
      return `Zone ${zone.zone}${acres} (${(zone.fraction * 100).toFixed(1)}%)`;
    });
  if (typeof area.unmappedFraction === 'number' && area.unmappedFraction > 0.0005) {
    rows.push(`unmapped ${(area.unmappedFraction * 100).toFixed(1)}%`);
  }
  return rows.join(', ');
}

/**
 * Catch the live Water failure mode where the point-query headline zone was
 * expanded into a false parcel-wide claim even though the same SED carried an
 * exact multi-zone polygon intersection. Representing it as a blocking
 * value-mismatch feeds the existing one-shot grounding repair and, if it
 * survives repair, the existing truth-hold policy.
 */
function checkWholeParcelFloodZoneAssertions(
  narrativeText: string,
  area: CheckGroundingArgs['floodAreaAnalysis'],
  sectionId: string | undefined,
): GroundingViolation[] {
  if (
    area?.method !== 'polygon-intersection'
    || !Number.isFinite(area.parcelAreaAcres)
    || (area.parcelAreaAcres ?? 0) <= 0
    || !Array.isArray(area.zoneAreas)
    || area.zoneAreas.length === 0
    || !Number.isFinite(area.closureErrorFraction)
    || Math.abs(area.closureErrorFraction ?? Number.POSITIVE_INFINITY) > 0.01
  ) {
    return [];
  }

  const zones = area.zoneAreas.filter(
    (zone) =>
      typeof zone.zone === 'string'
      && Number.isFinite(zone.acres)
      && zone.acres >= 0
      && Number.isFinite(zone.fraction)
      && zone.fraction >= 0
      && zone.fraction <= 1,
  );
  const unmapped = typeof area.unmappedFraction === 'number' && Number.isFinite(area.unmappedFraction)
    ? area.unmappedFraction
    : 0;
  const closure = zones.reduce((sum, zone) => sum + zone.fraction, 0) + unmapped;
  if (zones.length === 0 || unmapped < 0 || unmapped > 1 || Math.abs(closure - 1) > 0.02) return [];

  const expected = formatFloodAreaMix({ ...area, zoneAreas: zones });
  if (!expected) return [];

  const clean = stripRefTokens(narrativeText);
  const violations: GroundingViolation[] = [];
  const seen = new Set<string>();
  const geometryTier = area.geometryTier;
  WHOLE_PARCEL_ASSERTION_RE.lastIndex = 0;
  let absoluteMatch: RegExpExecArray | null;
  while ((absoluteMatch = WHOLE_PARCEL_ASSERTION_RE.exec(clean)) !== null) {
    // The floor is read on the sentence the PRE-CHANGE control used — the
    // shared splitter, its first zone token, its offsets — so the guarantee is
    // about that control and not about this gate's own wider sentence. When
    // the pre-change control found no zone token it raised nothing, which is
    // the same outcome as a suppression.
    const headSentence = sentenceAt(clean, absoluteMatch.index);
    const headZone = headSentence.match(FEMA_ZONE_TOKEN_RE);
    const headAssertion = headSentence.indexOf(absoluteMatch[0]);
    const floorSuppresses = !headZone?.[1] || preChangeWouldSuppress(
      headSentence,
      headAssertion >= 0 ? headAssertion : headZone.index ?? 0,
      headAssertion >= 0 ? headAssertion + absoluteMatch[0].length : headZone.index ?? 0,
      headZone.index ?? 0,
    );
    const raw = floodSentenceAt(clean, absoluteMatch.index);
    const sentenceStart = clean.indexOf(raw, Math.max(0, absoluteMatch.index - raw.length));
    const sentence = raw.trim();
    // The offset of THIS match, not of the first phrase that happens to have
    // the same surface text: two identical whole-parcel phrases in one
    // sentence are two independent assertions, and judging the second with the
    // first one's lead lets a hedge on the first suppress it.
    const trimmed = raw.length - raw.trimStart().length;
    // A question, hypothetical, or explicit exclusion does not assert that the
    // parcel occupies the named zone ("whether the entire site is Zone A",
    // "the entire site is not in Zone A", "lies outside Zone A"). A negation
    // that belongs to a different predicate ("mitigation is not required as
    // the entire site is in Zone X") is NOT a hedge and leaves this armed.
    // The span is the matched whole-parcel phrase itself, NOT the gap up to
    // the zone token: intervening prose ("which contains no wetlands", "400 ft
    // outside the drainage easement") is about something else and must not
    // disarm this control. "Outside" counts only where it governs the zone
    // token itself ("lies outside FEMA Zone A").
    //
    // EVERY zone token in the sentence is a candidate, tested on its own, and
    // every one that is not hedged is a claim. A denial of one zone must never
    // license an assertion of another ("excluded from FEMA flood zone A AND IS
    // LOCATED WITHIN FEMA FLOOD ZONE X" claims X), which is what taking only
    // the sentence's first zone token used to do; and a TRUE whole-parcel
    // claim about a dominant zone must not carry a false one about another
    // zone past the threshold check with it, which is why the loop collects
    // every unhedged token instead of stopping at the first.
    const absoluteInSentence = absoluteMatch.index - sentenceStart - trimmed;
    const claimedTokens: string[] = [];
    FEMA_ZONE_TOKEN_G.lastIndex = 0;
    let zoneMatch: RegExpExecArray | null;
    while ((zoneMatch = FEMA_ZONE_TOKEN_G.exec(sentence)) !== null) {
      if (!zoneMatch[1]) continue;
      const zoneInSentence = zoneMatch.index;
      const spanStart = absoluteInSentence >= 0 ? absoluteInSentence : zoneInSentence;
      const spanEnd = absoluteInSentence >= 0 ? absoluteInSentence + absoluteMatch[0].length : zoneInSentence;
      const beforeZone = sentence.slice(Math.max(spanEnd, zoneInSentence - 24), zoneInSentence);
      const hedged = floodClaimIsHedged(sentence, spanStart, spanEnd, zoneInSentence, floorSuppresses)
        // "outside" is the one exclusion the pre-change control also saw, so
        // reading it here cannot put this gate below that floor. Wider forms
        // ("beyond", "clear of") are not in that list and must earn their
        // stand-down inside floodAssertionIsHedged, where the floor applies.
        || (floorSuppresses && /\b(?:outside)\s+(?:of\s+)?(?:the\s+)?(?:mapped\s+)?$/i.test(beforeZone));
      if (hedged) continue;
      claimedTokens.push(zoneMatch[1]);
    }
    // MEMBERSHIP IS FLOORED TOO. This gate's splitter can end the sentence
    // where the pre-change one did not — an exotic separator after the period,
    // say — and a zone token the pre-change sentence contained would then
    // never enter the candidate set at all. The floor gates stand-downs, not
    // membership, so the pre-change control's own claim is added here when it
    // would have raised one.
    if (headZone?.[1] && !floorSuppresses
        && !claimedTokens.some((token) => normalizedFloodZone(token) === normalizedFloodZone(headZone[1]!))) {
      claimedTokens.push(headZone[1]);
    }
    for (const claimedToken of claimedTokens) {
    const claimedZone = normalizedFloodZone(claimedToken);
    const claimedFraction = zones.reduce(
      (sum, zone) => sum + (normalizedFloodZone(zone.zone) === claimedZone ? zone.fraction : 0),
      0,
    );
    if (claimedFraction >= EFFECTIVELY_WHOLE_PARCEL_FRACTION) continue;

    // One report claim is commonly projected through structuredFindings,
    // contextualKeyFindings, and risk rows whose serialization adds different
    // prefixes. The contradiction is the claimed whole-parcel zone, not each
    // projection prefix. A different claimed zone remains a distinct defect.
    const identity = claimedZone;
    if (seen.has(identity)) continue;
    seen.add(identity);
    violations.push({
      kind: 'value-mismatch',
      severity: 'block',
      message:
        `Narrative asserts the whole parcel is FEMA Zone ${claimedToken.toUpperCase()}, but `
        + (geometryTier
          ? `the parcel-polygon areaAnalysis (computed with a disclosed ${geometryTier} geometry fallback, not exact) reports ${expected}. State that zone mix and the geometry fallback; do not generalize `
          : `exact parcel-polygon areaAnalysis reports ${expected}. State the exact zone mix; do not generalize `)
        + 'a point/headline zone to the entire site.',
      observed: `whole parcel in FEMA Zone ${claimedToken.toUpperCase()}`,
      factId: 'flood.areaAnalysis',
      expected,
      // The zone TOKEN, not the bare letter: "All of the site ..." contains an
      // 'A' and would otherwise satisfy a substring test for Zone A.
      excerpt: [...sentence.matchAll(FEMA_ZONE_TOKEN_G)]
        .some((zone) => zone[1] && normalizedFloodZone(zone[1]) === claimedZone)
        ? sentence
        : `${headSentence} ${sentence}`.trim(),
      ...(sectionId ? { sectionId } : {}),
    });
    }
  }
  WHOLE_PARCEL_ASSERTION_RE.lastIndex = 0;
  return violations;
}

/**
 * The pure grounding checker. Scans `narrativeText` for numbers + entities
 * that contradict / invent / mis-label the store's facts. Never throws.
 */
export function checkGrounding(args: CheckGroundingArgs): GroundingResult {
  const { narrativeText, store } = args;
  const tolerancePct = args.tolerancePct ?? DEFAULT_TOLERANCE_PCT;
  const sectionId = args.sectionId;

  const numericFacts = store.numericIndex();
  const entityFacts = store.entityIndex();

  // Map units whose ONLY backing facts are absent (for invented-number).
  const absentNumericUnits = new Map<string, string[]>();
  // FIX 1 — ALL absent facts per unit (unpruned), for absent-fact assertion
  // detection when the unit ALSO has present facts ("a firm 49 MW" while
  // firmCapacity.targetMw is absent but power.declaredMw is present).
  const absentFactsByUnit = new Map<string, Array<{ factId: string; label: string }>>();
  const presentUnits = new Set<string>();
  for (const f of numericFacts) {
    if (f.unit) presentUnits.add(f.unit);
  }
  for (const fact of store.facts.values()) {
    if (fact.status === 'absent' && fact.unit) {
      const list = absentNumericUnits.get(fact.unit) ?? [];
      list.push(fact.factId);
      absentNumericUnits.set(fact.unit, list);
      const infoList = absentFactsByUnit.get(fact.unit) ?? [];
      infoList.push({ factId: fact.factId, label: fact.label });
      absentFactsByUnit.set(fact.unit, infoList);
    }
  }
  // A unit is "only-absent" when no present fact carries it.
  for (const unit of [...absentNumericUnits.keys()]) {
    if (presentUnits.has(unit)) absentNumericUnits.delete(unit);
  }

  const numericRes = checkNumerics(
    narrativeText,
    numericFacts,
    absentNumericUnits,
    absentFactsByUnit,
    tolerancePct,
    sectionId,
    args.numericSourceEvidence,
    args.frozenBomSubtotalSource,
    store.siteId,
  );

  // ABSENT-ISO ASSERTION (2026-08-04, live v1.22.0 Redhead run) — the
  // narrative asserted MISO as the project's ISO (queue, "MISO System Impact
  // Study") while the canonical isoRegion fact was ABSENT, and NOTHING here
  // flagged it: checkEntities compares only PRESENT entities, and the NER
  // deliberately skips ALL-CAPS acronyms. Because the violation was never
  // enumerated, the one-shot repair regen was never told to fix it — the
  // fabrication rode through the repair pass untouched. This check makes
  // absent-ISO assertions first-class blocking violations so the repair
  // overlay names them explicitly.
  const isoRes = checkAbsentIsoAssertions(narrativeText, store, sectionId);

  const floodAreaRes = checkWholeParcelFloodZoneAssertions(
    narrativeText,
    args.floodAreaAnalysis,
    sectionId,
  );

  // Entity categories: group by the dotted prefix before the first '.' so the
  // checker can reason per-category (electricUtility / isoRegion / …).
  const entityCategories = new Map<string, EntityFactIndexEntry[]>();
  for (const f of entityFacts) {
    const cat = f.factId.split('.')[0]!;
    const list = entityCategories.get(cat) ?? [];
    list.push(f);
    entityCategories.set(cat, list);
  }
  const entityRes = checkEntities(narrativeText, entityFacts, entityCategories, sectionId);

  // Part B — lightweight NER for fabricated/renamed entities with no in-store
  // peer (DEFAULT 'warn'; never block on its own). Appended AFTER checkEntities
  // so the sibling-swap case is policed first.
  const inventedEntityHallucinations = checkInventedEntities(narrativeText, store, sectionId);

  const violations = [
    ...numericRes.violations,
    ...isoRes,
    ...floodAreaRes,
    ...checkQuantitySourceRoles(narrativeText, args.quantitySourceRoles, sectionId),
    ...checkPublicSentimentAssertions(args.publicSentimentAudit),
    ...entityRes.violations,
    ...inventedEntityHallucinations,
  ];
  const blocking = violations.filter((v) => v.severity === 'block');

  return {
    clean: violations.length === 0,
    violations,
    blocking,
    stats: {
      numericFacts: numericFacts.length,
      entityFacts: entityFacts.length,
      numericMentions: numericRes.mentions,
      entityMentions: entityRes.mentions,
      valueMismatches: violations.filter((v) => v.kind === 'value-mismatch').length,
      inventedNumbers: violations.filter((v) => v.kind === 'invented-number').length,
      inventedEntities: violations.filter((v) => v.kind === 'invented-entity').length,
      inventedEntityHallucinations: violations.filter((v) => v.kind === 'invented-entity-hallucination').length,
      unlabeledEstimates: violations.filter((v) => v.kind === 'unlabeled-estimate').length,
      deadPointers: 0,
      orphanReferences: 0,
      ...(numericRes.phaseDeltaBindings ? {
        phaseDeltaBindings: numericRes.phaseDeltaBindings,
        phaseDeltaMismatches: numericRes.violations.filter(v => v.assertionRole === 'phase-delta-binding').length,
      } : {}),
    },
  };
}

/* ── Citation-target → grounding-violation mapping ───────────────────── */

function mapCitationViolation(v: CitationTargetViolation): GroundingViolation {
  return {
    kind: v.kind, // 'dead-pointer' | 'orphan-reference' are members of GroundingViolationKind
    severity: v.severity,
    message: v.message,
    observed: v.target,
    ...(typeof v.refIndex === 'number' ? { expected: `[Ref ${v.refIndex}]` } : {}),
  };
}

/* ── runGroundingGate — compose + apply the mode policy ──────────────── */

export interface RunGroundingGateArgs extends CheckGroundingArgs {
  /** Citation-target gate inputs (flattened by the caller). */
  citationGate?: {
    citations: readonly CitationTarget[];
    references: readonly ReferenceEntry[];
    renderedSections: readonly RenderedSection[];
  };
  /** The rollout mode. `'shadow'` measures only; `'warn'` never blocks; `'on'` blocks. */
  mode: GroundingMode;
}

export interface RunGroundingGateResult {
  /** The composed grounding + citation-target result. */
  result: GroundingResult;
  /** The raw citation-target gate result, when run (for telemetry / drill-down). */
  citationTargetResult?: CitationTargetGateResult;
  /**
   * Should the caller escalate to a one-shot regenerate? True only in `'on'`
   * mode when there is at least one blocking violation. `'shadow'`/`'warn'`
   * NEVER escalate (they measure / record).
   */
  shouldBlock: boolean;
}

/**
 * Compose the grounding checker with the structural citation-target gate and
 * apply the rollout-mode policy. PURE — never throws. The caller (Wire agent)
 * decides what to do with `shouldBlock` (regenerate-once via
 * `GroundingGateError` + `onGroundingRepair`, then record).
 *
 * Mode policy:
 *  - `'shadow'` / `'off'` : run the checks for telemetry, but `shouldBlock`
 *    is always false and no severity is escalated.
 *  - `'warn'`             : run the checks, surface findings, `shouldBlock`
 *    is always false (non-blocking).
 *  - `'on'`               : `labeled-unverifiable` unlabeled-estimates are
 *    UPGRADED warn→block; `shouldBlock` is true when any blocking violation
 *    remains.
 */
export function runGroundingGate(args: RunGroundingGateArgs): RunGroundingGateResult {
  const grounding = checkGrounding(args);

  let citationTargetResult: CitationTargetGateResult | undefined;
  const citationViolations: GroundingViolation[] = [];
  if (args.citationGate) {
    citationTargetResult = checkCitationTargets(args.citationGate);
    for (const v of citationTargetResult.violations) {
      citationViolations.push(mapCitationViolation(v));
    }
  }

  let violations = [...grounding.violations, ...citationViolations];

  // `on`-mode escalation: an unlabeled labeled-unverifiable value is a
  // reader-facing false-fact and must block.
  if (args.mode === 'on') {
    violations = violations.map((v) => {
      if (
        v.kind === 'unlabeled-estimate' &&
        v.severity === 'warn' &&
        // only labeled-unverifiable is upgraded; plain estimates stay warn.
        isLabeledUnverifiable(v.factId, args.store)
      ) {
        return { ...v, severity: 'block' as const };
      }
      return v;
    });
  }

  const blocking = violations.filter((v) => v.severity === 'block');
  const deadPointers = citationViolations.filter((v) => v.kind === 'dead-pointer').length;
  const orphanReferences = citationViolations.filter((v) => v.kind === 'orphan-reference').length;

  const result: GroundingResult = {
    clean: violations.length === 0,
    violations,
    blocking,
    stats: {
      ...grounding.stats,
      deadPointers,
      orphanReferences,
    },
  };

  /*
   * WHAT `shouldBlock` ACTUALLY DOES (2026-08-14): it is the sole trigger for
   * the ONE regenerate-once repair pass (siteRagService), a full narrative +
   * multi-iteration review re-run. It does NOT hold the report — measured, an
   * `unlabeled-estimate` sets no qualityHold, bars no export and raises no
   * truth question, because `qaSeverityPolicy` deliberately excludes it from
   * TRUTH_VIOLATION_GROUNDING_KINDS ("a missing uncertainty qualifier is a
   * labelling defect, not a false statement").
   *
   * So the on-mode escalation above was spending the single repair budget on
   * a class the authoritative policy says cannot block — and on the live
   * report it burned that pass, changed nothing, and left the genuine finding
   * unrepaired. The escalation predates the truth-only policy by seven weeks
   * and was never reconciled with it.
   *
   * The severity stamp is deliberately LEFT ALONE. Measured: downgrading it to
   * 'warn' does not make the finding advisory, it DELETES it — `blocking=0,
   * advisory=0`, gone from every surface — because the advisory channel keys
   * off 'block'. Keeping the stamp preserves the row as
   * `source-report-quality-advisory`; only the repair TRIGGER changes.
   */
  const shouldBlock = args.mode === 'on'
    && blocking.some((v) => v.kind !== 'unlabeled-estimate');

  return {
    result,
    ...(citationTargetResult ? { citationTargetResult } : {}),
    shouldBlock,
  };
}

function isLabeledUnverifiable(factId: string | undefined, store: CanonicalFactStore): boolean {
  if (!factId) return false;
  return store.get(factId)?.status === 'labeled-unverifiable';
}

/* ── GroundingGateError — the regenerate-once signal ─────────────────── */

/**
 * Thrown by the gate runner (in `'on'` mode) when blocking grounding
 * violations survive. Mirrors `CitationDensityGateError` so the existing
 * regenerate-once machinery (`onGroundingRepair`, guarded by a
 * `_groundingRepairUsed` flag) can catch it, build a repair overlay from
 * `violations`, and regenerate exactly once. The gate runner itself does NOT
 * throw — the Wire-layer escalation does, mirroring the citation-density
 * wiring at runTypedReportGeneration.ts / siteRagService.ts.
 */
export class GroundingGateError extends Error {
  readonly violations: GroundingViolation[];
  readonly result: GroundingResult;

  constructor(result: GroundingResult) {
    const blocks = result.blocking.length;
    super(
      `Grounding gate failed: ${blocks} blocking violation${blocks === 1 ? '' : 's'} ` +
        `(${result.stats.valueMismatches} value-mismatch, ${result.stats.inventedNumbers} invented-number, ` +
        `${result.stats.inventedEntities} invented-entity, ${result.stats.deadPointers} dead-pointer).`,
    );
    this.name = 'GroundingGateError';
    this.violations = result.violations;
    this.result = result;
  }
}

/** A planned phase quantity (`phaseCapacity.<N>.cumulativeMw` / `.additionMw`). */
const PHASE_CAPACITY_FACT_ID_RE = /^phaseCapacity\./;

/**
 * Present governed facts (other than the one the violation names) whose value
 * EQUALS the stated figure in the same unit. Such a figure is a correct value
 * of a different governed subject, so the repair must attribute it — never
 * omit it or overwrite it with the named fact's value.
 *
 * Planned phase quantities (`phaseCapacity.<N>.*`) take no part, on EITHER
 * side: a violation on a phase fact keeps the production (v1) repair line, and
 * a phase fact is never offered as the subject to attribute a figure to. Phase
 * binding is the v1 binding, which does not bind the attributed wordings
 * ("… the Phase 1 planned cumulative capacity of 200 MW"), so the retry that
 * attribution asks for would be held again where v1's OMIT / ONLY-value line
 * clears.
 */
function otherGovernedSubjectsWithValue(
  v: GroundingViolation,
  store: CanonicalFactStore | undefined,
): string[] {
  if (
    !store
    || PHASE_CAPACITY_FACT_ID_RE.test(v.factId ?? '')
    || (v.factId != null && store.get(v.factId)?.numericGroundingScope != null)
  ) {
    return [];
  }
  const stated = extractTaggedNumbers(v.observed)[0];
  if (!stated?.unit) return [];
  const labels = new Set<string>();
  for (const fact of store.numericIndex()) {
    if (PHASE_CAPACITY_FACT_ID_RE.test(fact.factId)) continue;
    if (fact.numericGroundingScope != null) continue;
    if (fact.factId === v.factId || fact.unit !== stated.unit) continue;
    if (!numericMatch(stated.value, fact.value, stated.unit, DEFAULT_TOLERANCE_PCT)) continue;
    labels.add(store.get(fact.factId)?.label ?? fact.factId);
  }
  return [...labels];
}

/**
 * Build the regenerate-once repair-overlay text from the blocking violations.
 * Mirrors `buildCitationRetryPrompt` — re-states the canonical facts the model
 * violated, so the retry is grounded. Pure.
 *
 * ROLE-AWARE (C2). A binding error must never turn into an instruction to
 * write a wrong value into correct prose:
 *  - a figure that EQUALS another NON-PHASE governed fact's value is to be
 *    attributed to that governed subject, never OMITTED; an invented-number
 *    is never replaced, while a value-mismatch still states the named fact's
 *    canonical value as the alternative (the figure may be a genuine
 *    substitution);
 *  - a generation relabel keeps the load role noun; an unverified queue
 *    benchmark is restated in its source's own form or omitted.
 * Phase figures keep the production (v1) repair templates. Outside D04's
 * closed parenthetical, phase binding retains v3: a `phaseCapacity.<N>.*`
 * violation keeps v1's
 * ONLY-value line, and a planned phase quantity is never offered as the
 * governed subject to attribute a figure to (v1 does not bind the attributed
 * wording, so that retry would be held again). For a phase sentence the text
 * therefore equals v1's, except the relabel / benchmark lines above.
 * `store` is optional: without it the governed-subject attribution is skipped
 * (other text is unchanged).
 */
export function buildGroundingRepairPrompt(violations: GroundingViolation[], store?: CanonicalFactStore): string {
  const blocking = violations.filter((v) => v.severity === 'block');
  if (blocking.length === 0) return '';
  const lines: string[] = [
    '## GROUNDING REPAIR — your previous draft violated the canonical facts',
    'Your previous draft asserted values that contradict, invent, or mislabel the authoritative facts. Regenerate, using ONLY the canonical values. Specifically:',
  ];
  const labelOf = (factId: string | undefined): string =>
    (factId ? store?.get(factId)?.label : undefined) ?? factId ?? 'the governed fact';
  const attribute = (v: GroundingViolation, subjects: readonly string[]): string => {
    const equals = `  - "${v.observed}" equals the governed value of ${subjects.join(' / ')}.`;
    // A genuine substitution can COINCIDE with another governed value, so a
    // value-mismatch keeps the named fact's canonical value as the
    // alternative: the retry may re-attribute the figure or correct it.
    if (v.kind === 'value-mismatch') {
      return `${equals} If the sentence is about that governed subject, keep the figure and attribute it explicitly to that subject in the same sentence; do not omit it. If the sentence is about ${labelOf(v.factId)}, its canonical value is ${v.expected ?? 'the canonical value'}: state that value instead of "${v.observed}".`;
    }
    return `${equals} Do NOT omit it and do NOT replace it with another number: attribute it explicitly to that governed subject in the same sentence, and do not present it as ${labelOf(v.factId)}.`;
  };
  // The same sentence can be projected into multiple report fields. Repeating
  // one instruction nine times spends repair context without adding a distinct
  // correction. Collapse only byte-identical detector messages; a different
  // conflict always receives its own repair instruction.
  const seenMessages = new Set<string>();
  for (const v of blocking) {
    if (seenMessages.has(v.message)) continue;
    seenMessages.add(v.message);
    switch (v.kind) {
      case 'value-mismatch': {
        if (v.assertionRole === 'generation-capacity-relabel') {
          lines.push(`  - ${v.message} Keep the figure's load role noun (campus / IT load / cumulative / addition) exactly as the verified text states it; never restate it as generation, plant or power-plant capacity.`);
          break;
        }
        if (v.assertionRole === 'phase-delta-binding') {
          // The same number may equal the campus total. That does not change
          // the explicit parenthetical's phase-increment electrical role.
          lines.push(`  - You stated "${v.observed}"${v.factId ? ` for ${v.factId}` : ''}. The ONLY permitted value is ${v.expected ?? 'the canonical value'}.`);
          break;
        }
        const subjects = otherGovernedSubjectsWithValue(v, store);
        if (subjects.length > 0) { lines.push(attribute(v, subjects)); break; }
        lines.push(`  - You stated "${v.observed}"${v.factId ? ` for ${v.factId}` : ''}. The ONLY permitted value is ${v.expected ?? 'the canonical value'}.`);
        break;
      }
      case 'invented-number': {
        if (v.assertionRole === 'source-financial-planning-energy-price') {
          lines.push('  - Retain only the exact estimated financial planning energy price from the current selected frozen owner document and its own numbered citation. Any copied demand charge and billed facility peak must match that same source. If that source does not bind, omit the price. Do not rename it as PPA, fuel price, LCOE, a utility-issued tariff, verified price or guaranteed commercial terms.');
          continue;
        }
        if (v.assertionRole === 'modeled-annual-financial-metric') {
          lines.push(`  - Preserve only the admitted estimated annual data-center model metric and its frozen identity. ${v.expected ?? ''} Payback uses the stated undiscounted reference-year clock including construction, not COD or an equity/discounted clock. Capital intensity requires the whole-project budget and its exact sole declared facility-phase basis; do not borrow billed peak, IT load, equipment ratings, utility allocation or future campus capacity. If that complete basis is unavailable, omit $/kW. Do not upgrade estimates to quote, paid, guaranteed or generation/PPA facts.`);
          break;
        }
        if (v.assertionRole === 'modeled-project-budget') {
          lines.push('  - Use only the admitted estimated modeled whole-project budget from the frozen saved annual data-center model. Do not convert it into an OEM quote, EPC/contract/award/paid price, phase budget or generation/PPA financial result. If its source authority cannot be bound, omit the numeric budget claim. Citation validity remains independent.');
          break;
        }
        if (v.assertionRole === 'source-design-load-margin') {
          lines.push('  - Restate only an admitted producer statement for the exact source-stated same-phase surviving-service allocation/facility-demand/design-load-margin tuple, with its own numbered document reference. This is a design comparison, not a fictional normal-case table, utility headroom, measured availability, completed commissioning or accreditation. If no complete frozen tuple is admitted, omit the numeric design comparison. Keep utility capacity and delivery conditions independent; do not calculate missing values or rename reserve headroom as utility headroom.');
          break;
        }
        if (v.assertionRole === 'modeled-grid-import-margin') {
          lines.push('  - A modeled normal-case grid-import margin is a separate fictional planning result, never utility-indicated, available, firm or committed capacity. Restate only a complete same-phase normal-case allocated-import/facility-load/margin tuple from an exact frozen numbered source, with its fictional/model status. Preserve independently unknown utility service. If the tuple or its source cannot be bound, omit the numeric planning claim and leave the modeled result undetermined; do not adopt a CFS headroom value or infer absence.');
          break;
        }
        if (v.assertionRole === 'generator-queue-benchmark') {
          lines.push(`  - "${v.observed}" is presented as a generator interconnection-queue benchmark that no cited current source verifies. Either restate it exactly as its current source states it — the statistic, its generation-project cohort and, in the same clause, that it is not a load-connection timetable for this project (for example "<source> reports a median of <N> years from interconnection request to commercial operation for <cohort> generation projects, which is not a load-connection timetable for this project [Ref N]") — with that source's [Ref N], or omit it. Never apply it to this project's schedule.`);
          break;
        }
        const subjects = otherGovernedSubjectsWithValue(v, store);
        if (subjects.length > 0) { lines.push(attribute(v, subjects)); break; }
        lines.push(`  - You asserted "${v.observed}"${v.factId ? ` for ${v.factId}` : ''}, which is NOT established by the evidence. OMIT the unsupported claim entirely. Do NOT state a value and do NOT replace it with an unsupported assertion that the value, contract, or commercial/legal status is absent, unsourced, unsigned, or unestablished.`);
        break;
      }
      case 'invented-entity':
        lines.push(`  - You named "${v.observed}"${v.factId ? ` for ${v.factId}` : ''}, which is not the canonical entity${v.expected ? ` (${v.expected})` : ''}. Use the canonical value or omit the claim.`);
        break;
      case 'unlabeled-estimate':
        lines.push(`  - "${v.observed}"${v.factId ? ` (${v.factId})` : ''} is unverified — you MUST label it as an estimate / unverified.`);
        break;
      case 'dead-pointer':
        lines.push(`  - A citation points to ${v.observed}, which is not a rendered section. Remove or repoint it.`);
        break;
      default:
        break;
    }
  }
  return lines.join('\n');
}
